import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import './TripManagement.css';
import * as tripsService from '../../services/tripsService';
import * as driversService from '../../services/driversService';
import * as vehiclesService from '../../services/vehiclesService';
import { getAgencies } from '../../services/agenciesService';
import { getCustomers } from '../../services/customersService';
import { useGooglePlacesAutocomplete } from '../../hooks/useGooglePlacesAutocomplete';

// RouteMap Component for displaying Google Maps route
const RouteMap = ({ startLocation, destination }) => {
  const mapRef = useRef(null);
  const [mapError, setMapError] = useState(null);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [mapInitialized, setMapInitialized] = useState(false);

  // Check if Google Maps is loaded
  useEffect(() => {
    const checkGoogle = setInterval(() => {
      if (window.google && window.google.maps && window.google.maps.places) {
        setIsGoogleLoaded(true);
        clearInterval(checkGoogle);
      }
    }, 100);

    return () => clearInterval(checkGoogle);
  }, []);

  // Initialize map when container is ready
  useEffect(() => {
    if (!isGoogleLoaded || mapInitialized) {
      return;
    }

    if (!startLocation || !destination) {
      setMapError('Start location and destination are required');
      return;
    }

    if (!window.google || !window.google.maps) {
      setMapError('Google Maps API not loaded');
      return;
    }

    const initMap = async () => {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(async () => {
        // Double-check after animation frame
        await new Promise(resolve => setTimeout(resolve, 100));
        
        if (!mapRef.current) {
          setMapError('Map container not found');
          return;
        }

        // Prevent multiple initializations
        if (mapInitialized) {
          return;
        }

        try {
          const geocoder = new window.google.maps.Geocoder();
          
          // Geocode start location
          const startPromise = new Promise((resolve, reject) => {
            geocoder.geocode({ address: startLocation }, (results, status) => {
              if (status === 'OK' && results[0]) {
                resolve(results[0].geometry.location);
              } else {
                reject(new Error(`Could not geocode start location: ${status}`));
              }
            });
          });

          // Geocode destination
          const destPromise = new Promise((resolve, reject) => {
            geocoder.geocode({ address: destination }, (results, status) => {
              if (status === 'OK' && results[0]) {
                resolve(results[0].geometry.location);
              } else {
                reject(new Error(`Could not geocode destination: ${status}`));
              }
            });
          });

          const [startLatLng, destLatLng] = await Promise.all([startPromise, destPromise]);

          // Double-check mapRef is still available before creating map
          if (!mapRef.current) {
            setMapError('Map container was removed. Please try again.');
            return;
          }

          // Create map
          const map = new window.google.maps.Map(mapRef.current, {
            zoom: 7,
            center: startLatLng,
            mapTypeId: 'roadmap'
          });

          // Add markers
          new window.google.maps.Marker({
            position: startLatLng,
            map: map,
            title: startLocation,
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: '#10B981',
              fillOpacity: 1,
              strokeColor: '#FFFFFF',
              strokeWeight: 2
            }
          });

          new window.google.maps.Marker({
            position: destLatLng,
            map: map,
            title: destination,
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: '#EF4444',
              fillOpacity: 1,
              strokeColor: '#FFFFFF',
              strokeWeight: 2
            }
          });

          // Fit map to show both locations
          const bounds = new window.google.maps.LatLngBounds();
          bounds.extend(startLatLng);
          bounds.extend(destLatLng);
          map.fitBounds(bounds);

          // Add route using DirectionsService (if available)
          try {
            const directionsService = new window.google.maps.DirectionsService();
            const directionsRenderer = new window.google.maps.DirectionsRenderer({
              map: map,
              suppressMarkers: true // We already have custom markers
            });

            directionsService.route(
              {
                origin: startLatLng,
                destination: destLatLng,
                travelMode: window.google.maps.TravelMode.DRIVING
              },
              (result, status) => {
                if (status === 'OK') {
                  directionsRenderer.setDirections(result);
                  // Fit map to show entire route
                  const routeBounds = new window.google.maps.LatLngBounds();
                  result.routes[0].legs.forEach(leg => {
                    routeBounds.extend(leg.start_location);
                    routeBounds.extend(leg.end_location);
                  });
                  map.fitBounds(routeBounds);
                } else {
                  // If Directions API fails, just show markers (already done above)
                  console.warn('Directions request failed:', status);
                  // Map with markers is already displayed, so no error needed
                }
              }
            );
          } catch (error) {
            // If DirectionsService is not available, just show markers
            console.warn('DirectionsService not available:', error);
            // Map with markers is already displayed
          }

          setMapError(null);
          setMapInitialized(true);
        } catch (error) {
          console.error('Error initializing map:', error);
          setMapError(error.message || 'Failed to load map');
        }
      });
    };

    // Initialize map after ensuring DOM is ready
    initMap();
  }, [startLocation, destination, isGoogleLoaded, mapInitialized]);

  return (
    <div className="view-trip-map" ref={mapRef} style={{ height: '400px', width: '100%', minHeight: '400px', position: 'relative' }}>
      {mapError && (
        <div className="view-trip-map-placeholder" style={{ 
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          color: '#666',
          fontSize: '14px',
          backgroundColor: '#f9fafb',
          zIndex: 1
        }}>
          {mapError}
        </div>
      )}
    </div>
  );
};

const TripManagement = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateRangeFilter, setDateRangeFilter] = useState('');
  const [driverFilter, setDriverFilter] = useState('');
  const [vehicleFilter, setVehicleFilter] = useState('');
  const [agencyFilter, setAgencyFilter] = useState('');
  const [activeTab, setActiveTab] = useState("all trips");
  const [activeSubTab, setActiveSubTab] = useState('trips'); // 'trips' or 'trip-schedule'
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [dateRangeDropdownOpen, setDateRangeDropdownOpen] = useState(false);
  const [driverDropdownOpen, setDriverDropdownOpen] = useState(false);
  const [vehicleDropdownOpen, setVehicleDropdownOpen] = useState(false);
  const [agencyDropdownOpen, setAgencyDropdownOpen] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showEntriesDropdownOpen, setShowEntriesDropdownOpen] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTrip, setEditingTrip] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingTrip, setViewingTrip] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [tripToCancel, setTripToCancel] = useState(null);
  const [showCancelSuccess, setShowCancelSuccess] = useState(false);
  const [cancelledTripInfo, setCancelledTripInfo] = useState(null);
  const [showStopsModal, setShowStopsModal] = useState(false);
  const [selectedTripForStops, setSelectedTripForStops] = useState(null);
  const [showAddScheduleModal, setShowAddScheduleModal] = useState(false);
  const [showScheduleConfirmModal, setShowScheduleConfirmModal] = useState(false);
  const [showScheduleSuccessModal, setShowScheduleSuccessModal] = useState(false);
  const [savedScheduleInfo, setSavedScheduleInfo] = useState(null);
  const [showScheduleViewModal, setShowScheduleViewModal] = useState(false);
  const [viewingSchedule, setViewingSchedule] = useState(null);
  const [showScheduleEditModal, setShowScheduleEditModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [showCalendarPopup, setShowCalendarPopup] = useState(false);
  const calendarInputRef = useRef(null);
  const calendarPopupRef = useRef(null);
  const [scheduleStartLocationSearch, setScheduleStartLocationSearch] = useState('');
  const [scheduleDestinationSearch, setScheduleDestinationSearch] = useState('');
  const [scheduleStartLocationSuggestionsOpen, setScheduleStartLocationSuggestionsOpen] = useState(false);
  const [scheduleDestinationSuggestionsOpen, setScheduleDestinationSuggestionsOpen] = useState(false);
  const scheduleStartLocationInputRef = useRef(null);
  const scheduleDestinationInputRef = useRef(null);
  const scheduleStartLocationSuggestionsRef = useRef(null);
  const scheduleDestinationSuggestionsRef = useRef(null);
  const scheduleStopLocationInputRefs = useRef({});
  const scheduleStopAutocompleteRefs = useRef({});
  const [scheduleStopLocationSearches, setScheduleStopLocationSearches] = useState({});
  const [scheduleStopLocationSuggestionsOpen, setScheduleStopLocationSuggestionsOpen] = useState({});
  const scheduleStopLocationSuggestionsRefs = useRef({});
  const stopLocationInputRefs = useRef({});
  const stopAutocompleteRefs = useRef({});
  const [stopLocationSearches, setStopLocationSearches] = useState({});
  const [stopLocationSuggestionsOpen, setStopLocationSuggestionsOpen] = useState({});
  const stopLocationSuggestionsRefs = useRef({});
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const customerDropdownRef = useRef(null);
  const customerInputRef = useRef(null);
  const [scheduleFormData, setScheduleFormData] = useState({
    scheduleId: '',
    scheduleName: '',
    scheduleType: 'daily', // 'daily', 'weekly', 'monthly'
    tripDateTime: '',
    tripStatus: 'Scheduled',
    clientAgency: '',
    notes: '',
    vehicle: '',
    assignedDriver: '',
    routeName: '',
    startLocation: '',
    destination: '',
    stops: [],
    loadDescription: '',
    loadWeight: '',
    weightUnit: 'kg',
    loadVolume: '',
    volumeUnit: 'm³',
    loadType: '',
    totalCapacity: '',
    usedCapacity: '',
    selectedDays: [], // For weekly option
    monthlyDay: [], // For monthly option - array of dates (e.g., [1, 15, 20] for 1st, 15th, 20th of every month)
    endDate: '' // Optional end date for recurring schedules
  });
  const [scheduleFormErrors, setScheduleFormErrors] = useState({});
  const [tripSchedules, setTripSchedules] = useState([]);
  const [totalSchedules, setTotalSchedules] = useState(0);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [formData, setFormData] = useState({
    tripId: '',
    tripDateTime: '',
    tripStatus: 'Scheduled',
    clientAgency: '',
    customer: '',
    notes: '',
    vehicle: '',
    assignedDriver: '',
    routeName: '',
    startLocation: '',
    destination: '',
    stops: [],
    loadDescription: '',
    loadWeight: '',
    weightUnit: 'kg',
    loadVolume: '',
    volumeUnit: 'm³',
    loadType: '', // 'full load' or 'partial load'
    totalCapacity: '', // For partial load
    usedCapacity: '', // For partial load
    remainingCapacity: '' // For partial load (calculated)
  });
  const [formErrors, setFormErrors] = useState({});

  // Mock vehicle and agency data for dropdowns
  const mockVehicles = [
    { vehicleNumber: 'TN10AB1234', assignedDriver: 'John Doe', agency: 'Express Logistics' },
    { vehicleNumber: 'KA01CD5678', assignedDriver: 'Mike Johnson', agency: 'Global Transport' },
    { vehicleNumber: 'MH02EF9012', assignedDriver: 'Sarah Williams', agency: 'City Movers' },
    { vehicleNumber: 'DL03GH3456', assignedDriver: 'Robert Brown', agency: 'Express Logistics' },
    { vehicleNumber: 'GJ04IJ7890', assignedDriver: 'Emily Davis', agency: 'Global Transport' },
    { vehicleNumber: 'UP05KL1234', assignedDriver: 'David Wilson', agency: 'City Movers' },
    { vehicleNumber: 'HR06MN5678', assignedDriver: 'Jennifer Taylor', agency: 'Express Logistics' }
  ];

  const mockAgencies = ['Express Logistics', 'Global Transport', 'City Movers'];

  // Get all unique drivers from mockVehicles
  const allDrivers = [...new Set(mockVehicles.map(v => v.assignedDriver))];

  // Mock trip data based on the image
  const initialTrips = [
    {
      id: 'TRP1001',
      tripDateTime: '15 Jun 2023, 08:30 AM',
      agency: 'Express Logistics',
      vehicleNumber: 'TN10AB1234',
      driverName: 'John Doe',
      route: 'Chennai Central → Bangalore Hub',
      stops: 3,
      loadWeight: 5200,
      status: 'In Progress',
      eta: '2h 15m remaining'
    },
    {
      id: 'TRP1002',
      tripDateTime: '15 Jun 2023, 10:00 AM',
      agency: 'Global Transport',
      vehicleNumber: 'KA01CD5678',
      driverName: 'Mike Johnson',
      route: 'Mumbai Port → Delhi Warehouse',
      stops: 5,
      loadWeight: 8500,
      status: 'Scheduled',
      eta: 'Starts in 45m'
    },
    {
      id: 'TRP1003',
      tripDateTime: '15 Jun 2023, 06:00 AM',
      agency: 'City Movers',
      vehicleNumber: 'MH02EF9012',
      driverName: 'Sarah Williams',
      route: 'Pune Hub → Hyderabad Depot',
      stops: 2,
      loadWeight: 3200,
      status: 'Completed',
      eta: 'Completed 3h 25m ago'
    },
    {
      id: 'TRP1004',
      tripDateTime: '14 Jun 2023, 02:00 PM',
      agency: 'Express Logistics',
      vehicleNumber: 'DL03GH3456',
      driverName: 'Robert Brown',
      route: 'Kolkata Station → Chennai Port',
      stops: 4,
      loadWeight: 6700,
      status: 'Delayed',
      eta: 'Delayed by 1h 30m'
    },
    {
      id: 'TRP1005',
      tripDateTime: '15 Jun 2023, 11:30 AM',
      agency: 'Global Transport',
      vehicleNumber: 'GJ04IJ7890',
      driverName: 'Emily Davis',
      route: 'Ahmedabad → Surat',
      stops: 1,
      loadWeight: 4100,
      status: 'Scheduled',
      eta: 'Starts in 2h 15m'
    },
    {
      id: 'TRP1006',
      tripDateTime: '15 Jun 2023, 09:15 AM',
      agency: 'City Movers',
      vehicleNumber: 'UP05KL1234',
      driverName: 'David Wilson',
      route: 'Lucknow → Kanpur',
      stops: 2,
      loadWeight: 2800,
      status: 'In Progress',
      eta: '1h 45m remaining'
    },
    {
      id: 'TRP1007',
      tripDateTime: '14 Jun 2023, 05:00 PM',
      agency: 'Express Logistics',
      vehicleNumber: 'HR06MN5678',
      driverName: 'Jennifer Taylor',
      route: 'Gurgaon → Noida',
      stops: 0,
      loadWeight: 1500,
      status: 'Cancelled',
      eta: 'N/A'
    },
    {
      id: 'TRP1008',
      tripDateTime: '15 Jun 2023, 12:00 PM',
      agency: 'Global Transport',
      vehicleNumber: 'TN10AB1234',
      driverName: 'John Doe',
      route: 'Bangalore Hub → Coimbatore',
      stops: 2,
      loadWeight: 4800,
      status: 'Scheduled',
      eta: 'Starts in 3h 30m'
    }
  ];

  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formDataOptions, setFormDataOptions] = useState({
    agencies: [],
    drivers: [],
    vehicles: []
  });
  const [totalTrips, setTotalTrips] = useState(0);
  
  // Filter lists - all drivers, vehicles, and agencies for filter dropdowns
  const [allDriversForFilter, setAllDriversForFilter] = useState([]);
  const [allVehiclesForFilter, setAllVehiclesForFilter] = useState([]);
  const [allAgenciesForFilter, setAllAgenciesForFilter] = useState([]);
  const [allCustomersForFilter, setAllCustomersForFilter] = useState([]);

  // Create lookup maps for agencies, vehicles, and drivers
  const agencyLookup = useMemo(() => {
    const lookup = new Map();
    if (formDataOptions.agencies && Array.isArray(formDataOptions.agencies)) {
      formDataOptions.agencies.forEach(agency => {
        const id = agency.id || agency.agency_id;
        const name = agency.agency_name || agency.name || agency.company_name || agency.agencyName;
        if (id && name) {
          lookup.set(id, name);
        }
      });
    }
    return lookup;
  }, [formDataOptions.agencies]);

  const vehicleLookup = useMemo(() => {
    const lookup = new Map();
    if (formDataOptions.vehicles && Array.isArray(formDataOptions.vehicles)) {
      formDataOptions.vehicles.forEach(vehicle => {
        const id = vehicle.id || vehicle.vehicle_id;
        const number = vehicle.vehicle_number || vehicle.vehicleNumber;
        if (id && number) {
          lookup.set(id, number);
        }
      });
    }
    return lookup;
  }, [formDataOptions.vehicles]);

  const driverLookup = useMemo(() => {
    const lookup = new Map();
    if (formDataOptions.drivers && Array.isArray(formDataOptions.drivers)) {
      formDataOptions.drivers.forEach(driver => {
        const id = driver.id || driver.driver_id;
        const name = driver.full_name || driver.name || driver.driverName;
        if (id && name) {
          lookup.set(id, name);
        }
      });
    }
    return lookup;
  }, [formDataOptions.drivers]);

  // Map API response (snake_case) to component format (camelCase)
  const mapTripFromAPI = useCallback((apiTrip) => {
    // Format date-time
    let tripDateTime = '';
    if (apiTrip.trip_date_time) {
      const date = new Date(apiTrip.trip_date_time);
      if (!isNaN(date.getTime())) {
        tripDateTime = date.toLocaleString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
      }
    }

    // Extract vehicle number from nested vehicle object, lookup map, or fallback
    let vehicleNumber = apiTrip.vehicle?.vehicle_number || apiTrip.vehicle_number;
    if (!vehicleNumber && apiTrip.vehicle_id) {
      vehicleNumber = vehicleLookup.get(apiTrip.vehicle_id) || null;
    }
    vehicleNumber = vehicleNumber || (apiTrip.vehicle_id ? `Vehicle #${apiTrip.vehicle_id}` : 'N/A');

    // Extract driver name from nested driver object, lookup map, or fallback
    let driverName = apiTrip.driver?.full_name || 
                     apiTrip.driver_name || 
                     apiTrip.assigned_driver?.full_name;
    if (!driverName && apiTrip.assigned_driver_id) {
      driverName = driverLookup.get(apiTrip.assigned_driver_id) || null;
    }
    driverName = driverName || (apiTrip.assigned_driver_id ? `Driver #${apiTrip.assigned_driver_id}` : 'Not Assigned');

    // Extract agency name from nested agency object, lookup map, or fallback
    let agency = apiTrip.agency?.agency_name || 
                 apiTrip.agency_name || 
                 apiTrip.client_agency;
    if (!agency && apiTrip.agency_id) {
      agency = agencyLookup.get(apiTrip.agency_id) || null;
    }
    agency = agency || (apiTrip.agency_id ? `Agency #${apiTrip.agency_id}` : 'N/A');

    // Build route from start and destination
    const route = apiTrip.start_location && apiTrip.destination
      ? `${apiTrip.start_location} → ${apiTrip.destination}`
      : apiTrip.route || 'N/A';

    // Get stops count - check multiple possible field names
    let stopsCount = 0;
    if (Array.isArray(apiTrip.stops) && apiTrip.stops.length > 0) {
      stopsCount = apiTrip.stops.length;
    } else if (apiTrip.stops_count !== undefined && apiTrip.stops_count !== null) {
      stopsCount = Number(apiTrip.stops_count) || 0;
    } else if (apiTrip.stopsCount !== undefined && apiTrip.stopsCount !== null) {
      stopsCount = Number(apiTrip.stopsCount) || 0;
    } else if (apiTrip.stop_count !== undefined && apiTrip.stop_count !== null) {
      stopsCount = Number(apiTrip.stop_count) || 0;
    }

    // Format load weight
    const loadWeight = apiTrip.load_weight || 0;
    const weightUnit = apiTrip.weight_unit || 'kg';

    // Calculate or get ETA
    const eta = apiTrip.eta || apiTrip.estimated_duration || 'N/A';

    return {
      id: apiTrip.id || apiTrip.trip_id || '',
      tripId: apiTrip.trip_id || apiTrip.id || '',
      tripDateTime: tripDateTime || apiTrip.trip_date_time || '',
      status: apiTrip.trip_status || apiTrip.status || 'Scheduled',
      agency: agency,
      vehicleNumber: vehicleNumber,
      driverName: driverName,
      route: route,
      startLocation: apiTrip.start_location || '',
      destination: apiTrip.destination || '',
      stops: stopsCount,
      stopsData: apiTrip.stops || [],
      loadWeight: loadWeight,
      weightUnit: weightUnit,
      loadVolume: apiTrip.load_volume || 0,
      volumeUnit: apiTrip.volume_unit || 'm³',
      loadDescription: apiTrip.load_description || '',
      estimatedDistance: apiTrip.estimated_distance || '',
      estimatedDuration: apiTrip.estimated_duration || '',
      eta: eta,
      notes: apiTrip.notes || '',
      vehicleId: apiTrip.vehicle_id,
      driverId: apiTrip.assigned_driver_id,
      agencyId: apiTrip.agency_id,
      customerId: apiTrip.customer_id,
      scheduleId: apiTrip.schedule_id,
      // Keep original API data for reference
      _original: apiTrip
    };
  }, [agencyLookup, vehicleLookup, driverLookup]);

  const statusDropdownRef = useRef(null);
  const dateRangeDropdownRef = useRef(null);
  const driverDropdownRef = useRef(null);
  const vehicleDropdownRef = useRef(null);
  const agencyDropdownRef = useRef(null);
  const showEntriesDropdownRef = useRef(null);
  const startLocationInputRef = useRef(null);
  const destinationInputRef = useRef(null);
  const startLocationSuggestionsRef = useRef(null);
  const destinationSuggestionsRef = useRef(null);

  // Indian cities list (all cities from all states)
  const getAllIndianCities = () => {
    const indianStatesAndCities = {
      'Andhra Pradesh': ['Visakhapatnam', 'Vijayawada', 'Guntur', 'Nellore', 'Kurnool', 'Rajahmundry', 'Tirupati', 'Kakinada', 'Kadapa', 'Anantapur', 'Eluru', 'Ongole', 'Nandyal', 'Machilipatnam', 'Adoni', 'Tenali', 'Chittoor', 'Hindupur', 'Proddatur', 'Bhimavaram'],
      'Arunachal Pradesh': ['Itanagar', 'Naharlagun', 'Tawang', 'Bomdila', 'Pasighat', 'Tezu', 'Ziro', 'Along', 'Daporijo', 'Namsai', 'Roing', 'Khonsa', 'Aalo', 'Seppa', 'Yingkiong'],
      'Assam': ['Guwahati', 'Silchar', 'Dibrugarh', 'Jorhat', 'Nagaon', 'Tinsukia', 'Tezpur', 'Bongaigaon', 'Dhubri', 'Diphu', 'North Lakhimpur', 'Karimganj', 'Goalpara', 'Barpeta', 'Sivasagar', 'Hailakandi', 'Golaghat', 'Dhekiajuli', 'Mangaldoi'],
      'Bihar': ['Patna', 'Gaya', 'Bhagalpur', 'Muzaffarpur', 'Purnia', 'Darbhanga', 'Arrah', 'Bihar Sharif', 'Katihar', 'Munger', 'Chapra', 'Sasaram', 'Hajipur', 'Sitamarhi', 'Samastipur', 'Bettiah', 'Motihari', 'Saharsa', 'Buxar', 'Kishanganj'],
      'Chhattisgarh': ['Raipur', 'Bhilai', 'Bilaspur', 'Durg', 'Korba', 'Rajpur', 'Raigarh', 'Jagdalpur', 'Ambikapur', 'Chirmiri', 'Dhamtari', 'Dantewada', 'Kanker', 'Kawardha', 'Mahasamund', 'Surajpur'],
      'Goa': ['Panaji', 'Margao', 'Vasco da Gama', 'Mapusa', 'Ponda', 'Bicholim', 'Valpoi', 'Curchorem', 'Quepem', 'Canacona'],
      'Gujarat': ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Bhavnagar', 'Jamnagar', 'Gandhinagar', 'Junagadh', 'Gandhidham', 'Anand', 'Navsari', 'Morbi', 'Nadiad', 'Surendranagar', 'Bharuch', 'Mehsana', 'Bhuj', 'Porbandar', 'Palanpur', 'Valsad'],
      'Haryana': ['Faridabad', 'Gurgaon', 'Panipat', 'Ambala', 'Yamunanagar', 'Rohtak', 'Hisar', 'Karnal', 'Sonipat', 'Panchkula', 'Sirsa', 'Bhiwani', 'Bahadurgarh', 'Jind', 'Thanesar', 'Kaithal', 'Rewari', 'Palwal', 'Fatehabad', 'Hansi'],
      'Himachal Pradesh': ['Shimla', 'Mandi', 'Solan', 'Dharamshala', 'Bilaspur', 'Kullu', 'Chamba', 'Hamirpur', 'Una', 'Kangra', 'Nahan', 'Palampur', 'Sundarnagar', 'Nurpur', 'Keylong'],
      'Jharkhand': ['Ranchi', 'Jamshedpur', 'Dhanbad', 'Bokaro Steel City', 'Hazaribagh', 'Deoghar', 'Giridih', 'Adityapur', 'Ramgarh', 'Medininagar', 'Chirkunda', 'Phusro', 'Chaibasa', 'Gumla', 'Lohardaga', 'Dumka', 'Pakur', 'Godda'],
      'Karnataka': ['Bangalore', 'Mysore', 'Hubli', 'Mangalore', 'Belagavi', 'Gulbarga', 'Davangere', 'Bellary', 'Bijapur', 'Shimoga', 'Tumkur', 'Raichur', 'Bidar', 'Hospet', 'Hassan', 'Udupi', 'Gadag', 'Chitradurga', 'Kolar', 'Mandya'],
      'Kerala': ['Kochi', 'Kozhikode', 'Thiruvananthapuram', 'Thrissur', 'Kollam', 'Kannur', 'Alappuzha', 'Kottayam', 'Palakkad', 'Manjeri', 'Thalassery', 'Ponnani', 'Kochi', 'Kayamkulam', 'Kottarakkara', 'Changanassery', 'Kasaragod', 'Pathanamthitta', 'Idukki', 'Wayanad'],
      'Madhya Pradesh': ['Bhopal', 'Indore', 'Gwalior', 'Jabalpur', 'Ujjain', 'Sagar', 'Ratlam', 'Satna', 'Rewa', 'Murwara', 'Singrauli', 'Burhanpur', 'Khandwa', 'Morena', 'Bhind', 'Chhindwara', 'Vidisha', 'Guna', 'Shivpuri', 'Damoh'],
      'Maharashtra': ['Mumbai', 'Pune', 'Nagpur', 'Thane', 'Nashik', 'Aurangabad', 'Solapur', 'Kalyan', 'Vasai-Virar', 'Navi Mumbai', 'Amravati', 'Nanded', 'Kolhapur', 'Sangli', 'Jalgaon', 'Akola', 'Latur', 'Dhule', 'Ahmednagar', 'Ichalkaranji', 'Jalna', 'Bhusawal', 'Panvel', 'Satara', 'Beed', 'Yavatmal', 'Kamptee', 'Gondia', 'Barshi', 'Achalpur'],
      'Manipur': ['Imphal', 'Thoubal', 'Kakching', 'Ukhrul', 'Bishnupur', 'Churachandpur', 'Senapati', 'Tamenglong', 'Jiribam', 'Moreh', 'Lilong', 'Moirang', 'Mayang Imphal'],
      'Meghalaya': ['Shillong', 'Tura', 'Nongpoh', 'Jowai', 'Nongstoin', 'Baghmara', 'Williamnagar', 'Resubelpara', 'Mankachar', 'Ampati'],
      'Mizoram': ['Aizawl', 'Lunglei', 'Saiha', 'Champhai', 'Kolasib', 'Serchhip', 'Lawngtlai', 'Mamit', 'Khawzawl', 'Hnahthial'],
      'Nagaland': ['Kohima', 'Dimapur', 'Mokokchung', 'Tuensang', 'Wokha', 'Zunheboto', 'Phek', 'Mon', 'Kiphire', 'Longleng', 'Peren'],
      'Odisha': ['Bhubaneswar', 'Cuttack', 'Rourkela', 'Berhampur', 'Sambalpur', 'Puri', 'Baleshwar', 'Bhadrak', 'Baripada', 'Balangir', 'Jharsuguda', 'Bargarh', 'Rayagada', 'Jeypore', 'Dhenkanal', 'Barbil', 'Kendujhar', 'Paradip', 'Bhuban', 'Talcher'],
      'Punjab': ['Ludhiana', 'Amritsar', 'Jalandhar', 'Patiala', 'Bathinda', 'Pathankot', 'Hoshiarpur', 'Batala', 'Moga', 'Abohar', 'Khanna', 'Phagwara', 'Muktsar', 'Barnala', 'Firozpur', 'Kapurthala', 'Sultanpur Lodhi', 'Fazilka', 'Gurdaspur', 'Malerkotla'],
      'Rajasthan': ['Jaipur', 'Jodhpur', 'Kota', 'Bikaner', 'Ajmer', 'Udaipur', 'Bhilwara', 'Alwar', 'Bharatpur', 'Sri Ganganagar', 'Sikar', 'Pali', 'Tonk', 'Chittorgarh', 'Hanumangarh', 'Beawar', 'Karauli', 'Dholpur', 'Bundi', 'Baran', 'Churu', 'Jhunjhunu', 'Nagaur', 'Jhalawar', 'Banswara'],
      'Sikkim': ['Gangtok', 'Namchi', 'Mangan', 'Gyalshing', 'Singtam', 'Rangpo', 'Jorethang', 'Pakyong', 'Rhenock', 'Soreng'],
      'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai', 'Tiruchirappalli', 'Salem', 'Tirunelveli', 'Tiruppur', 'Erode', 'Vellore', 'Thoothukudi', 'Dindigul', 'Thanjavur', 'Hosur', 'Nagercoil', 'Kanchipuram', 'Karaikudi', 'Neyveli', 'Cuddalore', 'Kumbakonam', 'Tiruvannamalai', 'Pollachi', 'Rajapalayam', 'Gudiyatham', 'Pudukkottai', 'Ambur'],
      'Telangana': ['Hyderabad', 'Warangal', 'Nizamabad', 'Karimnagar', 'Ramagundam', 'Khammam', 'Mahbubnagar', 'Nalgonda', 'Adilabad', 'Suryapet', 'Miryalaguda', 'Siddipet', 'Jagtial', 'Mancherial', 'Kamareddy', 'Kothagudem', 'Bhongir', 'Bodhan', 'Narayanpet', 'Wanaparthy'],
      'Tripura': ['Agartala', 'Udaipur', 'Dharmanagar', 'Kailashahar', 'Belonia', 'Khowai', 'Ambassa', 'Kumarghat', 'Sabroom', 'Teliamura'],
      'Uttar Pradesh': ['Lucknow', 'Kanpur', 'Ghaziabad', 'Agra', 'Meerut', 'Varanasi', 'Allahabad', 'Bareilly', 'Aligarh', 'Moradabad', 'Saharanpur', 'Gorakhpur', 'Noida', 'Firozabad', 'Jhansi', 'Muzaffarnagar', 'Mathura', 'Rampur', 'Shahjahanpur', 'Farrukhabad', 'Fatehpur', 'Budaun', 'Sitapur', 'Hapur', 'Etawah', 'Unnao', 'Pilibhit', 'Lakhimpur', 'Basti', 'Deoria'],
      'Uttarakhand': ['Dehradun', 'Haridwar', 'Roorkee', 'Haldwani', 'Rudrapur', 'Kashipur', 'Rishikesh', 'Nainital', 'Mussoorie', 'Pithoragarh', 'Almora', 'Srinagar', 'Kotdwara', 'Ramnagar', 'Pauri', 'Chamoli Gopeshwar', 'New Tehri', 'Uttarkashi'],
      'West Bengal': ['Kolkata', 'Howrah', 'Durgapur', 'Asansol', 'Siliguri', 'Bardhaman', 'Malda', 'Baharampur', 'Habra', 'Kharagpur', 'Shantipur', 'Ranaghat', 'Krishnanagar', 'Nabadwip', 'Medinipur', 'Chandannagar', 'Jalpaiguri', 'Baidyabati', 'Darjeeling', 'Purulia', 'Bankura', 'Alipurduar', 'Raiganj'],
      'Delhi': ['New Delhi', 'Delhi Cantonment', 'Narela', 'Najafgarh', 'Rohini', 'Dwarka', 'Patparganj'],
      'Jammu and Kashmir': ['Srinagar', 'Jammu', 'Anantnag', 'Baramulla', 'Sopore', 'Kathua', 'Udhampur', 'Poonch', 'Rajouri', 'Kupwara', 'Bandipora', 'Ganderbal', 'Pulwama', 'Shopian', 'Budgam', 'Kulgam', 'Doda', 'Ramban', 'Kishtwar', 'Reasi'],
      'Ladakh': ['Leh', 'Kargil', 'Drass', 'Nubra Valley', 'Zanskar'],
      'Puducherry': ['Puducherry', 'Karaikal', 'Mahe', 'Yanam'],
      'Andaman and Nicobar Islands': ['Port Blair', 'Diglipur', 'Mayabunder', 'Rangat', 'Car Nicobar'],
      'Chandigarh': ['Chandigarh'],
      'Dadra and Nagar Haveli and Daman and Diu': ['Silvassa', 'Daman', 'Diu'],
      'Lakshadweep': ['Kavaratti', 'Agatti', 'Amini', 'Andrott', 'Kadmat', 'Kalpeni', 'Kiltan', 'Minicoy']
    };
    
    const allCities = [];
    Object.values(indianStatesAndCities).forEach(cities => {
      allCities.push(...cities);
    });
    return [...new Set(allCities)].sort();
  };

  const indianCities = getAllIndianCities();
  const [startLocationSearch, setStartLocationSearch] = useState('');
  const [destinationSearch, setDestinationSearch] = useState('');
  const [startLocationSuggestionsOpen, setStartLocationSuggestionsOpen] = useState(false);
  const [destinationSuggestionsOpen, setDestinationSuggestionsOpen] = useState(false);

  // Filter cities based on search
  const filteredStartCities = useMemo(() => {
    if (!startLocationSearch.trim()) return [];
    const searchLower = startLocationSearch.toLowerCase();
    return indianCities
      .filter(city => city.toLowerCase().includes(searchLower))
      .slice(0, 10); // Limit to 10 suggestions
  }, [startLocationSearch, indianCities]);

  const filteredDestinationCities = useMemo(() => {
    if (!destinationSearch.trim()) return [];
    const searchLower = destinationSearch.toLowerCase();
    return indianCities
      .filter(city => city.toLowerCase().includes(searchLower))
      .slice(0, 10); // Limit to 10 suggestions
  }, [destinationSearch, indianCities]);

  // Filter cities for schedule form
  const filteredScheduleStartCities = useMemo(() => {
    if (!scheduleStartLocationSearch.trim()) return [];
    const searchLower = scheduleStartLocationSearch.toLowerCase();
    return indianCities
      .filter(city => city.toLowerCase().includes(searchLower))
      .slice(0, 10); // Limit to 10 suggestions
  }, [scheduleStartLocationSearch, indianCities]);

  const filteredScheduleDestinationCities = useMemo(() => {
    if (!scheduleDestinationSearch.trim()) return [];
    const searchLower = scheduleDestinationSearch.toLowerCase();
    return indianCities
      .filter(city => city.toLowerCase().includes(searchLower))
      .slice(0, 10); // Limit to 10 suggestions
  }, [scheduleDestinationSearch, indianCities]);

  // Handle start location input
  const handleStartLocationInput = (value) => {
    setStartLocationSearch(value);
    setFormData(prev => ({ ...prev, startLocation: value }));
    // Show suggestions if there's text and matches exist
    if (value.trim().length > 0) {
      const searchLower = value.toLowerCase();
      const hasMatches = indianCities.some(city => city.toLowerCase().includes(searchLower));
      setStartLocationSuggestionsOpen(hasMatches);
    } else {
      setStartLocationSuggestionsOpen(false);
    }
  };

  // Handle destination input
  const handleDestinationInput = (value) => {
    setDestinationSearch(value);
    setFormData(prev => ({ ...prev, destination: value }));
    // Show suggestions if there's text and matches exist
    if (value.trim().length > 0) {
      const searchLower = value.toLowerCase();
      const hasMatches = indianCities.some(city => city.toLowerCase().includes(searchLower));
      setDestinationSuggestionsOpen(hasMatches);
    } else {
      setDestinationSuggestionsOpen(false);
    }
  };

  // Handle start location suggestion select (fallback for city list)
  const handleStartLocationCitySelect = (city) => {
    setFormData(prev => ({ ...prev, startLocation: city }));
    setStartLocationSearch(city);
    setStartLocationSuggestionsOpen(false);
  };

  // Handle destination suggestion select (fallback for city list)
  const handleDestinationCitySelect = (city) => {
    setFormData(prev => ({ ...prev, destination: city }));
    setDestinationSearch(city);
    setDestinationSuggestionsOpen(false);
  };

  // Handle start location selection from Google Places
  const handleStartLocationSelect = (address, place) => {
    setStartLocationSearch(address);
    setFormData(prev => ({ ...prev, startLocation: address }));
    setStartLocationSuggestionsOpen(false);
  };

  // Handle destination selection from Google Places
  const handleDestinationSelect = (address, place) => {
    setDestinationSearch(address);
    setFormData(prev => ({ ...prev, destination: address }));
    setDestinationSuggestionsOpen(false);
  };

  // Google Places Autocomplete for start location
  const startLocationAutocomplete = useGooglePlacesAutocomplete({
    value: formData.startLocation,
    onPlaceSelect: handleStartLocationSelect,
    restrictions: { country: 'in' },
    types: ['geocode', 'establishment']
  });

  // Google Places Autocomplete for destination
  const destinationAutocomplete = useGooglePlacesAutocomplete({
    value: formData.destination,
    onPlaceSelect: handleDestinationSelect,
    restrictions: { country: 'in' },
    types: ['geocode', 'establishment']
  });

  // Check if Google Maps is loaded (for showing/hiding fallback city dropdown)
  const isGoogleMapsLoaded = startLocationAutocomplete.isGoogleLoaded || (window.google && window.google.maps && window.google.maps.places);

  // Calculate remaining capacity when used capacity or total capacity changes
  useEffect(() => {
    if (formData.totalCapacity && formData.usedCapacity) {
      const total = parseFloat(formData.totalCapacity) || 0;
      const used = parseFloat(formData.usedCapacity) || 0;
      // Calculate remaining capacity
      const remaining = Math.max(0, total - used);
      
      setFormData(prev => ({
        ...prev,
        remainingCapacity: remaining.toFixed(2)
      }));
    } else {
      // Clear remaining capacity if total or used capacity is missing
      setFormData(prev => ({
        ...prev,
        remainingCapacity: ''
      }));
    }
  }, [formData.totalCapacity, formData.usedCapacity]);

  // Use all drivers, vehicles, and agencies from services for filters (not just from trips)
  const uniqueDrivers = useMemo(() => allDriversForFilter, [allDriversForFilter]);
  const uniqueVehicles = useMemo(() => allVehiclesForFilter, [allVehiclesForFilter]);
  const uniqueAgencies = useMemo(() => allAgenciesForFilter, [allAgenciesForFilter]);

  const uniqueStatuses = ['All', 'Scheduled', 'In Progress', 'Completed', 'Cancelled', 'Delayed'];

  // Get display names for active filters
  const driverFilterDisplay = useMemo(() => {
    if (!driverFilter) return '';
    const driverId = Number(driverFilter);
    const driver = uniqueDrivers.find(d => d.id === driverId);
    return driver?.name || driverFilter;
  }, [driverFilter, uniqueDrivers]);

  const vehicleFilterDisplay = useMemo(() => {
    if (!vehicleFilter) return '';
    const vehicleId = Number(vehicleFilter);
    const vehicle = uniqueVehicles.find(v => v.id === vehicleId);
    return vehicle?.number || vehicleFilter;
  }, [vehicleFilter, uniqueVehicles]);

  const agencyFilterDisplay = useMemo(() => {
    if (!agencyFilter) return '';
    const agencyId = Number(agencyFilter);
    const agency = uniqueAgencies.find(a => a.id === agencyId);
    return agency?.name || agencyFilter;
  }, [agencyFilter, uniqueAgencies]);
  
  // Use all drivers, vehicles, and agencies from services for schedule filters (same as trips)
  const uniqueScheduleDrivers = useMemo(() => allDriversForFilter, [allDriversForFilter]);
  const uniqueScheduleVehicles = useMemo(() => allVehiclesForFilter, [allVehiclesForFilter]);
  const uniqueScheduleAgencies = useMemo(() => allAgenciesForFilter, [allAgenciesForFilter]);
  const uniqueScheduleStatuses = ['All', 'Scheduled', 'Active', 'Paused', 'Cancelled'];

  // Filter trips based on search only
  // Note: Driver, Vehicle, Agency, and Status filters are handled by the API server-side
  // We only do client-side search filtering for instant feedback
  // This prevents double-filtering which causes data to disappear when API filters are applied
  const filteredTrips = useMemo(() => {
    // If no search query, return all trips (API has already filtered by driver/vehicle/agency/status)
    if (!searchQuery || searchQuery.trim() === '') {
      return trips;
    }
    
    // Only apply client-side search filter
    const searchLower = searchQuery.toLowerCase();
    return trips.filter(trip => {
      return (
        (trip.id && trip.id.toLowerCase().includes(searchLower)) ||
        (trip.tripId && trip.tripId.toLowerCase().includes(searchLower)) ||
        (trip.vehicleNumber && trip.vehicleNumber.toLowerCase().includes(searchLower)) ||
        (trip.driverName && trip.driverName.toLowerCase().includes(searchLower)) ||
        (trip.route && trip.route.toLowerCase().includes(searchLower)) ||
        (trip.agency && trip.agency.toLowerCase().includes(searchLower))
      );
    });
  }, [trips, searchQuery]);

  // Filter trip schedules based on search only
  // Note: Driver, Vehicle, Agency, and Status filters are handled by the API server-side
  // We only do client-side search filtering for instant feedback
  const filteredSchedules = useMemo(() => {
    const schedulesList = tripSchedules || [];
    
    // If no search query, return all schedules (API has already filtered by driver/vehicle/agency/status)
    if (!searchQuery || searchQuery.trim() === '') {
      return schedulesList;
    }
    
    // Only apply client-side search filter
    const searchLower = searchQuery.toLowerCase();
    return schedulesList.filter(schedule => {
      if (!schedule) return false;
      return (
        (schedule.scheduleId && schedule.scheduleId.toLowerCase().includes(searchLower)) ||
        (schedule.scheduleName && schedule.scheduleName.toLowerCase().includes(searchLower)) ||
        (schedule.vehicle && schedule.vehicle.toLowerCase().includes(searchLower)) ||
        (schedule.assignedDriver && schedule.assignedDriver.toLowerCase().includes(searchLower)) ||
        (schedule.routeName && schedule.routeName.toLowerCase().includes(searchLower)) ||
        (schedule.startLocation && schedule.startLocation.toLowerCase().includes(searchLower)) ||
        (schedule.destination && schedule.destination.toLowerCase().includes(searchLower))
      );
    });
  }, [tripSchedules, searchQuery]);

  // Pagination
  const totalPages = Math.ceil(filteredTrips.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTrips = filteredTrips.slice(startIndex, endIndex);
  
  // Pagination for schedules - API handles pagination, so we use filteredSchedules directly
  const totalSchedulePages = Math.ceil(totalSchedules / itemsPerPage);
  const currentSchedules = filteredSchedules; // API already returns paginated data

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setStatusDropdownOpen(false);
      }
      if (dateRangeDropdownRef.current && !dateRangeDropdownRef.current.contains(event.target)) {
        setDateRangeDropdownOpen(false);
      }
      if (driverDropdownRef.current && !driverDropdownRef.current.contains(event.target)) {
        setDriverDropdownOpen(false);
      }
      if (vehicleDropdownRef.current && !vehicleDropdownRef.current.contains(event.target)) {
        setVehicleDropdownOpen(false);
      }
      if (agencyDropdownRef.current && !agencyDropdownRef.current.contains(event.target)) {
        setAgencyDropdownOpen(false);
      }
      if (showEntriesDropdownRef.current && !showEntriesDropdownRef.current.contains(event.target)) {
        setShowEntriesDropdownOpen(false);
      }
      // Handle click outside for customer dropdown
      if (customerInputRef.current && customerDropdownRef.current) {
        if (!customerInputRef.current.contains(event.target) && 
            !customerDropdownRef.current.contains(event.target)) {
          setCustomerDropdownOpen(false);
        }
      }
      // Handle click outside for start location (only for fallback city dropdown when Google Maps is not loaded)
      const isGoogleLoaded = window.google && window.google.maps && window.google.maps.places;
      if (!isGoogleLoaded) {
        const startInputRef = startLocationAutocomplete?.inputRef?.current || startLocationInputRef.current;
        if (startInputRef && startLocationSuggestionsRef.current) {
          if (!startInputRef.contains(event.target) && 
              !startLocationSuggestionsRef.current.contains(event.target)) {
            setStartLocationSuggestionsOpen(false);
          }
        } else if (startInputRef && !startInputRef.contains(event.target)) {
          setStartLocationSuggestionsOpen(false);
        }
      }
      // Handle click outside for destination (only for fallback city dropdown when Google Maps is not loaded)
      if (!isGoogleLoaded) {
        const destInputRef = destinationAutocomplete?.inputRef?.current || destinationInputRef.current;
        if (destInputRef && destinationSuggestionsRef.current) {
          if (!destInputRef.contains(event.target) &&
              !destinationSuggestionsRef.current.contains(event.target)) {
            setDestinationSuggestionsOpen(false);
          }
        } else if (destInputRef && !destInputRef.contains(event.target)) {
          setDestinationSuggestionsOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch trips function - extracted to be reusable
  const fetchTrips = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Build query parameters
      // For admin, fetch trips with pagination (API max limit is 100)
      const params = {
        limit: 100, // API maximum limit is 100
        offset: (currentPage - 1) * itemsPerPage,
      };

      // Add filter_type based on activeTab
      if (activeTab === "all trips") {
        params.filter_type = 'all';
      } else if (activeTab === "today's trips") {
        params.filter_type = 'today';
      } else if (activeTab === 'delayed trips') {
        params.filter_type = 'delayed';
      } else if (activeTab === 'upcoming departures') {
        params.filter_type = 'upcoming';
      } else if (activeTab === 'recent completions') {
        params.filter_type = 'recent_completions';
      }

      // Only apply filters if they are explicitly set
      // Admin should see all trips from all agencies by default
      
      // Trip Status filter - use exact status value
      if (statusFilter) {
        params.trip_status = statusFilter;
      }

      // Driver filter - convert to ID if needed
      if (driverFilter) {
        if (typeof driverFilter === 'number' || !isNaN(Number(driverFilter))) {
          params.assigned_driver_id = Number(driverFilter);
        } else {
          // Try to find driver ID from allDriversForFilter
          const driver = allDriversForFilter.find(d => 
            (d.name || d.full_name || '') === driverFilter ||
            (d.id && d.id.toString() === driverFilter.toString())
          );
          if (driver) {
            params.assigned_driver_id = driver.id;
          }
        }
      }

      // Vehicle filter - convert to ID if needed
      if (vehicleFilter) {
        if (typeof vehicleFilter === 'number' || !isNaN(Number(vehicleFilter))) {
          params.vehicle_id = Number(vehicleFilter);
        } else {
          // Try to find vehicle ID from allVehiclesForFilter
          const vehicle = allVehiclesForFilter.find(v => 
            (v.number || v.vehicle_number || '') === vehicleFilter ||
            (v.id && v.id.toString() === vehicleFilter.toString())
          );
          if (vehicle) {
            params.vehicle_id = vehicle.id;
          }
        }
      }

      // Agency filter - convert to ID if needed
      if (agencyFilter) {
        if (typeof agencyFilter === 'number' || !isNaN(Number(agencyFilter))) {
          params.agency_id = Number(agencyFilter);
        } else {
          // Try to find agency ID from allAgenciesForFilter
          const agency = allAgenciesForFilter.find(a => 
            (a.name || a.agency_name || '') === agencyFilter ||
            (a.id && a.id.toString() === agencyFilter.toString())
          );
          if (agency) {
            params.agency_id = agency.id;
          }
        }
      }
      
      // Handle date range filter - convert text to ISO dates
      if (dateRangeFilter) {
        const now = new Date();
        let startDate, endDate;
        
        if (dateRangeFilter === 'Today') {
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
          endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        } else if (dateRangeFilter === 'This Week') {
          const dayOfWeek = now.getDay();
          const diff = now.getDate() - dayOfWeek; // Get Sunday of current week
          startDate = new Date(now.getFullYear(), now.getMonth(), diff, 0, 0, 0);
          endDate = new Date(now.getFullYear(), now.getMonth(), diff + 6, 23, 59, 59);
        } else if (dateRangeFilter === 'This Month') {
          startDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
          endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
        } else if (dateRangeFilter.includes(' to ')) {
          // Custom date range format
          const [start, end] = dateRangeFilter.split(' to ');
          if (start) startDate = new Date(start);
          if (end) endDate = new Date(end);
        }
        
        if (startDate) {
          params.start_date = startDate.toISOString();
        }
        if (endDate) {
          params.end_date = endDate.toISOString();
        }
      }

      const response = await tripsService.getTrips(params);
      
      // Handle response structure - adjust based on your API response format
      let tripsData = [];
      let total = 0;
      
      if (response.data) {
        tripsData = response.data.items || response.data.trips || [];
        total = response.data.total || response.data.count || 0;
      } else {
        tripsData = response.items || response.trips || [];
        total = response.total || response.count || 0;
      }
      
      // Fetch all trip stops to calculate stops count for each trip
      let stopsCountMap = new Map();
      try {
        // Get all unique trip IDs from the fetched trips
        // Use the database ID (id or trip_id), not the display ID (tripId)
        const tripIds = tripsData
          .map(trip => {
            // Prefer 'id' field (database ID), then 'trip_id'
            const dbId = trip.id !== undefined ? trip.id : (trip.trip_id !== undefined ? trip.trip_id : null);
            return dbId;
          })
          .filter(id => id !== null && id !== undefined);
        
        console.log('Admin Trip Management: Fetching stops for trip IDs:', tripIds);
        
        if (tripIds.length > 0) {
          // Fetch all trip stops with pagination (API max limit is 100)
          let allStops = [];
          let offset = 0;
          const limit = 100; // API maximum limit
          let hasMore = true;
          
          // Fetch stops in batches of 100 until we get all stops
          while (hasMore) {
            try {
              const stopsResponse = await tripsService.getAllTripStops({ limit, offset });
              console.log(`Admin Trip Management: Stops response (offset ${offset}):`, stopsResponse);
              
              let batchStops = [];
              
              // Handle different response structures
              if (stopsResponse?.data) {
                if (Array.isArray(stopsResponse.data)) {
                  batchStops = stopsResponse.data;
                } else if (stopsResponse.data.items && Array.isArray(stopsResponse.data.items)) {
                  batchStops = stopsResponse.data.items;
                } else if (stopsResponse.data.stops && Array.isArray(stopsResponse.data.stops)) {
                  batchStops = stopsResponse.data.stops;
                } else if (stopsResponse.data.data && Array.isArray(stopsResponse.data.data)) {
                  batchStops = stopsResponse.data.data;
                }
              } else if (Array.isArray(stopsResponse)) {
                batchStops = stopsResponse;
              } else if (stopsResponse?.items && Array.isArray(stopsResponse.items)) {
                batchStops = stopsResponse.items;
              } else if (stopsResponse?.stops && Array.isArray(stopsResponse.stops)) {
                batchStops = stopsResponse.stops;
              }
              
              if (batchStops.length > 0) {
                allStops = allStops.concat(batchStops);
                // If we got less than the limit, we've reached the end
                hasMore = batchStops.length === limit;
                offset += limit;
              } else {
                hasMore = false;
              }
            } catch (batchError) {
              console.warn('Admin Trip Management: Error fetching stops batch:', batchError);
              hasMore = false;
            }
          }
          
          console.log('Admin Trip Management: Total stops found:', allStops.length);
          console.log('Admin Trip Management: Sample stops:', allStops.slice(0, 3));
          
          // Create a map of trip_id -> stops count
          // Normalize trip IDs to numbers for consistent matching
          allStops.forEach(stop => {
            const tripId = stop.trip_id || stop.tripId;
            if (tripId !== null && tripId !== undefined) {
              // Normalize to number for consistent matching
              const normalizedTripId = Number(tripId);
              if (!isNaN(normalizedTripId)) {
                const currentCount = stopsCountMap.get(normalizedTripId) || 0;
                stopsCountMap.set(normalizedTripId, currentCount + 1);
              }
            }
          });
          
          console.log('Admin Trip Management: Stops count map:', Array.from(stopsCountMap.entries()));
        }
      } catch (stopsError) {
        console.error('Admin Trip Management: Error fetching trip stops for count calculation:', stopsError);
        // Continue without stops count if fetch fails
      }
      
      // Map API response to component format, including stops count from our map
      const mappedTrips = Array.isArray(tripsData) 
        ? tripsData.map(trip => {
            // Use database ID (id or trip_id), not display ID
            const tripId = trip.id !== undefined ? trip.id : (trip.trip_id !== undefined ? trip.trip_id : null);
            // Normalize trip ID to number for consistent matching
            const normalizedTripId = tripId !== null && tripId !== undefined ? Number(tripId) : null;
            const stopsCount = (normalizedTripId && !isNaN(normalizedTripId)) 
              ? (stopsCountMap.get(normalizedTripId) || 0)
              : 0;
            
            console.log(`Admin Trip Management: Trip ID ${tripId} (normalized: ${normalizedTripId}) has ${stopsCount} stops. Map has:`, stopsCountMap.has(normalizedTripId));
            
            // Always set stops_count from our calculation (overrides API value if present)
            trip.stops_count = stopsCount;
            const mappedTrip = mapTripFromAPI(trip);
            console.log(`Admin Trip Management: Mapped trip ${mappedTrip.tripId || mappedTrip.id} has stops: ${mappedTrip.stops}`);
            return mappedTrip;
          })
        : [];
      
      setTrips(mappedTrips);
      setTotalTrips(total);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching trips:', err);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter, dateRangeFilter, driverFilter, vehicleFilter, agencyFilter, currentPage, itemsPerPage, activeTab, mapTripFromAPI, allDriversForFilter, allVehiclesForFilter, allAgenciesForFilter]);

  // Fetch trips and form data on component mount and when filters change
  useEffect(() => {
    fetchTrips();
  }, [fetchTrips]);

  // Fetch form data (agencies, drivers, vehicles) on component mount
  useEffect(() => {
    const fetchFormData = async () => {
      try {
        const response = await tripsService.getTripsFormData();
        
        // Handle response structure - adjust based on your API response format
        if (response.data) {
          setFormDataOptions({
            agencies: response.data.agencies || [],
            drivers: response.data.drivers || [],
            vehicles: response.data.vehicles || []
          });
        } else {
          setFormDataOptions({
            agencies: response.agencies || [],
            drivers: response.drivers || [],
            vehicles: response.vehicles || []
          });
        }
      } catch (err) {
        console.error('Error fetching form data:', err);
      }
    };

    fetchFormData();
  }, []);

  // Fetch all drivers, vehicles, and agencies for filter dropdowns
  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        // Fetch all drivers
        const driversResponse = await driversService.getDrivers({ page: 1, limit: 1000 });
        let driversData = [];
        if (driversResponse?.data && Array.isArray(driversResponse.data.items)) {
          driversData = driversResponse.data.items;
        } else if (Array.isArray(driversResponse?.data)) {
          driversData = driversResponse.data;
        } else if (Array.isArray(driversResponse)) {
          driversData = driversResponse;
        } else {
          driversData = driversResponse?.data?.items || driversResponse?.items || [];
        }
        
        const mappedDrivers = driversData
          .filter(driver => driver.id && (driver.full_name || driver.name || driver.driver_name))
          .map(driver => ({
            id: driver.id || driver.driver_id,
            name: driver.full_name || driver.name || driver.driver_name || ''
          }));
        setAllDriversForFilter(mappedDrivers);

        // Fetch all vehicles
        const vehiclesResponse = await vehiclesService.getVehicles({ page: 1, limit: 1000 });
        let vehiclesData = [];
        if (vehiclesResponse?.data && Array.isArray(vehiclesResponse.data)) {
          vehiclesData = vehiclesResponse.data;
        } else if (Array.isArray(vehiclesResponse)) {
          vehiclesData = vehiclesResponse;
        } else {
          vehiclesData = vehiclesResponse?.data?.items || vehiclesResponse?.items || [];
        }
        
        // Store full vehicle objects (not just id and number) so we can access driver info
        const mappedVehicles = vehiclesData
          .filter(vehicle => vehicle.id && (vehicle.vehicle_number || vehicle.vehicleNumber))
          .map(vehicle => ({
            id: vehicle.id || vehicle.vehicle_id,
            number: vehicle.vehicle_number || vehicle.vehicleNumber || '',
            // Store full vehicle data for driver lookup
            ...vehicle
          }));
        setAllVehiclesForFilter(mappedVehicles);

        // Fetch all agencies
        const agenciesResponse = await getAgencies({ page: 1, limit: 1000 });
        let agenciesData = [];
        if (agenciesResponse?.data && Array.isArray(agenciesResponse.data)) {
          agenciesData = agenciesResponse.data;
        } else if (agenciesResponse?.data && Array.isArray(agenciesResponse.data.items)) {
          agenciesData = agenciesResponse.data.items;
        } else if (Array.isArray(agenciesResponse)) {
          agenciesData = agenciesResponse;
        } else {
          agenciesData = agenciesResponse?.data?.items || agenciesResponse?.items || [];
        }
        
        const mappedAgencies = agenciesData
          .filter(agency => agency.id && (agency.agency_name || agency.name || agency.company_name))
          .map(agency => ({
            id: agency.id || agency.agency_id,
            name: agency.agency_name || agency.name || agency.company_name || ''
          }));
        setAllAgenciesForFilter(mappedAgencies);

        // Fetch all customers - API max limit is 100, so we need to paginate
        let allCustomersData = [];
        let page = 1;
        const limit = 100; // API maximum limit
        let hasMore = true;
        
        while (hasMore) {
          try {
            const customersResponse = await getCustomers({ page, limit });
            console.log(`Customers API response (page ${page}):`, customersResponse);
            
            let customersData = [];
            // Handle multiple possible response structures (same as CustomerManagement)
            if (Array.isArray(customersResponse)) {
              customersData = customersResponse;
            } else if (Array.isArray(customersResponse?.data)) {
              customersData = customersResponse.data;
            } else if (Array.isArray(customersResponse?.data?.items)) {
              customersData = customersResponse.data.items;
            } else if (Array.isArray(customersResponse?.items)) {
              customersData = customersResponse.items;
            } else if (Array.isArray(customersResponse?.data?.data)) {
              customersData = customersResponse.data.data;
            } else if (Array.isArray(customersResponse?.data?.customers)) {
              customersData = customersResponse.data.customers;
            }
            
            if (Array.isArray(customersData) && customersData.length > 0) {
              allCustomersData = allCustomersData.concat(customersData);
            }
            
            // Check if there are more pages
            const total = customersResponse?.pagination?.total || 
                         customersResponse?.meta?.total || 
                         customersResponse?.data?.pagination?.total || 
                         customersResponse?.data?.meta?.total || 0;
            
            hasMore = allCustomersData.length < total && customersData.length === limit;
            page++;
            
            // Safety limit: don't fetch more than 10 pages (1000 customers max)
            if (page > 10) break;
          } catch (pageError) {
            console.error(`Error fetching customers page ${page}:`, pageError);
            hasMore = false;
          }
        }
        
        console.log('All extracted customers data:', allCustomersData);
        
        const mappedCustomers = allCustomersData
          .filter(customer => {
            const hasId = customer.id || customer.customer_id;
            const hasName = customer.customer_name || customer.name || customer.customerName;
            return hasId && hasName;
          })
          .map(customer => ({
            id: customer.id || customer.customer_id,
            name: customer.customer_name || customer.name || customer.customerName || ''
          }))
          .sort((a, b) => a.name.localeCompare(b.name)); // Sort alphabetically by name
        
        console.log('Mapped customers for filter:', mappedCustomers);
        setAllCustomersForFilter(mappedCustomers);
      } catch (err) {
        console.error('Error fetching filter data:', err);
      }
    };

    fetchFilterData();
  }, []);

  // Sync customer search when customer is selected and customers are loaded
  useEffect(() => {
    if (formData.customer && allCustomersForFilter && allCustomersForFilter.length > 0) {
      const selectedCustomer = allCustomersForFilter.find(c => 
        c.id === formData.customer || c.id?.toString() === formData.customer?.toString()
      );
      if (selectedCustomer && customerSearch !== selectedCustomer.name) {
        setCustomerSearch(selectedCustomer.name || '');
      }
    } else if (!formData.customer && customerSearch) {
      setCustomerSearch('');
    }
  }, [formData.customer, allCustomersForFilter]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, driverFilter, vehicleFilter, activeTab]);

  // Auto-hide success modals
  useEffect(() => {
    if (showCancelSuccess) {
      const timer = setTimeout(() => {
        setShowCancelSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showCancelSuccess]);

  // Auto-hide schedule success modal
  useEffect(() => {
    if (showScheduleSuccessModal) {
      const timer = setTimeout(() => {
        setShowScheduleSuccessModal(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showScheduleSuccessModal]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleAddTrip = () => {
    // Generate Trip ID
    const newTripId = 'TRP' + String(Math.floor(Math.random() * 10000)).padStart(4, '0');
    setFormData({
      ...formData,
      tripId: newTripId
    });
    setShowAddModal(true);
  };

  const handleAddSchedule = () => {
    // Generate Schedule ID
    const newScheduleId = 'SCH' + String(Math.floor(Math.random() * 10000)).padStart(4, '0');
    setScheduleFormData({
      ...scheduleFormData,
      scheduleId: newScheduleId
    });
    setShowAddScheduleModal(true);
  };

  const handleCloseScheduleModal = () => {
    setShowAddScheduleModal(false);
    setShowScheduleEditModal(false);
    setEditingSchedule(null);
    setScheduleFormData({
      scheduleId: '',
      scheduleName: '',
      scheduleType: 'daily',
      tripDateTime: '',
      tripStatus: 'Scheduled',
      clientAgency: '',
      notes: '',
      vehicle: '',
      assignedDriver: '',
      routeName: '',
      startLocation: '',
      destination: '',
      stops: [],
      loadDescription: '',
      loadWeight: '',
      weightUnit: 'kg',
      loadVolume: '',
      volumeUnit: 'm³',
      loadType: '',
      totalCapacity: '',
      usedCapacity: '',
      selectedDays: [],
      monthlyDay: [],
      endDate: ''
    });
    setScheduleFormErrors({});
    setScheduleStartLocationSearch('');
    setScheduleDestinationSearch('');
    setScheduleStartLocationSuggestionsOpen(false);
    setScheduleDestinationSuggestionsOpen(false);
  };

  const handleScheduleFormChange = (field, value) => {
    setScheduleFormData({
      ...scheduleFormData,
      [field]: value
    });
    if (scheduleFormErrors[field]) {
      setScheduleFormErrors({
        ...scheduleFormErrors,
        [field]: ''
      });
    }
  };

  const handleScheduleVehicleChange = async (vehicleNumber) => {
    if (!vehicleNumber || vehicleNumber.trim() === '') {
      setScheduleFormData({
        ...scheduleFormData,
        vehicle: '',
        assignedDriver: ''
      });
      return;
    }
    
    // First try to find vehicle from allVehiclesForFilter (now has full vehicle data)
    let selectedVehicle = allVehiclesForFilter.find(v => 
      (v.number || v.vehicle_number || '') === vehicleNumber
    );
    
    // If not found in filter, try formDataOptions.vehicles
    if (!selectedVehicle && formDataOptions.vehicles && Array.isArray(formDataOptions.vehicles)) {
      selectedVehicle = formDataOptions.vehicles.find(v => 
        (v.vehicle_number || v.vehicleNumber || '') === vehicleNumber
      );
    }
    
    // If still not found, fetch from API
    if (!selectedVehicle) {
      try {
        console.log('Admin Trip Schedule: Fetching vehicle details for:', vehicleNumber);
        const vehiclesResponse = await vehiclesService.getVehicles({ limit: 1000 });
        let vehiclesList = [];
        
        if (Array.isArray(vehiclesResponse?.data)) {
          vehiclesList = vehiclesResponse.data;
        } else if (vehiclesResponse?.data?.items) {
          vehiclesList = vehiclesResponse.data.items;
        } else if (Array.isArray(vehiclesResponse)) {
          vehiclesList = vehiclesResponse;
        } else if (vehiclesResponse?.items) {
          vehiclesList = vehiclesResponse.items;
        }
        
        selectedVehicle = vehiclesList.find(v => 
          (v.vehicle_number || v.vehicleNumber || '') === vehicleNumber
        );
      } catch (err) {
        console.error('Admin Trip Schedule: Error fetching vehicle details:', err);
      }
    }
    
    if (!selectedVehicle) {
      // Vehicle not found, clear driver
      setScheduleFormData({
        ...scheduleFormData,
        vehicle: vehicleNumber,
        assignedDriver: ''
      });
      return;
    }

    console.log('Admin Trip Schedule: Selected vehicle:', selectedVehicle);

    // Try to get driver name from vehicle object
    let driverName = '';
    let driverId = null;

    // Check for driver name in various possible fields (in order of preference)
    if (selectedVehicle.driver?.full_name) {
      driverName = selectedVehicle.driver.full_name;
      driverId = selectedVehicle.driver.id || selectedVehicle.driver.driver_id;
      console.log('Admin Trip Schedule: Found driver from nested driver object:', driverName);
    } else if (selectedVehicle.assigned_driver_name) {
      driverName = selectedVehicle.assigned_driver_name;
      driverId = selectedVehicle.assigned_driver_id;
      console.log('Admin Trip Schedule: Found driver from assigned_driver_name:', driverName);
    } else if (selectedVehicle.driver_name) {
      driverName = selectedVehicle.driver_name;
      driverId = selectedVehicle.assigned_driver_id;
      console.log('Admin Trip Schedule: Found driver from driver_name:', driverName);
    } else if (selectedVehicle.assignedDriver) {
      driverName = selectedVehicle.assignedDriver;
      driverId = selectedVehicle.assigned_driver_id || selectedVehicle.assignedDriverId;
      console.log('Admin Trip Schedule: Found driver from assignedDriver:', driverName);
    } else if (selectedVehicle.assigned_driver_id) {
      // Only have driver ID, need to fetch driver details
      driverId = selectedVehicle.assigned_driver_id;
      console.log('Admin Trip Schedule: Fetching driver details for driver_id:', driverId);
      
      // First try to find in allDriversForFilter
      const driverFromFilter = allDriversForFilter.find(d => 
        (d.id || d.driver_id) === driverId
      );
      
      if (driverFromFilter) {
        driverName = driverFromFilter.name || driverFromFilter.full_name || '';
        console.log('Admin Trip Schedule: Found driver in allDriversForFilter:', driverName);
      } else {
        // If not in filter, fetch from API
        try {
          const driverResponse = await driversService.getDrivers({ limit: 1000 });
          
          // Find the driver in the response
          let driversList = [];
          if (Array.isArray(driverResponse.data)) {
            driversList = driverResponse.data;
          } else if (driverResponse.data?.items) {
            driversList = driverResponse.data.items;
          } else if (Array.isArray(driverResponse)) {
            driversList = driverResponse;
          } else if (driverResponse.items) {
            driversList = driverResponse.items;
          }
          
          const driver = driversList.find(d => 
            d.id === driverId || 
            d.driver_id === driverId ||
            d.assigned_driver_id === driverId
          );
          
          if (driver) {
            driverName = driver.full_name || driver.driver_name || driver.name || '';
            console.log('Admin Trip Schedule: Fetched driver name from API:', driverName);
          } else {
            console.warn('Admin Trip Schedule: Driver not found for driver_id:', driverId);
          }
        } catch (err) {
          console.error('Admin Trip Schedule: Error fetching driver details:', err);
          // Continue without driver name if fetch fails
        }
      }
    } else {
      console.log('Admin Trip Schedule: No driver assigned to this vehicle');
    }

    // Update form data with vehicle and driver info
    console.log('Admin Trip Schedule: Setting assigned driver to:', driverName);
    setScheduleFormData(prev => ({
      ...prev,
      vehicle: vehicleNumber,
      assignedDriver: driverName
    }));
    
    if (scheduleFormErrors.vehicle) {
      setScheduleFormErrors({...scheduleFormErrors, vehicle: ''});
    }
    if (scheduleFormErrors.assignedDriver) {
      setScheduleFormErrors({...scheduleFormErrors, assignedDriver: ''});
    }
  };

  const handleSelectedDayToggle = (day) => {
    const updatedDays = scheduleFormData.selectedDays.includes(day)
      ? scheduleFormData.selectedDays.filter(d => d !== day)
      : [...scheduleFormData.selectedDays, day];
    handleScheduleFormChange('selectedDays', updatedDays);
  };

  const handleMonthlyDateToggle = (date) => {
    const dateNum = parseInt(date);
    const updatedDates = scheduleFormData.monthlyDay.includes(dateNum)
      ? scheduleFormData.monthlyDay.filter(d => d !== dateNum)
      : [...scheduleFormData.monthlyDay, dateNum].sort((a, b) => a - b);
    handleScheduleFormChange('monthlyDay', updatedDates);
  };

  // Calendar functions
  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const getCalendarDays = () => {
    const daysInMonth = getDaysInMonth(calendarMonth, calendarYear);
    const firstDay = getFirstDayOfMonth(calendarMonth, calendarYear);
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  const handlePrevMonth = () => {
    if (calendarMonth === 0) {
      setCalendarMonth(11);
      setCalendarYear(calendarYear - 1);
    } else {
      setCalendarMonth(calendarMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (calendarMonth === 11) {
      setCalendarMonth(0);
      setCalendarYear(calendarYear + 1);
    } else {
      setCalendarMonth(calendarMonth + 1);
    }
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
    'July', 'August', 'September', 'October', 'November', 'December'];

  // Handle schedule start location selection from Google Places
  const handleScheduleStartLocationSelect = (address, place) => {
    setScheduleStartLocationSearch(address);
    handleScheduleFormChange('startLocation', address);
    setScheduleStartLocationSuggestionsOpen(false);
  };

  // Handle schedule destination selection from Google Places
  const handleScheduleDestinationSelect = (address, place) => {
    setScheduleDestinationSearch(address);
    handleScheduleFormChange('destination', address);
    setScheduleDestinationSuggestionsOpen(false);
  };

  // Google Places Autocomplete for schedule start location
  const scheduleStartLocationAutocomplete = useGooglePlacesAutocomplete({
    value: scheduleFormData.startLocation,
    onPlaceSelect: handleScheduleStartLocationSelect,
    restrictions: { country: 'in' },
    types: ['geocode', 'establishment']
  });

  // Google Places Autocomplete for schedule destination
  const scheduleDestinationAutocomplete = useGooglePlacesAutocomplete({
    value: scheduleFormData.destination,
    onPlaceSelect: handleScheduleDestinationSelect,
    restrictions: { country: 'in' },
    types: ['geocode', 'establishment']
  });

  // Check if Google Maps is loaded for schedule form (for showing/hiding fallback city dropdown)
  const isScheduleGoogleMapsLoaded = scheduleStartLocationAutocomplete.isGoogleLoaded || (window.google && window.google.maps && window.google.maps.places);

  // Handle schedule start location input
  const handleScheduleStartLocationInput = (value) => {
    setScheduleStartLocationSearch(value);
    handleScheduleFormChange('startLocation', value);
    // Show suggestions if there's text and matches exist (only for fallback when Google Maps is not loaded)
    if (!isScheduleGoogleMapsLoaded && value.trim().length > 0) {
      const searchLower = value.toLowerCase();
      const hasMatches = indianCities.some(city => city.toLowerCase().includes(searchLower));
      setScheduleStartLocationSuggestionsOpen(hasMatches);
    } else {
      setScheduleStartLocationSuggestionsOpen(false);
    }
  };

  // Handle schedule destination input
  const handleScheduleDestinationInput = (value) => {
    setScheduleDestinationSearch(value);
    handleScheduleFormChange('destination', value);
    // Show suggestions if there's text and matches exist (only for fallback when Google Maps is not loaded)
    if (!isScheduleGoogleMapsLoaded && value.trim().length > 0) {
      const searchLower = value.toLowerCase();
      const hasMatches = indianCities.some(city => city.toLowerCase().includes(searchLower));
      setScheduleDestinationSuggestionsOpen(hasMatches);
    } else {
      setScheduleDestinationSuggestionsOpen(false);
    }
  };

  // Handle schedule start location suggestion select (fallback for city list)
  const handleScheduleStartLocationCitySelect = (city) => {
    handleScheduleFormChange('startLocation', city);
    setScheduleStartLocationSearch(city);
    setScheduleStartLocationSuggestionsOpen(false);
  };

  // Handle schedule destination suggestion select (fallback for city list)
  const handleScheduleDestinationCitySelect = (city) => {
    handleScheduleFormChange('destination', city);
    setScheduleDestinationSearch(city);
    setScheduleDestinationSuggestionsOpen(false);
  };


  // Initialize autocomplete for schedule stop locations when modal opens
  useEffect(() => {
    if ((showAddScheduleModal || showScheduleEditModal) && isScheduleGoogleMapsLoaded) {
      const timer = setTimeout(() => {
        scheduleFormData.stops.forEach((stop, index) => {
          const inputRef = scheduleStopLocationInputRefs.current[index];
          if (inputRef && inputRef.current) {
            initializeScheduleStopAutocomplete(index, inputRef.current);
          }
        });
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [showAddScheduleModal, showScheduleEditModal, isScheduleGoogleMapsLoaded, scheduleFormData.stops.length]);

  // Close calendar popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCalendarPopup && 
          calendarInputRef.current && 
          calendarPopupRef.current &&
          !calendarInputRef.current.contains(event.target) &&
          !calendarPopupRef.current.contains(event.target)) {
        setShowCalendarPopup(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendarPopup]);

  const handleSaveSchedule = () => {
    // Basic validation
    const errors = {};
    if (!scheduleFormData.scheduleName.trim()) {
      errors.scheduleName = 'Schedule name is required';
    }
    if (!scheduleFormData.tripDateTime) {
      errors.tripDateTime = 'Trip time is required';
    }
    if (scheduleFormData.scheduleType === 'weekly' && scheduleFormData.selectedDays.length === 0) {
      errors.selectedDays = 'Please select at least one day';
    }
    if (scheduleFormData.scheduleType === 'monthly') {
      if (!scheduleFormData.monthlyDay || scheduleFormData.monthlyDay.length === 0) {
        errors.monthlyDay = 'Please select at least one date for monthly schedule';
      }
    }

    if (Object.keys(errors).length > 0) {
      setScheduleFormErrors(errors);
      return;
    }

    // Show confirmation modal
    setShowScheduleConfirmModal(true);
  };

  // Map schedule from API to component format
  const mapScheduleFromAPI = useCallback((apiSchedule) => {
    const agency = agencyLookup.get(apiSchedule.agency_id) || apiSchedule.agency?.agency_name || apiSchedule.agency?.name || `Agency #${apiSchedule.agency_id}`;
    const vehicleNumber = vehicleLookup.get(apiSchedule.vehicle_id) || apiSchedule.vehicle?.vehicle_number || apiSchedule.vehicle?.vehicleNumber || `Vehicle #${apiSchedule.vehicle_id}`;
    const driverName = driverLookup.get(apiSchedule.assigned_driver_id) || apiSchedule.driver?.full_name || apiSchedule.driver?.name || `Driver #${apiSchedule.assigned_driver_id}`;
    
    // Parse trip_time to display format
    let tripTimeDisplay = '';
    if (apiSchedule.trip_time) {
      if (typeof apiSchedule.trip_time === 'string') {
        // If it's already a string like "08:00:00", convert to "08:00 AM"
        const [hours, minutes] = apiSchedule.trip_time.split(':');
        const hour = parseInt(hours, 10);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour % 12 || 12;
        tripTimeDisplay = `${String(displayHour).padStart(2, '0')}:${minutes || '00'} ${ampm}`;
      }
    }
    
    return {
      id: apiSchedule.id,
      scheduleId: apiSchedule.schedule_id || `SCH${String(apiSchedule.id).padStart(6, '0')}`,
      scheduleName: apiSchedule.schedule_name || '',
      scheduleType: apiSchedule.schedule_type || 'daily',
      tripDateTime: tripTimeDisplay || apiSchedule.trip_time || '',
      tripStatus: apiSchedule.trip_status || 'Scheduled',
      clientAgency: agency,
      agencyId: apiSchedule.agency_id,
      vehicle: vehicleNumber,
      vehicleId: apiSchedule.vehicle_id,
      assignedDriver: driverName,
      driverId: apiSchedule.assigned_driver_id,
      routeName: apiSchedule.route_name || '',
      routeId: apiSchedule.route_id,
      startLocation: apiSchedule.start_location || '',
      destination: apiSchedule.destination || '',
      selectedDays: apiSchedule.selected_days || [],
      monthlyDay: apiSchedule.monthly_days || [],
      endDate: apiSchedule.end_date || '',
      loadDescription: apiSchedule.load_description || '',
      loadWeight: apiSchedule.load_weight || '',
      weightUnit: apiSchedule.weight_unit || 'kg',
      loadVolume: apiSchedule.load_volume || '',
      volumeUnit: apiSchedule.volume_unit || 'm³',
      loadType: apiSchedule.load_type || '',
      totalCapacity: apiSchedule.total_capacity || '',
      usedCapacity: apiSchedule.used_capacity || '',
      notes: apiSchedule.notes || '',
      stops: apiSchedule.stops || [],
      stops_count: Array.isArray(apiSchedule.stops) ? apiSchedule.stops.length : 0,
      stopsData: apiSchedule.stops || [], // Add stopsData for consistency with trips
      _original: apiSchedule
    };
  }, [agencyLookup, vehicleLookup, driverLookup]);

  // Fetch schedules from API
  const fetchSchedules = useCallback(async () => {
    if (activeSubTab !== 'trip-schedule') return; // Only fetch when schedule tab is active
    
    setLoadingSchedules(true);
    setError(null);
    try {
      const params = {
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage
      };

      // Add filter_type based on activeTab (same as trips page)
      if (activeTab === "all trips") {
        params.filter_type = 'all';
      } else if (activeTab === "today's trips") {
        params.filter_type = 'today';
      } else if (activeTab === 'delayed trips') {
        params.filter_type = 'delayed';
      } else if (activeTab === 'upcoming departures') {
        params.filter_type = 'upcoming';
      } else if (activeTab === 'recent completions') {
        params.filter_type = 'recent_completions';
      }

      // Search filter
      if (searchQuery) {
        params.search = searchQuery;
      }

      // Trip Status filter - use exact status value
      if (statusFilter) {
        params.trip_status = statusFilter;
      }

      // Driver filter - convert to ID if needed
      if (driverFilter) {
        if (typeof driverFilter === 'number' || !isNaN(Number(driverFilter))) {
          params.assigned_driver_id = Number(driverFilter);
        } else {
          // Try to find driver ID from allDriversForFilter
          const driver = allDriversForFilter.find(d => 
            (d.name || d.full_name || '') === driverFilter ||
            (d.id && d.id.toString() === driverFilter.toString())
          );
          if (driver) {
            params.assigned_driver_id = driver.id;
          }
        }
      }

      // Vehicle filter - convert to ID if needed
      if (vehicleFilter) {
        if (typeof vehicleFilter === 'number' || !isNaN(Number(vehicleFilter))) {
          params.vehicle_id = Number(vehicleFilter);
        } else {
          // Try to find vehicle ID from allVehiclesForFilter
          const vehicle = allVehiclesForFilter.find(v => 
            (v.number || v.vehicle_number || '') === vehicleFilter ||
            (v.id && v.id.toString() === vehicleFilter.toString())
          );
          if (vehicle) {
            params.vehicle_id = vehicle.id;
          }
        }
      }

      // Agency filter - convert to ID if needed
      if (agencyFilter) {
        if (typeof agencyFilter === 'number' || !isNaN(Number(agencyFilter))) {
          params.agency_id = Number(agencyFilter);
        } else {
          // Try to find agency ID from allAgenciesForFilter
          const agency = allAgenciesForFilter.find(a => 
            (a.name || a.agency_name || '') === agencyFilter ||
            (a.id && a.id.toString() === agencyFilter.toString())
          );
          if (agency) {
            params.agency_id = agency.id;
          }
        }
      }
      
      // Handle date range filter - convert text to ISO dates
      if (dateRangeFilter) {
        const now = new Date();
        let startDate, endDate;
        
        if (dateRangeFilter === 'Today') {
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
          endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        } else if (dateRangeFilter === 'This Week') {
          const dayOfWeek = now.getDay();
          const diff = now.getDate() - dayOfWeek; // Get Sunday of current week
          startDate = new Date(now.getFullYear(), now.getMonth(), diff, 0, 0, 0);
          endDate = new Date(now.getFullYear(), now.getMonth(), diff + 6, 23, 59, 59);
        } else if (dateRangeFilter === 'This Month') {
          startDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
          endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
        } else if (dateRangeFilter.includes(' to ')) {
          // Custom date range format
          const [start, end] = dateRangeFilter.split(' to ');
          if (start) startDate = new Date(start);
          if (end) endDate = new Date(end);
        }
        
        if (startDate) {
          params.start_date = startDate.toISOString();
        }
        if (endDate) {
          params.end_date = endDate.toISOString();
        }
      }

      console.log('fetchSchedules: Calling API with params:', params);
      const response = await tripsService.getTripSchedules(params);
      console.log('fetchSchedules: API response:', response);
      
      let schedulesData = [];
      let total = 0;
      
      if (response.data && Array.isArray(response.data)) {
        schedulesData = response.data;
        total = response.total || response.count || schedulesData.length;
      } else if (Array.isArray(response)) {
        schedulesData = response;
        total = schedulesData.length;
      } else {
        schedulesData = response.data?.items || response.items || [];
        total = response.data?.total || response.total || response.count || 0;
      }
      
      // Fetch stops for each schedule
      const schedulesWithStops = await Promise.all(
        schedulesData.map(async (schedule) => {
          try {
            const scheduleId = schedule.id || schedule.schedule_id;
            if (scheduleId) {
              const stopsResponse = await tripsService.getStopsByScheduleId(scheduleId);
              console.log(`Fetched stops for schedule ${scheduleId}:`, stopsResponse);
              
              // Handle different response formats
              let stops = [];
              if (Array.isArray(stopsResponse)) {
                stops = stopsResponse;
              } else if (stopsResponse && Array.isArray(stopsResponse.data)) {
                stops = stopsResponse.data;
              } else if (stopsResponse && Array.isArray(stopsResponse.items)) {
                stops = stopsResponse.items;
              } else if (stopsResponse && stopsResponse.data && Array.isArray(stopsResponse.data.data)) {
                stops = stopsResponse.data.data;
              }
              
              console.log(`Parsed stops for schedule ${scheduleId}:`, stops);
              return { ...schedule, stops };
            }
            return { ...schedule, stops: [] };
          } catch (err) {
            console.error(`Error fetching stops for schedule ${schedule.id}:`, err);
            return { ...schedule, stops: [] };
          }
        })
      );
      
      const mappedSchedules = Array.isArray(schedulesWithStops) 
        ? schedulesWithStops.map(schedule => {
            const mapped = mapScheduleFromAPI(schedule);
            // Ensure stops from fetchSchedules are preserved (they were attached before mapping)
            if (schedule.stops && Array.isArray(schedule.stops) && schedule.stops.length > 0) {
              mapped.stops = schedule.stops;
              mapped.stopsData = schedule.stops;
              mapped.stops_count = schedule.stops.length;
            }
            // Ensure stops are properly attached and counted
            console.log(`Schedule ${mapped.scheduleId} (id: ${mapped.id}) has stops:`, {
              originalStops: schedule.stops,
              mappedStops: mapped.stops,
              stopsCount: mapped.stops_count
            });
            return mapped;
          })
        : [];
      
      console.log('All mapped schedules with stops:', mappedSchedules.map(s => ({
        id: s.id,
        scheduleId: s.scheduleId,
        name: s.scheduleName,
        stopsCount: s.stops_count,
        stopsArrayLength: s.stops?.length || 0,
        stopsDataLength: s.stopsData?.length || 0
      })));
      
      setTripSchedules(mappedSchedules);
      setTotalSchedules(total);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching schedules:', err);
    } finally {
      setLoadingSchedules(false);
    }
  }, [activeSubTab, activeTab, searchQuery, statusFilter, dateRangeFilter, driverFilter, vehicleFilter, agencyFilter, currentPage, itemsPerPage, mapScheduleFromAPI, allDriversForFilter, allVehiclesForFilter, allAgenciesForFilter]);

  // Fetch schedules when filters change
  useEffect(() => {
    if (activeSubTab === 'trip-schedule') {
      fetchSchedules();
    }
  }, [activeSubTab, activeTab, searchQuery, statusFilter, dateRangeFilter, driverFilter, vehicleFilter, agencyFilter, currentPage, itemsPerPage, fetchSchedules]);

  const handleConfirmScheduleSave = async () => {
    const wasEdit = !!editingSchedule;
    
    try {
      // Find agency ID from agency name
      let agencyId = null;
      if (scheduleFormData.clientAgency) {
        const agency = allAgenciesForFilter.find(a => 
          (a.name || a.agency_name || '').toString() === scheduleFormData.clientAgency.toString() ||
          (a.id && a.id.toString() === scheduleFormData.clientAgency.toString())
        );
        if (agency) {
          agencyId = agency.id;
        } else if (!isNaN(Number(scheduleFormData.clientAgency))) {
          agencyId = Number(scheduleFormData.clientAgency);
        }
      }

      // Find vehicle ID from vehicle number
      let vehicleId = null;
      if (scheduleFormData.vehicle) {
        const vehicle = allVehiclesForFilter.find(v => 
          (v.number || v.vehicle_number || '').toString() === scheduleFormData.vehicle.toString() ||
          (v.id && v.id.toString() === scheduleFormData.vehicle.toString())
        );
        if (vehicle) {
          vehicleId = vehicle.id;
        } else if (!isNaN(Number(scheduleFormData.vehicle))) {
          vehicleId = Number(scheduleFormData.vehicle);
        }
      }

      // Find driver ID from driver name
      let driverId = null;
      if (scheduleFormData.assignedDriver) {
        const driver = allDriversForFilter.find(d => 
          (d.name || d.full_name || '').toString() === scheduleFormData.assignedDriver.toString() ||
          (d.id && d.id.toString() === scheduleFormData.assignedDriver.toString())
        );
        if (driver) {
          driverId = driver.id;
        } else if (!isNaN(Number(scheduleFormData.assignedDriver))) {
          driverId = Number(scheduleFormData.assignedDriver);
        }
      }

      // Convert trip time to TIME format (HH:MM:SS)
      let tripTime = '';
      if (scheduleFormData.tripDateTime) {
        // If it's in format like "08:00 AM", convert to "08:00:00"
        const timeMatch = scheduleFormData.tripDateTime.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
        if (timeMatch) {
          let hours = parseInt(timeMatch[1], 10);
          const minutes = timeMatch[2];
          const ampm = timeMatch[3].toUpperCase();
          if (ampm === 'PM' && hours !== 12) hours += 12;
          if (ampm === 'AM' && hours === 12) hours = 0;
          tripTime = `${String(hours).padStart(2, '0')}:${minutes}:00`;
        } else {
          // If already in HH:MM:SS or HH:MM format
          tripTime = scheduleFormData.tripDateTime;
        }
      }

      const schedulePayload = {
        schedule_name: scheduleFormData.scheduleName,
        schedule_type: scheduleFormData.scheduleType,
        trip_time: tripTime,
        trip_status: scheduleFormData.tripStatus || 'Scheduled',
        agency_id: agencyId,
        vehicle_id: vehicleId,
        assigned_driver_id: driverId,
        route_name: scheduleFormData.routeName,
        start_location: scheduleFormData.startLocation,
        destination: scheduleFormData.destination,
        selected_days: scheduleFormData.scheduleType === 'weekly' ? scheduleFormData.selectedDays : null,
        monthly_days: scheduleFormData.scheduleType === 'monthly' ? scheduleFormData.monthlyDay : null,
        end_date: scheduleFormData.endDate || null,
        load_description: scheduleFormData.loadDescription || null,
        load_weight: scheduleFormData.loadWeight ? parseFloat(scheduleFormData.loadWeight) : null,
        weight_unit: scheduleFormData.weightUnit || 'kg',
        load_volume: scheduleFormData.loadVolume ? parseFloat(scheduleFormData.loadVolume) : null,
        volume_unit: scheduleFormData.volumeUnit || 'm³',
        load_type: scheduleFormData.loadType || null,
        total_capacity: scheduleFormData.totalCapacity ? parseFloat(scheduleFormData.totalCapacity) : null,
        used_capacity: scheduleFormData.usedCapacity ? parseFloat(scheduleFormData.usedCapacity) : null,
        notes: scheduleFormData.notes || null
      };

    let scheduleId;
    if (editingSchedule) {
      // Update existing schedule
      const updateResponse = await tripsService.updateTripSchedule(editingSchedule.id || editingSchedule._original?.id, schedulePayload);
      scheduleId = editingSchedule.id || editingSchedule._original?.id || updateResponse?.data?.id || updateResponse?.id;
    } else {
        // Create new schedule
      const createResponse = await tripsService.createTripSchedule(schedulePayload);
      const createdSchedule = createResponse.data || createResponse;
      scheduleId = createdSchedule.id || createdSchedule.schedule_id || createdSchedule.data?.id;
    }

    // Save schedule stops if any stops are provided
    if (scheduleId && scheduleFormData.stops && scheduleFormData.stops.length > 0) {
      try {
        // For updates, first get existing stops to determine what to create/update/delete
        let existingStops = [];
        if (editingSchedule) {
          try {
            // Try to fetch existing stops - you may need to add this API endpoint
            // For now, we'll just create/update all stops
            const scheduleResponse = await tripsService.getTripScheduleById(scheduleId);
            existingStops = scheduleResponse?.data?.stops || scheduleResponse?.stops || [];
          } catch (err) {
            console.log('Could not fetch existing schedule stops:', err);
            existingStops = [];
          }
        }

        // Filter stops with valid locations
        const validStops = scheduleFormData.stops.filter(stop => stop.location && stop.location.trim());
        
        // Create/update stops
        const stopPromises = validStops.map((stop, index) => {
          // Convert estimated_date to ISO format if provided
          let estimatedDate = null;
          if (stop.estimatedDate) {
            const date = new Date(stop.estimatedDate);
            if (!isNaN(date.getTime())) {
              estimatedDate = date.toISOString();
            }
          }

          const stopData = {
            schedule_id: scheduleId,
            stop_sequence: index + 1,
            stop_location: stop.location || '',
            estimated_date: estimatedDate,
            purpose: stop.purpose || null
          };

          // Check if this stop already exists (has an ID from edit)
          if (stop.id && editingSchedule) {
            // Update existing stop using schedule stops API
            return tripsService.updateTripScheduleStop(stop.id, stopData);
          } else {
            // Create new stop using schedule stops API
            return tripsService.createTripScheduleStop(stopData);
          }
        });

        await Promise.all(stopPromises);
        console.log('Successfully saved schedule stops');
      } catch (stopError) {
        console.error('Error saving schedule stops:', stopError);
        // Don't fail the whole operation if stops fail, but log it
        alert('Schedule saved successfully, but some stops could not be saved. Please edit the schedule to add stops.');
      }
    }

      // Refresh schedules list
      await fetchSchedules();

    setSavedScheduleInfo({
      scheduleId: scheduleFormData.scheduleId,
      scheduleName: scheduleFormData.scheduleName,
      wasEdit: wasEdit
    });
    setShowScheduleConfirmModal(false);
    handleCloseScheduleModal();
    setShowScheduleSuccessModal(true);
    } catch (err) {
      console.error('Error saving schedule:', err);
      alert('Failed to save schedule: ' + (err.message || 'Unknown error'));
    }
  };

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const handleCloseModal = () => {
    setShowAddModal(false);
    setCustomerSearch('');
    setCustomerDropdownOpen(false);
    setShowEditModal(false);
    setEditingTrip(null);
    setFormData({
      tripId: '',
      tripDateTime: '',
      tripStatus: 'Scheduled',
      clientAgency: '',
      notes: '',
      vehicle: '',
      assignedDriver: '',
      routeName: '',
      startLocation: '',
      destination: '',
      stops: [],
      loadDescription: '',
      loadWeight: '',
      weightUnit: 'kg',
      loadVolume: '',
      volumeUnit: 'm³',
      loadType: '',
      totalCapacity: '',
      usedCapacity: '',
      remainingCapacity: ''
    });
    setFormErrors({});
    setStartLocationSearch('');
    setDestinationSearch('');
    setStartLocationSuggestionsOpen(false);
    setCustomerSearch('');
    setCustomerDropdownOpen(false);
    setDestinationSuggestionsOpen(false);
  };

  // Calculate trip progress percentage based on real data
  const calculateTripProgress = (trip) => {
    if (!trip) return 0;

    // Based on status
    if (trip.status === 'Cancelled') return 0;
    if (trip.status === 'Completed') return 100;
    if (trip.status === 'Scheduled') return 0;
    
    // For "In Progress" or "Delayed", calculate based on available data
    if (trip.status === 'In Progress' || trip.status === 'Delayed') {
      let progress = 0;
      let factors = 0;

      // Factor 1: Distance progress (if both estimated and actual distance available)
      if (trip.estimated_distance && trip.actual_distance) {
        const estDist = parseFloat(trip.estimated_distance);
        const actDist = parseFloat(trip.actual_distance);
        if (estDist > 0) {
          progress += Math.min(100, (actDist / estDist) * 100);
          factors++;
        }
      }

      // Factor 2: Time progress (if trip date and current time available)
      if (trip.trip_date_time) {
        const tripDate = new Date(trip.trip_date_time);
        const now = new Date();
        if (!isNaN(tripDate.getTime()) && tripDate <= now) {
          // If estimated duration is available, use it
          if (trip.estimated_duration) {
            // Try to parse duration (e.g., "4h 30m" or "4.5 hours")
            const durationMatch = trip.estimated_duration.match(/(\d+(?:\.\d+)?)\s*(?:h|hour|hours)/i);
            if (durationMatch) {
              const hours = parseFloat(durationMatch[1]);
              const estimatedEnd = new Date(tripDate.getTime() + hours * 60 * 60 * 1000);
              if (estimatedEnd > tripDate) {
                const elapsed = now.getTime() - tripDate.getTime();
                const total = estimatedEnd.getTime() - tripDate.getTime();
                progress += Math.min(100, (elapsed / total) * 100);
                factors++;
              }
            }
          } else {
            // If no estimated duration, assume 50% if trip has started
            progress += 50;
            factors++;
          }
        }
      }

      // Factor 3: Stops progress (if stops data available)
      if (trip.stopsData && Array.isArray(trip.stopsData) && trip.stopsData.length > 0) {
        const totalStops = trip.stopsData.length;
        // Count completed stops (stops with actual_date or past estimated_date)
        const now = new Date();
        const completedStops = trip.stopsData.filter(stop => {
          if (stop.actual_date) return true;
          if (stop.estimated_date) {
            const estDate = new Date(stop.estimated_date);
            return !isNaN(estDate.getTime()) && estDate <= now;
          }
          return false;
        }).length;
        
        if (totalStops > 0) {
          progress += (completedStops / totalStops) * 100;
          factors++;
        }
      }

      // Average the factors, or default to 50% if no factors available
      if (factors > 0) {
        return Math.min(95, Math.round(progress / factors)); // Cap at 95% until completed
      } else {
        return 50; // Default progress for "In Progress" trips
      }
    }

    return 0;
  };

  const handleViewClick = async (trip) => {
    try {
      const response = await tripsService.getTripById(trip.id || trip.tripId || trip._original?.id, true);
      // Handle response structure
      const apiTrip = response.data || response;
      // Map API response to component format using the same mapping function
      const mappedTrip = mapTripFromAPI(apiTrip);
      
      // Format dates for timeline
      const formatDateForTimeline = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return null;
        return date.toLocaleString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
      };

      // Add additional fields for view modal
      const viewTripData = {
        ...mappedTrip,
        // Vehicle details
        vehicleType: apiTrip.vehicle?.vehicle_type || apiTrip.vehicle?.type || 'N/A',
        vehicleModel: apiTrip.vehicle?.model || apiTrip.vehicle?.vehicle_model || 'N/A',
        // Driver details
        driverContact: apiTrip.driver?.mobile_number || apiTrip.driver?.contact || apiTrip.driver?.phone || 'N/A',
        driverLicense: apiTrip.driver?.license_number || apiTrip.driver?.license || 'N/A',
        driverTotalTrips: apiTrip.driver?.total_trips || apiTrip.driver?.trips_count || 'N/A',
        // Route details
        routeName: apiTrip.route_name || mappedTrip.route || 'N/A',
        startLocation: apiTrip.start_location || mappedTrip.startLocation || 'N/A',
        destination: apiTrip.destination || mappedTrip.destination || 'N/A',
        estimatedDistance: apiTrip.estimated_distance || apiTrip.actual_distance || mappedTrip.estimatedDistance || 'N/A',
        // Load details
        loadType: apiTrip.load_type || 'N/A',
        totalCapacity: apiTrip.total_capacity || 'N/A',
        usedCapacity: apiTrip.used_capacity || 'N/A',
        remainingCapacity: apiTrip.remaining_capacity || 'N/A',
        // Client/Agency
        clientAgency: apiTrip.client_agency || mappedTrip.agency || 'N/A',
        // Timeline fields
        created_at: apiTrip.created_at,
        updated_at: apiTrip.updated_at,
        actual_duration: apiTrip.actual_duration,
        actual_distance: apiTrip.actual_distance,
        trip_date_time: apiTrip.trip_date_time,
        estimated_duration: apiTrip.estimated_duration,
        created_at_formatted: formatDateForTimeline(apiTrip.created_at),
        updated_at_formatted: formatDateForTimeline(apiTrip.updated_at),
        // Stops data
        stopsData: apiTrip.stops || mappedTrip.stopsData || [],
        // Calculate progress
        progress: calculateTripProgress({
          status: apiTrip.trip_status || apiTrip.status,
          estimated_distance: apiTrip.estimated_distance,
          actual_distance: apiTrip.actual_distance,
          trip_date_time: apiTrip.trip_date_time,
          estimated_duration: apiTrip.estimated_duration,
          stopsData: apiTrip.stops || []
        })
      };
      
      setViewingTrip(viewTripData);
      setShowViewModal(true);
    } catch (err) {
      console.error('Error fetching trip details:', err);
      // Fallback to using the trip data we already have (mapped)
      const mappedTrip = mapTripFromAPI(trip._original || trip);
      setViewingTrip(mappedTrip);
      setShowViewModal(true);
    }
  };

  const handleScheduleViewClick = async (schedule) => {
    try {
      const scheduleId = schedule.id || schedule._original?.id;
      if (scheduleId) {
        const response = await tripsService.getTripScheduleById(scheduleId);
        const scheduleData = response.data || response;
        const mappedSchedule = mapScheduleFromAPI(scheduleData);
        setViewingSchedule(mappedSchedule);
        setShowScheduleViewModal(true);
      } else {
        // Fallback to using the schedule data we already have
    setViewingSchedule(schedule);
    setShowScheduleViewModal(true);
      }
    } catch (err) {
      console.error('Error fetching schedule details:', err);
      // Fallback to using the schedule data we already have
      setViewingSchedule(schedule);
      setShowScheduleViewModal(true);
    }
  };

  const handleScheduleEditClick = async (schedule) => {
    try {
      const scheduleId = schedule.id || schedule._original?.id;
      if (scheduleId) {
        const response = await tripsService.getTripScheduleById(scheduleId);
        const scheduleData = response.data || response;
        
        // Fetch stops for this schedule
        let stopsArray = [];
        try {
          const stopsResponse = await tripsService.getStopsByScheduleId(scheduleId);
          console.log('Edit: Fetched stops response for schedule', scheduleId, ':', stopsResponse);
          
          // Handle different response formats
          let stops = [];
          if (Array.isArray(stopsResponse)) {
            stops = stopsResponse;
          } else if (stopsResponse && Array.isArray(stopsResponse.data)) {
            stops = stopsResponse.data;
          } else if (stopsResponse && Array.isArray(stopsResponse.items)) {
            stops = stopsResponse.items;
          } else if (stopsResponse && stopsResponse.data && Array.isArray(stopsResponse.data.data)) {
            stops = stopsResponse.data.data;
          }
          
          console.log('Edit: Parsed stops for schedule', scheduleId, ':', stops);
          
          stopsArray = stops.map(stop => {
            // Format estimated_date for datetime-local input
            let estimatedDate = '';
            if (stop.estimated_date) {
              const date = new Date(stop.estimated_date);
              if (!isNaN(date.getTime())) {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');
                estimatedDate = `${year}-${month}-${day}T${hours}:${minutes}`;
              }
            }
            
            return {
              id: stop.id, // Keep stop ID for updates
              location: stop.stop_location || stop.location || '',
              purpose: stop.purpose || '',
              estimatedDate: estimatedDate
            };
          });
        } catch (stopsErr) {
          console.log('Could not fetch stops for schedule:', stopsErr);
        }
        
        const mappedSchedule = mapScheduleFromAPI(scheduleData);
        setEditingSchedule(mappedSchedule);
        
        // Format trip_time for time input (HH:MM format)
        let tripTimeFormatted = '';
        if (scheduleData.trip_time) {
          if (typeof scheduleData.trip_time === 'string') {
            // If it's in format "08:00:00" or "08:00", extract HH:MM
            const timeMatch = scheduleData.trip_time.match(/(\d{1,2}):(\d{2})/);
            if (timeMatch) {
              tripTimeFormatted = `${timeMatch[1].padStart(2, '0')}:${timeMatch[2]}`;
            } else {
              tripTimeFormatted = scheduleData.trip_time;
            }
          }
        }
        
        // Format end_date for date input (YYYY-MM-DD format)
        let endDateFormatted = '';
        if (scheduleData.end_date) {
          const date = new Date(scheduleData.end_date);
          if (!isNaN(date.getTime())) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            endDateFormatted = `${year}-${month}-${day}`;
          }
        }
        
    // Populate form with schedule data
        setScheduleFormData({
          scheduleId: mappedSchedule.scheduleId,
          scheduleName: mappedSchedule.scheduleName,
          scheduleType: mappedSchedule.scheduleType,
          tripDateTime: tripTimeFormatted || mappedSchedule.tripDateTime,
          tripStatus: mappedSchedule.tripStatus,
          clientAgency: mappedSchedule.clientAgency,
          notes: mappedSchedule.notes || '',
          vehicle: mappedSchedule.vehicle || '',
          assignedDriver: mappedSchedule.assignedDriver || '',
          routeName: mappedSchedule.routeName || '',
          startLocation: mappedSchedule.startLocation || '',
          destination: mappedSchedule.destination || '',
          stops: stopsArray,
          selectedDays: mappedSchedule.selectedDays || [],
          monthlyDay: mappedSchedule.monthlyDay || [],
          endDate: endDateFormatted || mappedSchedule.endDate || '',
          loadDescription: mappedSchedule.loadDescription || '',
          loadWeight: mappedSchedule.loadWeight || '',
          weightUnit: mappedSchedule.weightUnit || 'kg',
          loadVolume: mappedSchedule.loadVolume || '',
          volumeUnit: mappedSchedule.volumeUnit || 'm³',
          loadType: mappedSchedule.loadType || '',
          totalCapacity: mappedSchedule.totalCapacity || '',
          usedCapacity: mappedSchedule.usedCapacity || ''
        });
        setScheduleFormErrors({});
        setShowScheduleEditModal(true);
      } else {
        // Fallback to using the schedule data we already have
        setEditingSchedule(schedule);
    setScheduleFormData({
      scheduleId: schedule.scheduleId,
      scheduleName: schedule.scheduleName,
      scheduleType: schedule.scheduleType,
      tripDateTime: schedule.tripDateTime,
      tripStatus: schedule.tripStatus,
      clientAgency: schedule.clientAgency,
      notes: schedule.notes || '',
      vehicle: schedule.vehicle || '',
      assignedDriver: schedule.assignedDriver || '',
      routeName: schedule.routeName || '',
      startLocation: schedule.startLocation || '',
      destination: schedule.destination || '',
          stops: schedule.stops || [],
          selectedDays: schedule.selectedDays || [],
          monthlyDay: schedule.monthlyDay || [],
          endDate: schedule.endDate || '',
          loadDescription: schedule.loadDescription || '',
          loadWeight: schedule.loadWeight || '',
          weightUnit: schedule.weightUnit || 'kg',
          loadVolume: schedule.loadVolume || '',
          volumeUnit: schedule.volumeUnit || 'm³',
          loadType: schedule.loadType || '',
          totalCapacity: schedule.totalCapacity || '',
          usedCapacity: schedule.usedCapacity || ''
        });
        setScheduleFormErrors({});
        setShowScheduleEditModal(true);
      }
    } catch (err) {
      console.error('Error fetching schedule details:', err);
      // Fallback to using the schedule data we already have
      setEditingSchedule(schedule);
      setScheduleFormData({
        scheduleId: schedule.scheduleId,
        scheduleName: schedule.scheduleName,
        scheduleType: schedule.scheduleType,
        tripDateTime: schedule.tripDateTime,
        tripStatus: schedule.tripStatus,
        clientAgency: schedule.clientAgency,
        notes: schedule.notes || '',
        vehicle: schedule.vehicle || '',
        assignedDriver: schedule.assignedDriver || '',
        routeName: schedule.routeName || '',
        startLocation: schedule.startLocation || '',
        destination: schedule.destination || '',
      stops: schedule.stops || [],
      selectedDays: schedule.selectedDays || [],
      monthlyDay: schedule.monthlyDay || [],
      endDate: schedule.endDate || ''
    });
    setScheduleFormErrors({});
    setShowScheduleEditModal(true);
    }
  };

  // Get stops data from trip - use actual API data
  const getStopsData = (trip) => {
    // If stopsData is available (from API), use it directly
    if (trip.stopsData && Array.isArray(trip.stopsData) && trip.stopsData.length > 0) {
      return trip.stopsData.map((stop, index) => {
        // Format estimated_date for display
        let estimatedDateDisplay = '';
        if (stop.estimated_date) {
          const date = new Date(stop.estimated_date);
          if (!isNaN(date.getTime())) {
            estimatedDateDisplay = date.toLocaleString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: true
            });
          }
        }
        
        // Format actual_arrival_time for display
        let actualArrivalDisplay = '';
        if (stop.actual_arrival_time) {
          const date = new Date(stop.actual_arrival_time);
          if (!isNaN(date.getTime())) {
            actualArrivalDisplay = date.toLocaleString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: true
            });
          }
        }
      
      return {
          location: stop.stop_location || stop.location || `Stop ${index + 1}`,
          purpose: stop.purpose || '',
          estimatedDate: stop.estimatedDate || estimatedDateDisplay, // Use pre-formatted if available
          actualArrivalTime: actualArrivalDisplay,
          stopSequence: stop.stop_sequence || stop.stopSequence || index + 1,
          hasActualArrival: !!stop.actual_arrival_time
      };
    });
    }
    
    // If no stops data, return empty array
    return [];
  };

  const handleStopsClick = async (trip) => {
    console.log('Admin Trip Management: handleStopsClick called for trip:', trip);
    console.log('Admin Trip Management: Trip ID:', trip.id || trip.trip_id || trip.tripId);
    console.log('Admin Trip Management: Trip stops count:', trip.stops);
    console.log('Admin Trip Management: Trip stopsData:', trip.stopsData);
    
    // Handle both regular trips (with stops as number) and schedules (with stops as array)
    if (trip.stopsData && Array.isArray(trip.stopsData) && trip.stopsData.length > 0) {
      // This is a schedule with stops array already loaded
      console.log('Admin Trip Management: Using existing stopsData');
      setSelectedTripForStops(trip);
      setShowStopsModal(true);
      return;
    } else if (trip.scheduleId || trip.schedule_id) {
      // This is a schedule - fetch stops from API
      const scheduleId = trip.id || trip._original?.id;
      if (!scheduleId) {
        console.warn('Admin Trip Management: No schedule ID found, cannot fetch stops');
          return;
        }
      
      try {
        console.log('Admin Trip Management: Fetching stops for schedule ID:', scheduleId);
        const response = await tripsService.getStopsByScheduleId(scheduleId);
        console.log('Admin Trip Management: Fetched schedule stops response:', response);
        
        // Handle different response formats
        let stops = [];
        if (Array.isArray(response)) {
          stops = response;
        } else if (response && Array.isArray(response.data)) {
          stops = response.data;
        } else if (response && Array.isArray(response.items)) {
          stops = response.items;
        } else if (response && response.data && Array.isArray(response.data.data)) {
          stops = response.data.data;
        }
        
        console.log('Admin Trip Management: Parsed schedule stops:', stops);
        
        // Pass raw stops data - getStopsData will format them for display
        setSelectedTripForStops({ 
          ...trip, 
          stopsData: stops, // Pass raw stops - getStopsData will format them
          scheduleId: trip.scheduleId || trip.schedule_id
        });
        setShowStopsModal(true);
      } catch (err) {
        console.error('Admin Trip Management: Error fetching schedule stops:', err);
        // Still show modal even if fetch fails, with empty stops
        setSelectedTripForStops({ 
          ...trip, 
          stopsData: [],
          scheduleId: trip.scheduleId || trip.schedule_id
        });
        setShowStopsModal(true);
      }
    } else {
      // This is a regular trip - fetch stops from API
      const tripId = trip.id || trip.trip_id || trip.tripId;
      
      // Always try to show modal if we have a trip ID, even if stops count is 0
      if (!tripId) {
        console.warn('Admin Trip Management: No trip ID found, cannot fetch stops');
        return;
      }
      
      try {
        console.log('Admin Trip Management: Fetching stops for trip ID:', tripId);
        const response = await tripsService.getStopsByTripId(tripId);
        console.log('Admin Trip Management: Fetched stops response:', response);
        
        // Handle different response structures
        let stopsData = [];
        if (response?.data) {
          if (Array.isArray(response.data)) {
            stopsData = response.data;
          } else if (response.data.stops && Array.isArray(response.data.stops)) {
            stopsData = response.data.stops;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            stopsData = response.data.data;
          }
        } else if (Array.isArray(response)) {
          stopsData = response;
        } else if (response?.stops && Array.isArray(response.stops)) {
          stopsData = response.stops;
        }
        
        console.log('Admin Trip Management: Parsed stops data:', stopsData);
        console.log('Admin Trip Management: Opening stops modal with', stopsData.length, 'stops');
        
        // Always show modal, even if no stops found (will show empty state)
        const tripWithStops = { ...trip, stopsData: stopsData || [] };
        console.log('Admin Trip Management: Setting selectedTripForStops:', tripWithStops);
        setSelectedTripForStops(tripWithStops);
        console.log('Admin Trip Management: Setting showStopsModal to true');
    setShowStopsModal(true);
        console.log('Admin Trip Management: Modal state should be open now');
      } catch (err) {
        console.error('Admin Trip Management: Error fetching stops:', err);
        // Still show modal even if fetch fails, with empty stops
        setSelectedTripForStops({ ...trip, stopsData: [] });
        setShowStopsModal(true);
      }
    }
  };

  // Extract location name from stop description (e.g., "Started From Madurai" -> "Madurai")
  const extractLocationName = (description) => {
    if (!description) return '';
    // Remove common prefixes like "Started From", "Reached", "Arrived at", etc.
    const cleaned = description
      .replace(/^(Started From|Reached|Arrived at|Departed from|Left)\s+/i, '')
      .trim();
    return cleaned || description;
  };

  // Generate and download trip sheet
  const handleDownloadTripSheet = async () => {
    if (!viewingTrip) return;
    
    try {
      const blob = await tripsService.downloadTripsheet(viewingTrip.id, 'excel');
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Trip_Sheet_${viewingTrip.id || viewingTrip.trip_id}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      return;
    } catch (err) {
      console.error('Error downloading tripsheet:', err);
      alert('Failed to download tripsheet: ' + err.message);
      // Fall through to HTML generation if API fails
    }
    
    // Fallback to HTML generation if API call fails

    // Extract stop locations
    const stopLocations = [
      'Madurai',
      'Dindugal',
      'Dindugal',
      '' // Empty for last incomplete entry
    ].filter(loc => loc);

    // Create HTML content for trip sheet
    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Trip Sheet - ${viewingTrip.id}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      padding: 40px;
      background: #f5f7fa;
      color: #333;
    }
    .trip-sheet {
      max-width: 900px;
      margin: 0 auto;
      background: white;
      padding: 40px;
      border-radius: 12px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }
    .header {
      text-align: center;
      border-bottom: 3px solid #2563EB;
      padding-bottom: 20px;
      margin-bottom: 30px;
    }
    .header h1 {
      color: #2563EB;
      font-size: 28px;
      margin-bottom: 10px;
    }
    .header .trip-id {
      font-size: 18px;
      color: #666;
      font-weight: 600;
    }
    .section {
      margin-bottom: 30px;
    }
    .section-title {
      font-size: 18px;
      color: #2563EB;
      margin-bottom: 15px;
      padding-bottom: 8px;
      border-bottom: 2px solid #e5e7eb;
    }
    .info-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 20px;
      margin-bottom: 20px;
    }
    .info-item {
      display: flex;
      flex-direction: column;
    }
    .info-label {
      font-size: 12px;
      color: #6b7280;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 5px;
    }
    .info-value {
      font-size: 16px;
      color: #111827;
      font-weight: 500;
    }
    .status-badge {
      display: inline-block;
      padding: 4px 12px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 600;
    }
    .status-in-progress { background: #dbeafe; color: #1e40af; }
    .status-scheduled { background: #fef3c7; color: #92400e; }
    .status-completed { background: #d1fae5; color: #065f46; }
    .status-cancelled { background: #fee2e2; color: #991b1b; }
    .status-delayed { background: #fce7f3; color: #9f1239; }
    .route-section {
      background: #f9fafb;
      padding: 20px;
      border-radius: 8px;
      margin-bottom: 20px;
    }
    .route-item {
      display: flex;
      align-items: center;
      margin-bottom: 15px;
    }
    .route-dot {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      margin-right: 15px;
    }
    .origin-dot { background: #10b981; }
    .destination-dot { background: #ef4444; }
    .route-location {
      font-weight: 600;
      font-size: 16px;
      color: #111827;
    }
    .route-address {
      font-size: 14px;
      color: #6b7280;
      margin-top: 3px;
    }
    .stops-list {
      list-style: none;
      padding-left: 0;
    }
    .stops-list li {
      padding: 12px 15px;
      margin-bottom: 8px;
      background: #f9fafb;
      border-left: 4px solid #2563EB;
      border-radius: 4px;
      font-size: 15px;
      font-weight: 500;
      color: #111827;
    }
    .timeline-list {
      list-style: none;
      padding-left: 0;
    }
    .timeline-list li {
      padding: 15px;
      margin-bottom: 10px;
      background: #f9fafb;
      border-left: 4px solid #10b981;
      border-radius: 4px;
      position: relative;
    }
    .timeline-date {
      font-size: 12px;
      color: #6b7280;
      margin-bottom: 5px;
    }
    .timeline-description {
      font-size: 15px;
      color: #111827;
      font-weight: 500;
    }
    .footer {
      margin-top: 40px;
      padding-top: 20px;
      border-top: 2px solid #e5e7eb;
      text-align: center;
      color: #6b7280;
      font-size: 12px;
    }
    @media print {
      body { background: white; padding: 20px; }
      .trip-sheet { box-shadow: none; }
    }
  </style>
</head>
<body>
  <div class="trip-sheet">
    <div class="header">
      <h1>Nettaa Logistics Service</h1>
      <div class="trip-id">Trip Sheet - ${viewingTrip.id}</div>
    </div>

    <div class="section">
      <h2 class="section-title">Trip Information</h2>
      <div class="info-grid">
        <div class="info-item">
          <span class="info-label">Trip ID</span>
          <span class="info-value">${viewingTrip.id}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Date & Time</span>
          <span class="info-value">${viewingTrip.tripDateTime}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Status</span>
          <span class="info-value">
            <span class="status-badge status-${getStatusClass(viewingTrip.status).replace('status-', '')}">${viewingTrip.status}</span>
          </span>
        </div>
        <div class="info-item">
          <span class="info-label">ETA</span>
          <span class="info-value">${viewingTrip.eta}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Transporter</span>
          <span class="info-value">${viewingTrip.agency || 'N/A'}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Load Weight</span>
          <span class="info-value">${viewingTrip.loadWeight ? viewingTrip.loadWeight.toLocaleString() : '0'} ${viewingTrip.weightUnit || 'kg'}</span>
        </div>
      </div>
    </div>

    <div class="section">
      <h2 class="section-title">Vehicle & Driver Information</h2>
      <div class="info-grid">
        <div class="info-item">
          <span class="info-label">Vehicle Number</span>
          <span class="info-value">${viewingTrip.vehicleNumber}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Vehicle Type</span>
          <span class="info-value">Heavy Truck - 16 Wheeler</span>
        </div>
        <div class="info-item">
          <span class="info-label">Driver Name</span>
          <span class="info-value">${viewingTrip.driverName}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Driver Contact</span>
          <span class="info-value">+91 98765 43210</span>
        </div>
        <div class="info-item">
          <span class="info-label">License Number</span>
          <span class="info-value">DL-0420110012345</span>
        </div>
        <div class="info-item">
          <span class="info-label">Route</span>
          <span class="info-value">${viewingTrip.route || 'N/A'}</span>
        </div>
      </div>
    </div>

    <div class="section">
      <h2 class="section-title">Route Information</h2>
      <div class="route-section">
        <div class="route-item">
          <div class="route-dot origin-dot"></div>
          <div>
            <div class="route-location">Chennai Central</div>
            <div class="route-address">Chennai Central Warehouse, Anna Salai, Chennai - 600001</div>
          </div>
        </div>
        <div style="text-align: center; margin: 15px 0; color: #6b7280; font-weight: 600;">346 km</div>
        <div class="route-item">
          <div class="route-dot destination-dot"></div>
          <div>
            <div class="route-location">Bangalore Hub</div>
            <div class="route-address">Bangalore Distribution Hub, Electronic City, Bangalore - 560100</div>
          </div>
        </div>
      </div>
    </div>

    <div class="section">
      <h2 class="section-title">Stops</h2>
      <ul class="stops-list">
        ${stopLocations.map(location => `<li>${location}</li>`).join('')}
      </ul>
    </div>

    <div class="section">
      <h2 class="section-title">Timeline</h2>
      <ul class="timeline-list">
        <li>
          <div class="timeline-date">15 Jun 2023, 07:45 AM</div>
          <div class="timeline-description">Driver arrived at origin</div>
        </li>
        <li>
          <div class="timeline-date">15 Jun 2023, 08:30 AM</div>
          <div class="timeline-description">Loading started</div>
        </li>
        <li>
          <div class="timeline-date">15 Jun 2023, 09:15 AM</div>
          <div class="timeline-description">Loading completed & departed origin</div>
        </li>
        <li>
          <div class="timeline-date">15 Jun 2023, 10:22 AM</div>
          <div class="timeline-description">In transit</div>
        </li>
      </ul>
    </div>

    <div class="footer">
      <p>Generated on ${new Date().toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
      <p>Nettaa Logistics Service - Trip Management System</p>
    </div>
  </div>
</body>
</html>
    `;

    // Create blob and download
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Trip_Sheet_${viewingTrip.id}_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleCancelClick = (trip) => {
    setTripToCancel(trip);
    setShowCancelConfirm(true);
  };

  const handleCancelConfirm = async () => {
    if (tripToCancel) {
      try {
        const tripId = tripToCancel.id || tripToCancel.tripId || tripToCancel._original?.id;
        if (!tripId) {
          alert('Trip ID not found. Please try again.');
          return;
        }
        
        await tripsService.cancelTrip(tripId);
        
        // Refresh trips list
        await fetchTrips();
      
      // Set success modal info
      setCancelledTripInfo({
          id: tripToCancel.tripId || tripToCancel.id,
        status: tripToCancel.status
      });
      
      // Close confirm, show success
      setShowCancelConfirm(false);
      setShowCancelSuccess(true);
      setTripToCancel(null);
      } catch (err) {
        setError(err.message);
        console.error('Error cancelling trip:', err);
        alert('Failed to cancel trip: ' + err.message);
      }
    }
  };

  const handleCancelCancel = () => {
    setShowCancelConfirm(false);
    setTripToCancel(null);
  };

  const handleCancelSuccessClose = () => {
    setShowCancelSuccess(false);
    setCancelledTripInfo(null);
  };

  const handleEditClick = async (trip) => {
    try {
      setLoading(true);
      
      // Fetch full trip details including stops
      const response = await tripsService.getTripById(trip.id || trip._original?.id || trip.tripId, true);
      const tripData = response.data || response;
      
      console.log('Edit trip - Full trip data:', tripData);
      
      // Use original API data if available, otherwise use mapped data
      const apiTrip = trip._original || tripData;
      
    // Parse trip date and time for datetime-local input
      let parsedDateTime = '';
      if (apiTrip.trip_date_time) {
        const date = new Date(apiTrip.trip_date_time);
        if (!isNaN(date.getTime())) {
          // Format to datetime-local format (YYYY-MM-DDTHH:mm)
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          parsedDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;
        }
      }
    
      // Extract load weight (remove unit and parse number)
      let loadWeightValue = '';
      if (apiTrip.load_weight) {
        loadWeightValue = apiTrip.load_weight.toString().replace(/,/g, '');
      } else if (trip.loadWeight) {
        loadWeightValue = trip.loadWeight.toString().replace(/,/g, '').replace(/\s*(kg|tons|ton)\s*/gi, '');
      }
      
      // Get weight unit
      const weightUnit = apiTrip.weight_unit || trip.weightUnit || 'kg';
    
      // Get volume unit
      const volumeUnit = apiTrip.volume_unit || trip.volumeUnit || 'm³';
      
      // Get load volume
      const loadVolume = apiTrip.load_volume || trip.loadVolume || '';
      
      // Get load type
      const loadType = apiTrip.load_type || trip.loadType || '';
      
      // Get start and destination locations
      const startLoc = apiTrip.start_location || trip.startLocation || (trip.route ? trip.route.split(' → ')[0] : '');
      const destLoc = apiTrip.destination || trip.destination || (trip.route ? trip.route.split(' → ')[1] : '');
      
      // Get route name (if available)
      const routeName = apiTrip.route_name || trip.routeName || trip.route || '';
      
      // Get agency name - try multiple sources
      const agencyName = apiTrip.agency?.agency_name || apiTrip.agency_name || apiTrip.client_agency || trip.agency || '';
      
      // Get vehicle number
      const vehicleNumber = apiTrip.vehicle?.vehicle_number || apiTrip.vehicle_number || trip.vehicleNumber || '';
      
      // Get driver name
      const driverName = apiTrip.driver?.full_name || apiTrip.driver_name || apiTrip.assigned_driver?.full_name || trip.driverName || '';
      
      // Get stops from API response - try multiple sources
      let stopsArray = [];
      
      // First, try to get stops from the trip response
      let stopsData = null;
      if (apiTrip.stops && Array.isArray(apiTrip.stops) && apiTrip.stops.length > 0) {
        stopsData = apiTrip.stops;
      } else if (tripData.stops && Array.isArray(tripData.stops) && tripData.stops.length > 0) {
        stopsData = tripData.stops;
      } else if (trip.stopsData && Array.isArray(trip.stopsData) && trip.stopsData.length > 0) {
        stopsData = trip.stopsData;
      }
      
      // If no stops found, try fetching separately
      if (!stopsData || stopsData.length === 0) {
        try {
          const stopsResponse = await tripsService.getStopsByTripId(trip.id || trip._original?.id || trip.tripId);
          const stopsResponseData = stopsResponse.data || stopsResponse;
          if (Array.isArray(stopsResponseData)) {
            stopsData = stopsResponseData;
          } else if (stopsResponseData.stops && Array.isArray(stopsResponseData.stops)) {
            stopsData = stopsResponseData.stops;
          } else if (stopsResponseData.items && Array.isArray(stopsResponseData.items)) {
            stopsData = stopsResponseData.items;
          }
        } catch (err) {
          console.error('Error fetching stops separately:', err);
        }
      }
      
      // Map stops to form format
      if (stopsData && Array.isArray(stopsData) && stopsData.length > 0) {
        stopsArray = stopsData.map(stop => {
          // Format estimated_date for datetime-local input
          let estimatedDate = '';
          if (stop.estimated_date) {
            const date = new Date(stop.estimated_date);
            if (!isNaN(date.getTime())) {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              const hours = String(date.getHours()).padStart(2, '0');
              const minutes = String(date.getMinutes()).padStart(2, '0');
              estimatedDate = `${year}-${month}-${day}T${hours}:${minutes}`;
            }
          }
          
          return {
            id: stop.id, // Keep stop ID for updates
            location: stop.stop_location || stop.location || '',
            purpose: stop.purpose || '',
            estimatedDate: estimatedDate
          };
        });
      }
    
    // Get customer_id from trip data
    const customerId = apiTrip.customer_id || trip.customerId || null;
    
    // Set customer search to show selected customer name
    if (customerId) {
      const selectedCustomer = allCustomersForFilter.find(c => 
        c.id === customerId || c.id?.toString() === customerId?.toString()
      );
      if (selectedCustomer) {
        setCustomerSearch(selectedCustomer.name || '');
      } else {
        setCustomerSearch('');
      }
    } else {
      setCustomerSearch('');
    }
    setCustomerDropdownOpen(false);
    
    setFormData({
        tripId: trip.tripId || trip.id || apiTrip.trip_id || '',
        tripDateTime: parsedDateTime,
        tripStatus: apiTrip.trip_status || trip.status || 'Scheduled',
        clientAgency: agencyName,
        customer: customerId ? String(customerId) : '',
        notes: apiTrip.notes || trip.notes || '',
        vehicle: vehicleNumber,
        assignedDriver: driverName,
        routeName: routeName,
      startLocation: startLoc,
      destination: destLoc,
      stops: stopsArray,
      loadDescription: apiTrip.load_description || trip.loadDescription || '',
      loadWeight: loadWeightValue,
      weightUnit: weightUnit,
      loadVolume: loadVolume,
      volumeUnit: volumeUnit,
      loadType: loadType,
      totalCapacity: apiTrip.total_capacity ? String(apiTrip.total_capacity) : (trip.totalCapacity || ''),
      usedCapacity: apiTrip.used_capacity ? String(apiTrip.used_capacity) : (trip.usedCapacity || ''),
      remainingCapacity: apiTrip.remaining_capacity ? String(apiTrip.remaining_capacity) : (trip.remainingCapacity || '')
    });
      setStartLocationSearch(startLoc || '');
      setDestinationSearch(destLoc || '');
    setFormErrors({});
      setEditingTrip(trip);
    setShowEditModal(true);
    } catch (err) {
      console.error('Error fetching trip details:', err);
      alert('Failed to load trip details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVehicleChange = async (vehicleNumber) => {
    if (!vehicleNumber || vehicleNumber.trim() === '') {
      setFormData({
        ...formData,
        vehicle: '',
        assignedDriver: ''
      });
      return;
    }
    
    // First try to find vehicle from allVehiclesForFilter (now has full vehicle data)
    let selectedVehicle = allVehiclesForFilter.find(v => 
      (v.number || v.vehicle_number || '') === vehicleNumber
    );
    
    // If not found in filter, try formDataOptions.vehicles
    if (!selectedVehicle && formDataOptions.vehicles && Array.isArray(formDataOptions.vehicles)) {
      selectedVehicle = formDataOptions.vehicles.find(v => 
        (v.vehicle_number || v.vehicleNumber || '') === vehicleNumber
      );
    }
    
    // If still not found, fetch from API
    if (!selectedVehicle) {
      try {
        console.log('Admin Trip Management: Fetching vehicle details for:', vehicleNumber);
        const vehiclesResponse = await vehiclesService.getVehicles({ limit: 1000 });
        let vehiclesList = [];
        
        if (Array.isArray(vehiclesResponse?.data)) {
          vehiclesList = vehiclesResponse.data;
        } else if (vehiclesResponse?.data?.items) {
          vehiclesList = vehiclesResponse.data.items;
        } else if (Array.isArray(vehiclesResponse)) {
          vehiclesList = vehiclesResponse;
        } else if (vehiclesResponse?.items) {
          vehiclesList = vehiclesResponse.items;
        }
        
        console.log('Admin Trip Management: Fetched vehicles list:', vehiclesList.length, 'vehicles');
        
        selectedVehicle = vehiclesList.find(v => 
          (v.vehicle_number || v.vehicleNumber || '') === vehicleNumber
        );
        
        console.log('Admin Trip Management: Found vehicle from API:', selectedVehicle);
      } catch (err) {
        console.error('Admin Trip Management: Error fetching vehicle details:', err);
      }
    } else {
      console.log('Admin Trip Management: Found vehicle in allVehiclesForFilter:', selectedVehicle);
    }
    
    if (!selectedVehicle) {
      // Vehicle not found, clear driver
    setFormData({
      ...formData,
      vehicle: vehicleNumber,
        assignedDriver: '',
        clientAgency: formData.clientAgency
      });
      return;
    }

    console.log('Admin Trip Management: Selected vehicle:', selectedVehicle);

    // Try to get driver name from vehicle object
    let driverName = '';
    let driverId = null;

    // Check for driver name in various possible fields (in order of preference)
    if (selectedVehicle.driver?.full_name) {
      driverName = selectedVehicle.driver.full_name;
      driverId = selectedVehicle.driver.id || selectedVehicle.driver.driver_id;
      console.log('Admin Trip Management: Found driver from nested driver object:', driverName);
    } else if (selectedVehicle.assigned_driver_name) {
      driverName = selectedVehicle.assigned_driver_name;
      driverId = selectedVehicle.assigned_driver_id;
      console.log('Admin Trip Management: Found driver from assigned_driver_name:', driverName);
    } else if (selectedVehicle.driver_name) {
      driverName = selectedVehicle.driver_name;
      driverId = selectedVehicle.assigned_driver_id;
      console.log('Admin Trip Management: Found driver from driver_name:', driverName);
    } else if (selectedVehicle.assignedDriver) {
      driverName = selectedVehicle.assignedDriver;
      driverId = selectedVehicle.assigned_driver_id || selectedVehicle.assignedDriverId;
      console.log('Admin Trip Management: Found driver from assignedDriver:', driverName);
    } else if (selectedVehicle.assigned_driver_id) {
      // Only have driver ID, need to fetch driver details
      driverId = selectedVehicle.assigned_driver_id;
      console.log('Admin Trip Management: Fetching driver details for driver_id:', driverId);
      
      // First try to find in allDriversForFilter
      const driverFromFilter = allDriversForFilter.find(d => 
        (d.id || d.driver_id) === driverId
      );
      
      if (driverFromFilter) {
        driverName = driverFromFilter.name || driverFromFilter.full_name || '';
        console.log('Admin Trip Management: Found driver in allDriversForFilter:', driverName);
      } else {
        // If not in filter, fetch from API
        try {
          const driverResponse = await driversService.getDrivers({ limit: 1000 });
          
          // Find the driver in the response
          let driversList = [];
          if (Array.isArray(driverResponse.data)) {
            driversList = driverResponse.data;
          } else if (driverResponse.data?.items) {
            driversList = driverResponse.data.items;
          } else if (Array.isArray(driverResponse)) {
            driversList = driverResponse;
          } else if (driverResponse.items) {
            driversList = driverResponse.items;
          }
          
          const driver = driversList.find(d => 
            d.id === driverId || 
            d.driver_id === driverId ||
            d.assigned_driver_id === driverId
          );
          
          if (driver) {
            driverName = driver.full_name || driver.driver_name || driver.name || '';
            console.log('Admin Trip Management: Fetched driver name from API:', driverName);
          } else {
            console.warn('Admin Trip Management: Driver not found for driver_id:', driverId);
          }
        } catch (err) {
          console.error('Admin Trip Management: Error fetching driver details:', err);
          // Continue without driver name if fetch fails
        }
      }
    } else {
      console.log('Admin Trip Management: No driver assigned to this vehicle');
    }

    // Update form data with vehicle and driver info
    console.log('Admin Trip Management: Setting assigned driver to:', driverName);
    console.log('Admin Trip Management: Vehicle object keys:', selectedVehicle ? Object.keys(selectedVehicle) : 'No vehicle');
    console.log('Admin Trip Management: Vehicle driver fields:', {
      driver: selectedVehicle?.driver,
      assigned_driver_name: selectedVehicle?.assigned_driver_name,
      driver_name: selectedVehicle?.driver_name,
      assignedDriver: selectedVehicle?.assignedDriver,
      assigned_driver_id: selectedVehicle?.assigned_driver_id
    });
    
    setFormData(prev => {
      const updated = {
        ...prev,
        vehicle: vehicleNumber,
        assignedDriver: driverName,
        clientAgency: prev.clientAgency
      };
      console.log('Admin Trip Management: Updated formData:', updated);
      return updated;
    });
    
    if (formErrors.vehicle) {
      setFormErrors({...formErrors, vehicle: ''});
    }
    if (formErrors.assignedDriver) {
      setFormErrors({...formErrors, assignedDriver: ''});
    }
  };


  // Initialize autocomplete for trip stop locations
  const initializeStopAutocomplete = (index, inputElement) => {
    if (!inputElement || !isGoogleMapsLoaded) return;
    
    // Clean up existing autocomplete if any
    if (stopAutocompleteRefs.current[index]) {
      window.google.maps.event.clearInstanceListeners(stopAutocompleteRefs.current[index]);
    }
    
    try {
      const autocomplete = new window.google.maps.places.Autocomplete(inputElement, {
        types: ['geocode', 'establishment'],
        componentRestrictions: { country: 'in' },
        fields: ['formatted_address', 'geometry', 'name', 'place_id']
      });
      
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place && place.formatted_address) {
          const fullAddress = place.formatted_address;
          // Update formData first
          handleStopChange(index, 'location', fullAddress);
          // Then sync the search state to match
          setStopLocationSearches(prev => ({ ...prev, [index]: fullAddress }));
        }
      });
      
      stopAutocompleteRefs.current[index] = autocomplete;
      inputElement.setAttribute('data-gm-autocomplete', 'true');
    } catch (e) {
      console.error(`Error initializing autocomplete for stop ${index}:`, e);
    }
  };

  const handleAddStop = () => {
    const newIndex = formData.stops.length;
    setFormData({
      ...formData,
      stops: [...formData.stops, { location: '', purpose: '', estimatedDate: '' }]
    });
    // Initialize autocomplete for new stop after a short delay
    setTimeout(() => {
      const inputRef = stopLocationInputRefs.current[newIndex];
      if (inputRef && inputRef.current) {
        initializeStopAutocomplete(newIndex, inputRef.current);
      }
    }, 100);
  };

  const handleStopChange = (index, field, value) => {
    const updatedStops = [...formData.stops];
    updatedStops[index][field] = value;
    setFormData({
      ...formData,
      stops: updatedStops
    });
    
    // Update search query for location field
    if (field === 'location') {
      setStopLocationSearches(prev => ({ ...prev, [index]: value }));
    }
  };

  const handleStopLocationInput = (index, value) => {
    // Update search state for autocomplete suggestions
    setStopLocationSearches(prev => ({ ...prev, [index]: value }));
    // Update formData - this is the source of truth
    handleStopChange(index, 'location', value);
    // Show suggestions if there's text and matches exist (only for fallback when Google Maps is not loaded)
    if (!isGoogleMapsLoaded && value && value.trim().length > 0) {
      const searchLower = value.toLowerCase();
      const hasMatches = indianCities.some(city => city.toLowerCase().includes(searchLower));
      setStopLocationSuggestionsOpen(prev => ({ ...prev, [index]: hasMatches }));
    } else {
      setStopLocationSuggestionsOpen(prev => ({ ...prev, [index]: false }));
    }
  };

  const handleStopLocationCitySelect = (index, city) => {
    handleStopChange(index, 'location', city);
    setStopLocationSearches(prev => ({ ...prev, [index]: city }));
    setStopLocationSuggestionsOpen(prev => ({ ...prev, [index]: false }));
  };

  const filteredStopCities = (index) => {
    const search = stopLocationSearches[index] || '';
    if (!search || !search.trim()) return [];
    const searchLower = search.toLowerCase();
    return indianCities
      .filter(city => city.toLowerCase().includes(searchLower))
      .slice(0, 10);
  };

  const handleRemoveStop = (index) => {
    const updatedStops = formData.stops.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      stops: updatedStops
    });
    // Clean up refs and autocomplete
    delete stopLocationInputRefs.current[index];
    if (stopAutocompleteRefs.current[index]) {
      window.google.maps.event.clearInstanceListeners(stopAutocompleteRefs.current[index]);
      delete stopAutocompleteRefs.current[index];
    }
    setStopLocationSearches(prev => {
      const newSearches = { ...prev };
      delete newSearches[index];
      return newSearches;
    });
    setStopLocationSuggestionsOpen(prev => {
      const newSuggestions = { ...prev };
      delete newSuggestions[index];
      return newSuggestions;
    });
  };

  // Initialize autocomplete for schedule stop locations
  const initializeScheduleStopAutocomplete = (index, inputElement) => {
    if (!inputElement || !isScheduleGoogleMapsLoaded) return;
    
    // Clean up existing autocomplete if any
    if (scheduleStopAutocompleteRefs.current[index]) {
      window.google.maps.event.clearInstanceListeners(scheduleStopAutocompleteRefs.current[index]);
    }
    
    try {
      const autocomplete = new window.google.maps.places.Autocomplete(inputElement, {
        types: ['geocode', 'establishment'],
        componentRestrictions: { country: 'in' },
        fields: ['formatted_address', 'geometry', 'name', 'place_id']
      });
      
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place && place.formatted_address) {
          const fullAddress = place.formatted_address;
          // Update formData first
          handleScheduleStopChange(index, 'location', fullAddress);
          // Then sync the search state to match
          setScheduleStopLocationSearches(prev => ({ ...prev, [index]: fullAddress }));
        }
      });
      
      scheduleStopAutocompleteRefs.current[index] = autocomplete;
      inputElement.setAttribute('data-gm-autocomplete', 'true');
    } catch (e) {
      console.error(`Error initializing autocomplete for schedule stop ${index}:`, e);
    }
  };

  // Schedule stops handlers
  const handleScheduleAddStop = () => {
    const newIndex = scheduleFormData.stops.length;
    handleScheduleFormChange('stops', [...scheduleFormData.stops, { location: '', estimatedDate: '', purpose: '' }]);
    // Initialize autocomplete for new stop after a short delay
    setTimeout(() => {
      const inputRef = scheduleStopLocationInputRefs.current[newIndex];
      if (inputRef && inputRef.current) {
        initializeScheduleStopAutocomplete(newIndex, inputRef.current);
      }
    }, 100);
  };

  const handleScheduleStopChange = (index, field, value) => {
    const updatedStops = [...scheduleFormData.stops];
    updatedStops[index][field] = value;
    handleScheduleFormChange('stops', updatedStops);
    
    // Update search query for location field
    if (field === 'location') {
      setScheduleStopLocationSearches(prev => ({ ...prev, [index]: value }));
    }
  };

  const handleScheduleStopLocationInput = (index, value) => {
    // Update search state for autocomplete suggestions
    setScheduleStopLocationSearches(prev => ({ ...prev, [index]: value }));
    // Update formData - this is the source of truth
    handleScheduleStopChange(index, 'location', value);
    // Show suggestions if there's text and matches exist (only for fallback when Google Maps is not loaded)
    if (!isScheduleGoogleMapsLoaded && value && value.trim().length > 0) {
      const searchLower = value.toLowerCase();
      const hasMatches = indianCities.some(city => city.toLowerCase().includes(searchLower));
      setScheduleStopLocationSuggestionsOpen(prev => ({ ...prev, [index]: hasMatches }));
    } else {
      setScheduleStopLocationSuggestionsOpen(prev => ({ ...prev, [index]: false }));
    }
  };

  const handleScheduleStopLocationCitySelect = (index, city) => {
    handleScheduleStopChange(index, 'location', city);
    setScheduleStopLocationSearches(prev => ({ ...prev, [index]: city }));
    setScheduleStopLocationSuggestionsOpen(prev => ({ ...prev, [index]: false }));
  };

  const filteredScheduleStopCities = (index) => {
    const search = scheduleStopLocationSearches[index] || '';
    if (!search || !search.trim()) return [];
    const searchLower = search.toLowerCase();
    return indianCities
      .filter(city => city.toLowerCase().includes(searchLower))
      .slice(0, 10);
  };

  const handleScheduleRemoveStop = (index) => {
    const updatedStops = scheduleFormData.stops.filter((_, i) => i !== index);
    handleScheduleFormChange('stops', updatedStops);
    // Clean up refs and autocomplete
    delete scheduleStopLocationInputRefs.current[index];
    if (scheduleStopAutocompleteRefs.current[index]) {
      window.google.maps.event.clearInstanceListeners(scheduleStopAutocompleteRefs.current[index]);
      delete scheduleStopAutocompleteRefs.current[index];
    }
    setScheduleStopLocationSearches(prev => {
      const newSearches = { ...prev };
      delete newSearches[index];
      return newSearches;
    });
    setScheduleStopLocationSuggestionsOpen(prev => {
      const newSuggestions = { ...prev };
      delete newSuggestions[index];
      return newSuggestions;
    });
  };

  const handleExport = async () => {
    try {
      // Build query parameters for export (same as current filters)
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (statusFilter) params.trip_status = statusFilter;
      
      // Convert filter IDs to numbers
      if (driverFilter) {
        params.assigned_driver_id = typeof driverFilter === 'number' ? driverFilter : Number(driverFilter);
      }
      if (vehicleFilter) {
        params.vehicle_id = typeof vehicleFilter === 'number' ? vehicleFilter : Number(vehicleFilter);
      }
      if (agencyFilter) {
        params.agency_id = typeof agencyFilter === 'number' ? agencyFilter : Number(agencyFilter);
      }
      
      // Handle date range filter - convert text to ISO dates
      if (dateRangeFilter) {
        const now = new Date();
        let startDate, endDate;
        
        if (dateRangeFilter === 'Today') {
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
          endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        } else if (dateRangeFilter === 'This Week') {
          const dayOfWeek = now.getDay();
          const diff = now.getDate() - dayOfWeek;
          startDate = new Date(now.getFullYear(), now.getMonth(), diff, 0, 0, 0);
          endDate = new Date(now.getFullYear(), now.getMonth(), diff + 6, 23, 59, 59);
        } else if (dateRangeFilter === 'This Month') {
          startDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
          endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
        } else if (dateRangeFilter.includes(' to ')) {
          const [start, end] = dateRangeFilter.split(' to ');
          if (start) startDate = new Date(start);
          if (end) endDate = new Date(end);
        }
        
        if (startDate) params.start_date = startDate.toISOString();
        if (endDate) params.end_date = endDate.toISOString();
      }

      const blob = await tripsService.exportTrips(params);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Trips_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting trips:', err);
      alert('Failed to export trips: ' + err.message);
      
      // Fallback to CSV export if API fails
    const headers = [
      'Trip ID',
      'Trip Date & Time',
      'Transporter',
      'Vehicle Number',
      'Driver Name',
      'Route',
      'Stops',
      'Load Weight (kg)',
      'Status',
      'ETA / Duration'
    ];

    const escapeCSV = (str) => {
      if (str === null || str === undefined) {
        return '';
      }
      str = String(str);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const rows = [headers.map(escapeCSV)];
    filteredTrips.forEach((trip) => {
      const row = [
        trip.id,
        trip.tripDateTime,
        trip.agency || '',
        trip.vehicleNumber,
        trip.driverName,
        trip.route,
        trip.stops || 0,
        trip.loadWeight || 0,
        trip.status,
        trip.eta || ''
      ].map(escapeCSV);
      rows.push(row);
    });

    const csvContent = '\ufeff' + rows.map(r => r.join(',')).join('\n');
    
    // Create blob and download
      const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const csvUrl = URL.createObjectURL(csvBlob);
      const csvLink = document.createElement('a');
      csvLink.href = csvUrl;
      csvLink.download = `Trips_Export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(csvLink);
      csvLink.click();
      document.body.removeChild(csvLink);
      URL.revokeObjectURL(csvUrl);
    }
  };

  const getStatusClass = (status) => {
    const statusMap = {
      'In Progress': 'status-in-progress',
      'Scheduled': 'status-scheduled',
      'Completed': 'status-completed',
      'Cancelled': 'status-cancelled',
      'Delayed': 'status-delayed'
    };
    return statusMap[status] || '';
  };

  return (
    <div className="trip-management-container">
      {/* Header Section */}
      <div className="trip-header">
        {(activeSubTab === 'trips' || activeSubTab === 'trip-schedule') ? (
          <div className="trip-filters">
        <div className="filter-group">
          <div className="filter-wrapper" ref={statusDropdownRef}>
            <button
              className={`filter-btn ${statusFilter ? 'active' : ''}`}
              onClick={() => {
                setStatusDropdownOpen(!statusDropdownOpen);
                setDateRangeDropdownOpen(false);
                setDriverDropdownOpen(false);
                setVehicleDropdownOpen(false);
                setAgencyDropdownOpen(false);
              }}
            >
              <span>🔍</span> Trip Status {statusFilter && `(${statusFilter})`}
              <span>▼</span>
            </button>
            {statusDropdownOpen && (
              <div className="filter-dropdown">
                <button
                  className={`filter-option ${statusFilter === '' ? 'selected' : ''}`}
                  onClick={() => {
                    setStatusFilter('');
                    setStatusDropdownOpen(false);
                    setCurrentPage(1); // Reset to first page when filter changes
                  }}
                >
                  All
                </button>
                {(activeSubTab === 'trips' ? uniqueStatuses : uniqueScheduleStatuses).filter(s => s !== 'All').map((status) => (
                  <button
                    key={status}
                    className={`filter-option ${statusFilter === status ? 'selected' : ''}`}
                    onClick={() => {
                      setStatusFilter(status);
                      setStatusDropdownOpen(false);
                      setCurrentPage(1); // Reset to first page when filter changes
                    }}
                  >
                    {status}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="filter-wrapper" ref={dateRangeDropdownRef}>
            <button
              className={`filter-btn ${dateRangeFilter ? 'active' : ''}`}
              onClick={() => {
                setDateRangeDropdownOpen(!dateRangeDropdownOpen);
                setStatusDropdownOpen(false);
                setDriverDropdownOpen(false);
                setVehicleDropdownOpen(false);
                setAgencyDropdownOpen(false);
              }}
            >
              <span>📅</span> Date Range {dateRangeFilter && `(${dateRangeFilter})`}
              <span>▼</span>
            </button>
            {dateRangeDropdownOpen && (
              <div className="filter-dropdown">
                <button
                  className={`filter-option ${dateRangeFilter === '' ? 'selected' : ''}`}
                  onClick={() => {
                    setDateRangeFilter('');
                    setDateRangeDropdownOpen(false);
                    setCurrentPage(1); // Reset to first page when filter changes
                  }}
                >
                  All Dates
                </button>
                <button
                  className={`filter-option ${dateRangeFilter === 'Today' ? 'selected' : ''}`}
                  onClick={() => {
                    setDateRangeFilter('Today');
                    setDateRangeDropdownOpen(false);
                    setCurrentPage(1); // Reset to first page when filter changes
                  }}
                >
                  Today
                </button>
                <button
                  className={`filter-option ${dateRangeFilter === 'This Week' ? 'selected' : ''}`}
                  onClick={() => {
                    setDateRangeFilter('This Week');
                    setDateRangeDropdownOpen(false);
                    setCurrentPage(1); // Reset to first page when filter changes
                  }}
                >
                  This Week
                </button>
                <button
                  className={`filter-option ${dateRangeFilter === 'This Month' ? 'selected' : ''}`}
                  onClick={() => {
                    setDateRangeFilter('This Month');
                    setDateRangeDropdownOpen(false);
                    setCurrentPage(1); // Reset to first page when filter changes
                  }}
                >
                  This Month
                </button>
              </div>
            )}
          </div>

          <div className="filter-wrapper" ref={driverDropdownRef}>
            <button
              className={`filter-btn ${driverFilter ? 'active' : ''}`}
              onClick={() => {
                setDriverDropdownOpen(!driverDropdownOpen);
                setStatusDropdownOpen(false);
                setDateRangeDropdownOpen(false);
                setVehicleDropdownOpen(false);
                setAgencyDropdownOpen(false);
              }}
            >
              <span>👤</span> Driver {driverFilterDisplay && `(${driverFilterDisplay})`}
              <span>▼</span>
            </button>
            {driverDropdownOpen && (
              <div className="filter-dropdown">
                <button
                  className={`filter-option ${driverFilter === '' ? 'selected' : ''}`}
                  onClick={() => {
                    setDriverFilter('');
                    setDriverDropdownOpen(false);
                  }}
                >
                  All Drivers
                </button>
                {(activeSubTab === 'trips' ? uniqueDrivers : uniqueScheduleDrivers.map(d => typeof d === 'string' ? { name: d } : d)).map((driver) => {
                  const driverId = driver.id || driver;
                  const driverName = driver.name || driver;
                  return (
                  <button
                      key={driverId}
                      className={`filter-option ${driverFilter === driverId ? 'selected' : ''}`}
                    onClick={() => {
                        setDriverFilter(driverId);
                      setDriverDropdownOpen(false);
                      setCurrentPage(1); // Reset to first page when filter changes
                    }}
                  >
                      {driverName}
                  </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="filter-wrapper" ref={vehicleDropdownRef}>
            <button
              className={`filter-btn ${vehicleFilter ? 'active' : ''}`}
              onClick={() => {
                setVehicleDropdownOpen(!vehicleDropdownOpen);
                setStatusDropdownOpen(false);
                setDateRangeDropdownOpen(false);
                setDriverDropdownOpen(false);
                setAgencyDropdownOpen(false);
              }}
            >
              <span>🚚</span> Vehicle {vehicleFilterDisplay && `(${vehicleFilterDisplay})`}
              <span>▼</span>
            </button>
            {vehicleDropdownOpen && (
              <div className="filter-dropdown">
                <button
                  className={`filter-option ${vehicleFilter === '' ? 'selected' : ''}`}
                  onClick={() => {
                    setVehicleFilter('');
                    setVehicleDropdownOpen(false);
                    setCurrentPage(1); // Reset to first page when filter changes
                  }}
                >
                  All Vehicles
                </button>
                {(activeSubTab === 'trips' ? uniqueVehicles : uniqueScheduleVehicles.map(v => typeof v === 'string' ? { number: v } : v)).map((vehicle) => {
                  const vehicleId = vehicle.id || vehicle;
                  const vehicleNumber = vehicle.number || vehicle;
                  return (
                  <button
                      key={vehicleId}
                      className={`filter-option ${vehicleFilter === vehicleId ? 'selected' : ''}`}
                    onClick={() => {
                        setVehicleFilter(vehicleId);
                      setVehicleDropdownOpen(false);
                      setCurrentPage(1); // Reset to first page when filter changes
                    }}
                  >
                      {vehicleNumber}
                  </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="filter-wrapper" ref={agencyDropdownRef}>
            <button
              className={`filter-btn ${agencyFilter ? 'active' : ''}`}
              onClick={() => {
                setAgencyDropdownOpen(!agencyDropdownOpen);
                setStatusDropdownOpen(false);
                setDateRangeDropdownOpen(false);
                setDriverDropdownOpen(false);
                setVehicleDropdownOpen(false);
              }}
            >
              <span>🏢</span> Transporter {agencyFilterDisplay && `(${agencyFilterDisplay})`}
              <span>▼</span>
            </button>
            {agencyDropdownOpen && (
              <div className="filter-dropdown">
                <button
                  className={`filter-option ${agencyFilter === '' ? 'selected' : ''}`}
                  onClick={() => {
                    setAgencyFilter('');
                    setAgencyDropdownOpen(false);
                    setCurrentPage(1); // Reset to first page when filter changes
                  }}
                >
                  All Transporters
                </button>
                {uniqueAgencies.map((agency) => {
                  const agencyId = agency.id;
                  const agencyName = agency.name;
                  return (
                <button
                      key={agencyId}
                      className={`filter-option ${agencyFilter === agencyId ? 'selected' : ''}`}
                  onClick={() => {
                        setAgencyFilter(agencyId);
                    setAgencyDropdownOpen(false);
                    setCurrentPage(1); // Reset to first page when filter changes
                  }}
                >
                      {agencyName}
                </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
        ) : null}
        <div className="trip-actions">
          {activeSubTab === 'trips' ? (
            <>
              <button className="btn-primary" onClick={handleAddTrip}>
                <span>+</span> Add Trip
              </button>
              <button className="btn-secondary" onClick={handleExport} title="Export">
                <span>📤</span>
              </button>
            </>
          ) : (
            <button className="btn-primary" onClick={handleAddSchedule}>
              <span>+</span> Add Trip Schedule
            </button>
          )}
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="trip-sub-tabs">
        <button
          className={`trip-sub-tab ${activeSubTab === 'trips' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('trips')}
        >
          Trips
        </button>
        <button
          className={`trip-sub-tab ${activeSubTab === 'trip-schedule' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('trip-schedule')}
        >
          Trip Schedule
        </button>
      </div>

      {/* Trips Tab Content */}
      {activeSubTab === 'trips' && (
        <>
      {/* Status Tabs */}
      <div className="trip-tabs">
        <button
          className={`trip-tab ${activeTab === 'all trips' ? 'active' : ''}`}
          onClick={() => setActiveTab('all trips')}
        >
          All Trips
        </button>
        <button
          className={`trip-tab ${activeTab === "today's trips" ? 'active' : ''}`}
          onClick={() => setActiveTab("today's trips")}
        >
          Today's Trips
        </button>
        <button
          className={`trip-tab ${activeTab === 'delayed trips' ? 'active' : ''}`}
          onClick={() => setActiveTab('delayed trips')}
        >
          Delayed Trips
        </button>
        <button
          className={`trip-tab ${activeTab === 'upcoming departures' ? 'active' : ''}`}
          onClick={() => setActiveTab('upcoming departures')}
        >
          Upcoming Departures
        </button>
        <button
          className={`trip-tab ${activeTab === 'recent completions' ? 'active' : ''}`}
          onClick={() => setActiveTab('recent completions')}
        >
          Recent Completions
        </button>
      </div>

      {/* Show Entries Filter */}
      <div className="show-entries-wrapper">
        <div className="show-entries-left">
          <span className="show-entries-label">Show</span>
          <div className="filter-wrapper" ref={showEntriesDropdownRef}>
            <button
              className="show-entries-btn"
              onClick={() => setShowEntriesDropdownOpen(!showEntriesDropdownOpen)}
            >
              <span>{itemsPerPage}</span>
              <span>▼</span>
            </button>
            {showEntriesDropdownOpen && (
              <div className="filter-dropdown">
                {[10, 25, 50, 100, 500].map((num) => (
                  <button
                    key={num}
                    className={`filter-option ${itemsPerPage === num ? 'selected' : ''}`}
                    onClick={() => {
                      setItemsPerPage(num);
                      setShowEntriesDropdownOpen(false);
                      setCurrentPage(1);
                    }}
                  >
                    {num}
                  </button>
                ))}
              </div>
            )}
          </div>
          <span className="show-entries-label">entries</span>
        </div>
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search trips..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1); // Reset to first page when search changes
            }}
          />
          <span className="search-icon">🔍</span>
        </div>
      </div>

      {/* Table Section */}
      <div className="trip-table-container">
        <table className="trip-table">
          <thead>
            <tr>
              <th>Trip ID</th>
              <th>Trip Date & Time</th>
              <th>Transporter</th>
              <th>Vehicle Number</th>
              <th>Driver Name</th>
              <th>Route</th>
              <th>Stops</th>
              <th>Load Weight</th>
              <th>Trip Status</th>
              <th>ETA / Duration</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentTrips.map((trip) => (
              <tr key={trip.id || trip.tripId}>
                <td>{trip.tripId || trip.id || 'N/A'}</td>
                <td>{trip.tripDateTime || 'N/A'}</td>
                <td>{trip?.agency || 'N/A'}</td>
                <td>{trip.vehicleNumber || 'N/A'}</td>
                <td>{trip.driverName || 'N/A'}</td>
                <td>{trip.route}</td>
                <td>
                  <span 
                    className="stops-link" 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('Admin Trip Management: Stops link clicked for trip:', trip);
                      handleStopsClick(trip);
                    }}
                    style={{ cursor: 'pointer', color: '#2563EB', textDecoration: 'underline' }}
                  >
                    {trip.stops || 0} Stops
                  </span>
                </td>
                <td>{trip.loadWeight ? trip.loadWeight.toLocaleString() : '0'} {trip.weightUnit || 'kg'}</td>
                <td>
                  <span className={`status-badge ${getStatusClass(trip.status)}`}>
                    {trip.status}
                  </span>
                </td>
                <td>{trip.eta}</td>
                <td>
                  <div className="action-buttons">
                    <button className="action-btn view-btn" title="View" onClick={() => handleViewClick(trip)}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 3C4.5 3 1.73 5.11 0 8C1.73 10.89 4.5 13 8 13C11.5 13 14.27 10.89 16 8C14.27 5.11 11.5 3 8 3ZM8 11C6.34 11 5 9.66 5 8C5 6.34 6.34 5 8 5C9.66 5 11 6.34 11 8C11 9.66 9.66 11 8 11ZM8 6.5C7.17 6.5 6.5 7.17 6.5 8C6.5 8.83 7.17 9.5 8 9.5C8.83 9.5 9.5 8.83 9.5 8C9.5 7.17 8.83 6.5 8 6.5Z" fill="#2563EB"/>
                      </svg>
                    </button>
                    <button className="action-btn edit-btn" title="Edit" onClick={() => handleEditClick(trip)}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.3333 1.33333C11.5084 1.15822 11.7163 1.01898 11.9454 0.924232C12.1745 0.829488 12.4199 0.781332 12.6667 0.781332C12.9134 0.781332 13.1588 0.829488 13.3879 0.924232C13.617 1.01898 13.8249 1.15822 14 1.33333C14.1751 1.50844 14.3143 1.71631 14.4091 1.94541C14.5038 2.17451 14.552 2.41992 14.552 2.66667C14.552 2.91341 14.5038 3.15882 14.4091 3.38792C14.3143 3.61702 14.1751 3.82489 14 4L4.66667 13.3333L0.666667 14.6667L2 10.6667L11.3333 1.33333Z" stroke="#10B981" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                      </svg>
                    </button>
                    <button className="action-btn cancel-btn" title="Cancel Trip" onClick={() => handleCancelClick(trip)}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1ZM10.7071 10.7071C10.3166 11.0976 9.68342 11.0976 9.29289 10.7071L8 9.41421L6.70711 10.7071C6.31658 11.0976 5.68342 11.0976 5.29289 10.7071C4.90237 10.3166 4.90237 9.68342 5.29289 9.29289L6.58579 8L5.29289 6.70711C4.90237 6.31658 4.90237 5.68342 5.29289 5.29289C5.68342 4.90237 6.31658 4.90237 6.70711 5.29289L8 6.58579L9.29289 5.29289C9.68342 4.90237 10.3166 4.90237 10.7071 5.29289C11.0976 5.68342 11.0976 6.31658 10.7071 6.70711L9.41421 8L10.7071 9.29289C11.0976 9.68342 11.0976 10.3166 10.7071 10.7071Z" fill="#F59E0B" stroke="#F59E0B" strokeWidth="0.5"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

          {/* Pagination */}
          <div className="trip-pagination">
            <div className="pagination-info">
              Showing {startIndex + 1} to {Math.min(endIndex, filteredTrips.length)} of {filteredTrips.length} results
            </div>
            <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            ←
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          ))}
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            →
          </button>
            </div>
          </div>
        </>
      )}

      {/* Trip Schedule Tab Content */}
      {activeSubTab === 'trip-schedule' && (
        <>
          {/* Status Tabs */}
          <div className="trip-tabs">
            <button
              className={`trip-tab ${activeTab === 'all trips' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('all trips');
                setCurrentPage(1); // Reset to first page when tab changes
              }}
            >
              All Trips
            </button>
            <button
              className={`trip-tab ${activeTab === "today's trips" ? 'active' : ''}`}
              onClick={() => {
                setActiveTab("today's trips");
                setCurrentPage(1); // Reset to first page when tab changes
              }}
            >
              Today's Trips
            </button>
            <button
              className={`trip-tab ${activeTab === 'delayed trips' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('delayed trips');
                setCurrentPage(1); // Reset to first page when tab changes
              }}
            >
              Delayed Trips
            </button>
            <button
              className={`trip-tab ${activeTab === 'upcoming departures' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('upcoming departures');
                setCurrentPage(1); // Reset to first page when tab changes
              }}
            >
              Upcoming Departures
            </button>
            <button
              className={`trip-tab ${activeTab === 'recent completions' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('recent completions');
                setCurrentPage(1); // Reset to first page when tab changes
              }}
            >
              Recent Completions
            </button>
          </div>

          {/* Show Entries Filter */}
          <div className="show-entries-wrapper">
            <div className="show-entries-left">
              <span className="show-entries-label">Show</span>
              <div className="filter-wrapper" ref={showEntriesDropdownRef}>
                <button
                  className="show-entries-btn"
                  onClick={() => setShowEntriesDropdownOpen(!showEntriesDropdownOpen)}
                >
                  <span>{itemsPerPage}</span>
                  <span>▼</span>
                </button>
                {showEntriesDropdownOpen && (
                  <div className="filter-dropdown">
                    {[10, 25, 50, 100, 500].map((num) => (
                      <button
                        key={num}
                        className={`filter-option ${itemsPerPage === num ? 'selected' : ''}`}
                        onClick={() => {
                          setItemsPerPage(num);
                          setShowEntriesDropdownOpen(false);
                          setCurrentPage(1);
                        }}
                      >
                        {num}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <span className="show-entries-label">entries</span>
            </div>
            <div className="search-wrapper">
              <input
                type="text"
                className="search-input"
                placeholder="Search by schedule name, route, vehicle..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <span className="search-icon">🔍</span>
            </div>
          </div>

          {/* Table Section */}
          <div className="trip-table-container">
            <table className="trip-table">
              <thead>
                <tr>
                  <th>Schedule ID</th>
                  <th>Schedule Name</th>
                  <th>Schedule Type</th>
                  <th>Route</th>
                  <th>Stops</th>
                  <th>Vehicle Number</th>
                  <th>Driver Name</th>
                  <th>Time</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSchedules.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="no-data">
                      No trip schedules found. Click "Add Trip Schedule" to create one.
                    </td>
                  </tr>
                ) : (
                  currentSchedules.map((schedule) => (
                    <tr key={schedule.scheduleId}>
                      <td>{schedule.scheduleId}</td>
                      <td>{schedule.scheduleName}</td>
                      <td>
                        <span className="schedule-type-badge">
                          {schedule.scheduleType === 'daily' ? '🔄 Daily' : 
                           schedule.scheduleType === 'weekly' ? '📅 Weekly' : 
                           '📆 Monthly'}
                        </span>
                      </td>
                      <td>{schedule.routeName || `${schedule.startLocation} → ${schedule.destination}`}</td>
                      <td>
                        <span 
                          className="stops-link" 
                          onClick={() => {
                            // Always allow clicking to fetch and show stops
                            const stopsCount = (schedule.stops && Array.isArray(schedule.stops)) 
                              ? schedule.stops.length 
                              : (schedule.stops_count || 0);
                              handleStopsClick({ 
                                ...schedule, 
                              scheduleId: schedule.scheduleId,
                              schedule_id: schedule.scheduleId,
                              stops: stopsCount,
                              stopsData: schedule.stops || []
                              });
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          {(() => {
                            // Check multiple sources for stops count
                            const stopsFromArray = (schedule.stops && Array.isArray(schedule.stops)) ? schedule.stops.length : 0;
                            const stopsFromData = (schedule.stopsData && Array.isArray(schedule.stopsData)) ? schedule.stopsData.length : 0;
                            const stopsFromCount = schedule.stops_count || 0;
                            const finalCount = stopsFromArray || stopsFromData || stopsFromCount;
                            console.log(`Schedule ${schedule.scheduleId} stops display:`, {
                              stopsArray: schedule.stops,
                              stopsData: schedule.stopsData,
                              stopsCount: schedule.stops_count,
                              finalCount
                            });
                            return finalCount;
                          })()} Stops
                        </span>
                      </td>
                      <td>{schedule.vehicle || 'Not assigned'}</td>
                      <td>{schedule.assignedDriver || 'Not assigned'}</td>
                      <td>{schedule.tripDateTime || 'Not set'}</td>
                      <td>
                        <span className={`status-badge ${getStatusClass(schedule.tripStatus)}`}>
                          {schedule.tripStatus}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button className="action-btn view-btn" title="View" onClick={() => handleScheduleViewClick(schedule)}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M8 3C4.5 3 1.73 5.11 0 8C1.73 10.89 4.5 13 8 13C11.5 13 14.27 10.89 16 8C14.27 5.11 11.5 3 8 3ZM8 11C6.34 11 5 9.66 5 8C5 6.34 6.34 5 8 5C9.66 5 11 6.34 11 8C11 9.66 9.66 11 8 11ZM8 6.5C7.17 6.5 6.5 7.17 6.5 8C6.5 8.83 7.17 9.5 8 9.5C8.83 9.5 9.5 8.83 9.5 8C9.5 7.17 8.83 6.5 8 6.5Z" fill="#2563EB"/>
                            </svg>
                          </button>
                          <button className="action-btn edit-btn" title="Edit" onClick={() => handleScheduleEditClick(schedule)}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M11.3333 1.33333C11.5084 1.15822 11.7163 1.01898 11.9454 0.924232C12.1745 0.829488 12.4199 0.781332 12.6667 0.781332C12.9134 0.781332 13.1588 0.829488 13.3879 0.924232C13.617 1.01898 13.8249 1.15822 14 1.33333C14.1751 1.50844 14.3143 1.71631 14.4091 1.94541C14.5038 2.17451 14.552 2.41992 14.552 2.66667C14.552 2.91341 14.5038 3.15882 14.4091 3.38792C14.3143 3.61702 14.1751 3.82489 14 4L4.66667 13.3333L0.666667 14.6667L2 10.6667L11.3333 1.33333Z" stroke="#10B981" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="trip-pagination">
            <div className="pagination-info">
              Showing {filteredSchedules.length > 0 ? ((currentPage - 1) * itemsPerPage + 1) : 0} to {Math.min(currentPage * itemsPerPage, totalSchedules)} of {totalSchedules} results
            </div>
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                ←
              </button>
              {Array.from({ length: totalSchedulePages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              ))}
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalSchedulePages}
              >
                →
              </button>
            </div>
          </div>
        </>
      )}

      {/* Add Trip Modal */}
      {showAddModal && (
        <div className="add-vehicle-modal-overlay" onClick={handleCloseModal}>
          <div className="add-vehicle-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-bar">
              <h2 className="modal-title">Add Trip</h2>
              <button className="modal-close-btn" onClick={handleCloseModal}>
                ✕
              </button>
            </div>

            <div className="modal-body-content">
              <div className="form-tabs">
                <button className="form-tab active">Trip Details</button>
              </div>

              <div className="form-content-trip">
                {/* Form Fields */}
                <div className="form-fields-column">
                  {/* Trip Information Section */}
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333', marginBottom: '8px', marginTop: '0' }}>Trip Information</h3>
                  
                  <div className="form-field">
                    <label className="form-field-label">
                      <span>🆔</span> Trip ID (auto-generated)
                    </label>
                    <input
                      type="text"
                      className="form-input-field"
                      value={formData.tripId}
                      readOnly
                      style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                    />
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>📅</span> Trip Date & Time <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      className={`form-input-field ${formErrors.tripDateTime ? 'error' : ''}`}
                      value={formData.tripDateTime}
                      onChange={(e) => {
                        setFormData({...formData, tripDateTime: e.target.value});
                        if (formErrors.tripDateTime) {
                          setFormErrors({...formErrors, tripDateTime: ''});
                        }
                      }}
                    />
                    {formErrors.tripDateTime && <span className="error-message">{formErrors.tripDateTime}</span>}
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>📊</span> Trip Status <span className="required-asterisk">*</span>
                    </label>
                    <select
                      className={`form-input-field ${formErrors.tripStatus ? 'error' : ''}`}
                      value={formData.tripStatus}
                      onChange={(e) => {
                        setFormData({...formData, tripStatus: e.target.value});
                        if (formErrors.tripStatus) {
                          setFormErrors({...formErrors, tripStatus: ''});
                        }
                      }}
                    >
                      <option value="Scheduled">Scheduled</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Completed">Completed</option>
                      <option value="Cancelled">Cancelled</option>
                      <option value="Delayed">Delayed</option>
                    </select>
                    {formErrors.tripStatus && <span className="error-message">{formErrors.tripStatus}</span>}
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>🏢</span> Client / Transporter Name <span className="required-asterisk">*</span>
                    </label>
                    <select
                      className={`form-input-field ${formErrors.clientAgency ? 'error' : ''}`}
                      value={formData.clientAgency}
                      onChange={(e) => {
                        setFormData({...formData, clientAgency: e.target.value});
                        if (formErrors.clientAgency) {
                          setFormErrors({...formErrors, clientAgency: ''});
                        }
                      }}
                    >
                      <option value="">Select client or transporter</option>
                      {allAgenciesForFilter.map((agency) => {
                        const agencyName = agency.name || agency.agency_name || '';
                        const agencyId = agency.id || agency.agency_id;
                        return (
                          <option key={agencyId || agencyName} value={agencyName}>
                            {agencyName}
                          </option>
                        );
                      })}
                    </select>
                    {formErrors.clientAgency && <span className="error-message">{formErrors.clientAgency}</span>}
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>👤</span> Customer
                    </label>
                    <div style={{ position: 'relative' }} ref={customerInputRef}>
                      <input
                        type="text"
                        className="form-input-field"
                        placeholder="Search or select customer"
                        value={customerSearch || (formData.customer ? allCustomersForFilter.find(c => c.id === formData.customer || c.id?.toString() === formData.customer?.toString())?.name || '' : '')}
                        onChange={(e) => {
                          setCustomerSearch(e.target.value);
                          setCustomerDropdownOpen(true);
                          if (!e.target.value) {
                            setFormData({...formData, customer: ''});
                          }
                        }}
                        onFocus={() => {
                          setCustomerDropdownOpen(true);
                          if (!customerSearch && formData.customer) {
                            const selectedCustomer = allCustomersForFilter.find(c => c.id === formData.customer || c.id?.toString() === formData.customer?.toString());
                            if (selectedCustomer) {
                              setCustomerSearch(selectedCustomer.name || '');
                            }
                          }
                        }}
                        style={{ cursor: 'text' }}
                      />
                      {customerDropdownOpen && allCustomersForFilter && allCustomersForFilter.length > 0 && (
                        <div
                          ref={customerDropdownRef}
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            zIndex: 1000,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            marginTop: '4px'
                          }}
                        >
                          {allCustomersForFilter
                            .filter(customer => {
                              const customerName = customer.name || customer.customer_name || '';
                              if (!customerName || !customer.id) return false;
                              if (!customerSearch) return true;
                              return customerName.toLowerCase().includes(customerSearch.toLowerCase());
                            })
                            .map((customer) => {
                              const customerName = customer.name || customer.customer_name || '';
                              const customerId = customer.id;
                              return (
                                <div
                                  key={customerId || customerName}
                                  onClick={() => {
                                    setFormData({...formData, customer: customerId});
                                    setCustomerSearch(customerName);
                                    setCustomerDropdownOpen(false);
                                  }}
                                  style={{
                                    padding: '10px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #eee',
                                    backgroundColor: formData.customer === customerId || formData.customer?.toString() === customerId?.toString() ? '#f0f7ff' : 'white'
                                  }}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = formData.customer === customerId || formData.customer?.toString() === customerId?.toString() ? '#f0f7ff' : 'white'}
                                >
                                  {customerName}
                                </div>
                              );
                            })}
                          {allCustomersForFilter.filter(customer => {
                            const customerName = customer.name || customer.customer_name || '';
                            if (!customerName || !customer.id) return false;
                            if (!customerSearch) return true;
                            return customerName.toLowerCase().includes(customerSearch.toLowerCase());
                          }).length === 0 && (
                            <div style={{ padding: '10px', color: '#999', textAlign: 'center' }}>
                              No customers found
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-field full-width">
                    <label className="form-field-label">
                      <span>📝</span> Notes / Special Instructions
                    </label>
                    <textarea
                      className="form-input-field"
                      placeholder="Enter any special instructions or notes for this trip"
                      rows="3"
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      style={{ resize: 'vertical', minHeight: '80px' }}
                    />
                  </div>

                  {/* Vehicle & Driver Assignment Section */}
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333', marginBottom: '8px', marginTop: '24px' }}>Vehicle & Driver Assignment</h3>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>🚚</span> Vehicle <span className="required-asterisk">*</span>
                    </label>
                    <select
                      className={`form-input-field ${formErrors.vehicle ? 'error' : ''}`}
                      value={formData.vehicle}
                      onChange={(e) => handleVehicleChange(e.target.value)}
                    >
                      <option value="">Select vehicle</option>
                      {allVehiclesForFilter.map((vehicle) => {
                        const vehicleNumber = vehicle.number || vehicle.vehicle_number || '';
                        const vehicleId = vehicle.id || vehicle.vehicle_id;
                        return (
                          <option key={vehicleId || vehicleNumber} value={vehicleNumber}>
                            {vehicleNumber}
                        </option>
                        );
                      })}
                    </select>
                    {formErrors.vehicle && <span className="error-message">{formErrors.vehicle}</span>}
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>👤</span> Assigned Driver <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input-field ${formErrors.assignedDriver ? 'error' : ''}`}
                      placeholder="Select vehicle to auto-populate driver"
                      value={formData.assignedDriver}
                      readOnly
                      style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                    />
                    {formErrors.assignedDriver && <span className="error-message">{formErrors.assignedDriver}</span>}
                  </div>

                  {/* Route Details Section */}
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333', marginBottom: '8px', marginTop: '24px' }}>Route Details</h3>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>🛣️</span> Route Name
                    </label>
                    <input
                      type="text"
                      className="form-input-field"
                      placeholder="Enter route name"
                      value={formData.routeName}
                      onChange={(e) => setFormData({ ...formData, routeName: e.target.value })}
                    />
                  </div>

                  <div className="form-field" style={{ position: 'relative' }}>
                    <label className="form-field-label">
                      <span>📍</span> Start Location
                    </label>
                    <input
                      ref={startLocationAutocomplete.inputRef}
                      type="text"
                      className="form-input-field"
                      placeholder="Search location (e.g., Mumbai, Maharashtra)"
                      value={formData.startLocation || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only update if the value is actually different to prevent truncation
                        if (value !== formData.startLocation) {
                          handleStartLocationInput(value);
                        }
                      }}
                      onBlur={(e) => {
                        // Ensure the value is preserved on blur
                        if (e.target.value && e.target.value !== formData.startLocation) {
                          setFormData(prev => ({ ...prev, startLocation: e.target.value }));
                          setStartLocationSearch(e.target.value);
                        }
                      }}
                      onFocus={() => {
                        if (startLocationSearch.trim().length > 0) {
                          const searchLower = startLocationSearch.toLowerCase();
                          const hasMatches = indianCities.some(city => city.toLowerCase().includes(searchLower));
                          setStartLocationSuggestionsOpen(hasMatches);
                        }
                      }}
                    />
                    {/* Only show city dropdown if Google Maps is not loaded (fallback) */}
                    {!isGoogleMapsLoaded && startLocationSuggestionsOpen && filteredStartCities.length > 0 && (
                      <div
                        ref={startLocationSuggestionsRef}
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: 'white',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          zIndex: 1000,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          marginTop: '4px'
                        }}
                      >
                        {filteredStartCities.map((city, index) => (
                          <div
                            key={index}
                            onClick={() => handleStartLocationCitySelect(city)}
                            style={{
                              padding: '10px',
                              cursor: 'pointer',
                              borderBottom: index < filteredStartCities.length - 1 ? '1px solid #eee' : 'none'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                          >
                            {city}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="form-field" style={{ position: 'relative' }}>
                    <label className="form-field-label">
                      <span>📍</span> Destination
                    </label>
                    <input
                      ref={destinationAutocomplete.inputRef}
                      type="text"
                      className="form-input-field"
                      placeholder="Search location (e.g., Delhi, Delhi)"
                      value={formData.destination || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only update if the value is actually different to prevent truncation
                        if (value !== formData.destination) {
                          setDestinationSearch(value);
                          setFormData(prev => ({ ...prev, destination: value }));
                        }
                      }}
                      onBlur={(e) => {
                        // Ensure the value is preserved on blur
                        if (e.target.value && e.target.value !== formData.destination) {
                          setFormData(prev => ({ ...prev, destination: e.target.value }));
                          setDestinationSearch(e.target.value);
                        }
                      }}
                      onFocus={() => {
                        if (destinationSearch.trim().length > 0) {
                          const searchLower = destinationSearch.toLowerCase();
                          const hasMatches = indianCities.some(city => city.toLowerCase().includes(searchLower));
                          setDestinationSuggestionsOpen(hasMatches);
                        }
                      }}
                    />
                    {/* Only show city dropdown if Google Maps is not loaded (fallback) */}
                    {!isGoogleMapsLoaded && destinationSuggestionsOpen && filteredDestinationCities.length > 0 && (
                      <div
                        ref={destinationSuggestionsRef}
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: 'white',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          zIndex: 1000,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          marginTop: '4px'
                        }}
                      >
                        {filteredDestinationCities.map((city, index) => (
                          <div
                            key={index}
                            onClick={() => handleDestinationCitySelect(city)}
                            style={{
                              padding: '10px',
                              cursor: 'pointer',
                              borderBottom: index < filteredDestinationCities.length - 1 ? '1px solid #eee' : 'none'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                          >
                            {city}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Intermediate Stops Section */}
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333', marginBottom: '8px', marginTop: '24px' }}>Intermediate Stops</h3>

                  {formData.stops.map((stop, index) => (
                    <div key={index} className="stop-item">
                      <div className="stop-drag-handle">⋮⋮</div>
                      <div className="stop-fields">
                        <div className="form-field" style={{ position: 'relative' }}>
                          <label className="form-field-label">Location</label>
                          <input
                            ref={el => {
                              if (el) {
                                stopLocationInputRefs.current[index] = { current: el };
                                // Initialize autocomplete if Google Maps is loaded
                                if (isGoogleMapsLoaded) {
                                  setTimeout(() => initializeStopAutocomplete(index, el), 100);
                                }
                              }
                            }}
                            type="text"
                            className="form-input-field"
                            placeholder="Search location (e.g., Mumbai, Maharashtra)"
                            value={stop.location || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Only update if the value is actually different to prevent truncation
                              if (value !== stop.location) {
                                handleStopLocationInput(index, value);
                              }
                            }}
                            onBlur={(e) => {
                              // Ensure the value is preserved on blur
                              if (e.target.value && e.target.value !== stop.location) {
                                handleStopChange(index, 'location', e.target.value);
                                setStopLocationSearches(prev => ({ ...prev, [index]: e.target.value }));
                              }
                            }}
                            onFocus={() => {
                              if (!isGoogleMapsLoaded && stopLocationSearches[index] && stopLocationSearches[index].trim().length > 0) {
                                const searchLower = stopLocationSearches[index].toLowerCase();
                                const hasMatches = indianCities.some(city => city.toLowerCase().includes(searchLower));
                                setStopLocationSuggestionsOpen(prev => ({ ...prev, [index]: hasMatches }));
                              }
                            }}
                          />
                          {!isGoogleMapsLoaded && stopLocationSuggestionsOpen[index] && filteredStopCities(index).length > 0 && (
                            <div
                              ref={el => {
                                if (el) stopLocationSuggestionsRefs.current[index] = el;
                              }}
                              style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                backgroundColor: 'white',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                maxHeight: '200px',
                                overflowY: 'auto',
                                zIndex: 1000,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                marginTop: '4px'
                              }}
                            >
                              {filteredStopCities(index).map((city) => (
                                <div
                                  key={city}
                                  style={{ padding: '10px 15px', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                                  onClick={() => handleStopLocationCitySelect(index, city)}
                                >
                                  {city}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="stop-grid">
                          <div className="form-field">
                            <label className="form-field-label">Estimated Date</label>
                            <input
                              type="datetime-local"
                              className="form-input-field"
                              value={stop.estimatedDate || ''}
                              onChange={(e) => handleStopChange(index, 'estimatedDate', e.target.value)}
                            />
                          </div>
                          <div className="form-field">
                            <label className="form-field-label">Purpose</label>
                            <select
                              className="form-input-field"
                              value={stop.purpose}
                              onChange={(e) => handleStopChange(index, 'purpose', e.target.value)}
                            >
                              <option value="">Select purpose</option>
                              <option value="Checkpoint">Checkpoint</option>
                              <option value="Break">Break</option>
                              <option value="Loading">Loading</option>
                              <option value="Unloading">Unloading</option>
                              <option value="Inspection">Inspection</option>
                            </select>
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="stop-delete-btn"
                        onClick={() => handleRemoveStop(index)}
                        title="Delete stop"
                      >
                        🗑️
                      </button>
                    </div>
                  ))}

                  <button
                    type="button"
                    className="add-stop-btn"
                    onClick={handleAddStop}
                  >
                    + Add Stop
                  </button>

                  {/* Load Details Section */}
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333', marginBottom: '8px', marginTop: '24px' }}>Load Details</h3>

                      <div className="form-field">
                        <label className="form-field-label">
                          <span>📊</span> Total Capacity (tons) <span className="required-asterisk">*</span>
                        </label>
                        <input
                          type="number"
                          className={`form-input-field ${formErrors.totalCapacity ? 'error' : ''}`}
                          placeholder="Enter total capacity"
                          value={formData.totalCapacity}
                          onChange={(e) => {
                            setFormData({...formData, totalCapacity: e.target.value});
                            if (formErrors.totalCapacity) {
                              setFormErrors({...formErrors, totalCapacity: ''});
                            }
                          }}
                          min="0"
                          step="0.01"
                        />
                        {formErrors.totalCapacity && <span className="error-message">{formErrors.totalCapacity}</span>}
                      </div>

                      <div className="form-field">
                        <label className="form-field-label">
                          <span>📦</span> Used Capacity (tons) <span className="required-asterisk">*</span>
                        </label>
                        <input
                          type="number"
                          className={`form-input-field ${formErrors.usedCapacity ? 'error' : ''}`}
                          placeholder="Enter used capacity"
                          value={formData.usedCapacity}
                          onChange={(e) => {
                            setFormData({...formData, usedCapacity: e.target.value});
                            if (formErrors.usedCapacity) {
                              setFormErrors({...formErrors, usedCapacity: ''});
                            }
                          }}
                          min="0"
                          step="0.01"
                        />
                        {formErrors.usedCapacity && <span className="error-message">{formErrors.usedCapacity}</span>}
                      </div>

                  <div className="form-field full-width">
                    <label className="form-field-label">
                      <span>📝</span> Load Description
                    </label>
                    <textarea
                      className="form-input-field"
                      placeholder="Enter load description"
                      rows="3"
                      value={formData.loadDescription}
                      onChange={(e) => setFormData({...formData, loadDescription: e.target.value})}
                      style={{ resize: 'vertical', minHeight: '80px' }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer-bar">
              <button className="modal-btn-cancel" onClick={handleCloseModal}>
                Cancel
              </button>
              <button className="modal-btn-save" onClick={async () => {
                // Basic validation
                const errors = {};
                if (!formData.tripDateTime) errors.tripDateTime = 'Trip date and time is required';
                if (!formData.clientAgency) errors.clientAgency = 'Transporter is required';
                if (!formData.vehicle) errors.vehicle = 'Vehicle is required';
                if (!formData.assignedDriver) errors.assignedDriver = 'Driver is required';
                if (!formData.startLocation) errors.startLocation = 'Start location is required';
                if (!formData.destination) errors.destination = 'Destination is required';
                
                if (Object.keys(errors).length > 0) {
                  setFormErrors(errors);
                  return;
                }
                
                try {
                  // Find agency ID from agency name
                  let agencyId = null;
                  if (allAgenciesForFilter && allAgenciesForFilter.length > 0) {
                    const agency = allAgenciesForFilter.find(a => 
                      (a.name || a.agency_name || '').toString() === formData.clientAgency.toString() ||
                      (a.id && a.id.toString() === formData.clientAgency.toString())
                    );
                    if (agency) {
                      agencyId = agency.id;
                    }
                  }
                  
                  if (!agencyId) {
                    alert('Transporter not found. Please select a valid transporter.');
                    return;
                  }

                  // Find vehicle ID from vehicle number
                  let vehicleId = null;
                  if (allVehiclesForFilter && allVehiclesForFilter.length > 0) {
                    const vehicle = allVehiclesForFilter.find(v => 
                      (v.number || v.vehicle_number || '').toString() === formData.vehicle.toString() ||
                      (v.id && v.id.toString() === formData.vehicle.toString())
                    );
                    if (vehicle) {
                      vehicleId = vehicle.id;
                    }
                  }
                  
                  if (!vehicleId) {
                    alert('Vehicle not found. Please select a valid vehicle.');
                    return;
                  }

                  // Find driver ID from driver name
                  let driverId = null;
                  if (allDriversForFilter && allDriversForFilter.length > 0) {
                    const driver = allDriversForFilter.find(d => 
                      (d.name || d.full_name || '').toString() === formData.assignedDriver.toString() ||
                      (d.id && d.id.toString() === formData.assignedDriver.toString())
                    );
                    if (driver) {
                      driverId = driver.id;
                    }
                  }
                  
                  if (!driverId && formData.assignedDriver) {
                    try {
                      const driverResponse = await driversService.getDrivers({ limit: 1000 });
                      let driversList = [];
                      if (Array.isArray(driverResponse.data)) {
                        driversList = driverResponse.data;
                      } else if (driverResponse.data?.items) {
                        driversList = driverResponse.data.items;
                      } else if (Array.isArray(driverResponse)) {
                        driversList = driverResponse;
                      } else if (driverResponse.items) {
                        driversList = driverResponse.items;
                      }
                      
                      const driver = driversList.find(d => 
                        (d.full_name || d.name || d.driver_name || '') === formData.assignedDriver ||
                        (d.id && d.id.toString() === formData.assignedDriver) ||
                        (d.driver_id && d.driver_id.toString() === formData.assignedDriver)
                      );
                      
                      if (driver) {
                        driverId = driver.id || driver.driver_id;
                      }
                    } catch (err) {
                      console.error('Error fetching drivers:', err);
                    }
                  }
                  
                  if (!driverId) {
                    alert('Driver not found. Please select a valid driver.');
                    return;
                  }

                  // Convert datetime to ISO format
                  let tripDateTime = formData.tripDateTime;
                  if (tripDateTime && !tripDateTime.includes('T')) {
                    tripDateTime = tripDateTime + 'T00:00:00';
                  }
                  if (tripDateTime) {
                    const date = new Date(tripDateTime);
                    if (!isNaN(date.getTime())) {
                      tripDateTime = date.toISOString();
                    }
                  }

                  // Get customer_id from selected customer
                  let customerId = null;
                  if (formData.customer) {
                    const customer = allCustomersForFilter.find(c => 
                      (c.id && c.id.toString() === formData.customer.toString()) ||
                      (c.name && c.name === formData.customer)
                    );
                    if (customer) {
                      customerId = customer.id;
                    }
                  }

                  // Prepare trip data for API
                  const tripData = {
                    trip_date_time: tripDateTime,
                    trip_status: formData.tripStatus || 'Scheduled',
                    agency_id: agencyId,
                    client_agency: formData.clientAgency || '',
                    customer_id: customerId || null,
                    vehicle_id: vehicleId,
                    assigned_driver_id: driverId,
                    route_name: formData.routeName || null, // Route name
                    start_location: formData.startLocation || '',
                    destination: formData.destination || '',
                    load_type: formData.loadType || null,
                    total_capacity: formData.totalCapacity ? parseFloat(formData.totalCapacity) : null, // Total capacity in tons
                    used_capacity: formData.usedCapacity ? parseFloat(formData.usedCapacity) : null, // Used capacity in tons
                    remaining_capacity: formData.remainingCapacity ? parseFloat(formData.remainingCapacity) : null, // Remaining capacity in tons
                    load_description: formData.loadDescription || null,
                    load_volume: formData.loadVolume ? parseFloat(formData.loadVolume) : null,
                    volume_unit: formData.volumeUnit || 'm³',
                    notes: formData.notes || ''
                  };
                  
                  console.log('Creating trip with data:', tripData);
                  
                  const createResponse = await tripsService.createTrip(tripData);
                  
                  // Get the created trip ID from response
                  const createdTrip = createResponse.data || createResponse;
                  const newTripId = createdTrip.id || createdTrip.trip_id || createdTrip.data?.id;
                  
                  // Create trip stops if any stops are provided
                  if (formData.stops && formData.stops.length > 0 && newTripId) {
                    try {
                      // Create stops with stop_sequence
                      const stopPromises = formData.stops
                        .filter(stop => stop.location && stop.location.trim()) // Only create stops with location
                        .map((stop, index) => {
                          // Convert estimated_date to ISO format if provided
                          let estimatedDate = null;
                          if (stop.estimatedDate) {
                            const date = new Date(stop.estimatedDate);
                            if (!isNaN(date.getTime())) {
                              estimatedDate = date.toISOString();
                            }
                          }
                          
                          const stopData = {
                            trip_id: newTripId,
                            stop_sequence: index + 1,
                            stop_location: stop.location || '',
                            estimated_date: estimatedDate,
                            purpose: stop.purpose || null
                          };
                          
                          return tripsService.createTripStop(stopData);
                        });
                      
                      await Promise.all(stopPromises);
                      console.log('Successfully created trip stops');
                    } catch (stopError) {
                      console.error('Error creating trip stops:', stopError);
                      // Don't fail the whole operation if stops fail, but log it
                      alert('Trip created successfully, but some stops could not be saved. Please edit the trip to add stops.');
                    }
                  }
                  
                  // Refresh trips list
                  await fetchTrips();
                  
                handleCloseModal();
                } catch (err) {
                  setError(err.message);
                  console.error('Error creating trip:', err);
                  alert('Failed to create trip: ' + err.message);
                }
              }}>
                Add Trip
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Trip Modal */}
      {showEditModal && editingTrip && (
        <div className="add-vehicle-modal-overlay" onClick={handleCloseModal}>
          <div className="add-vehicle-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-bar">
              <h2 className="modal-title">Edit Trip</h2>
              <button className="modal-close-btn" onClick={handleCloseModal}>
                ✕
              </button>
            </div>

            <div className="modal-body-content">
              <div className="form-tabs">
                <button className="form-tab active">Trip Details</button>
              </div>

              <div className="form-content-trip">
                {/* Form Fields */}
                <div className="form-fields-column">
                  {/* Trip Information Section */}
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333', marginBottom: '8px', marginTop: '0' }}>Trip Information</h3>
                  
                  <div className="form-field">
                    <label className="form-field-label">
                      <span>🆔</span> Trip ID (auto-generated)
                    </label>
                    <input
                      type="text"
                      className="form-input-field"
                      value={formData.tripId}
                      readOnly
                      style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                    />
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>📅</span> Trip Date & Time <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      className={`form-input-field ${formErrors.tripDateTime ? 'error' : ''}`}
                      value={formData.tripDateTime}
                      onChange={(e) => {
                        setFormData({...formData, tripDateTime: e.target.value});
                        if (formErrors.tripDateTime) {
                          setFormErrors({...formErrors, tripDateTime: ''});
                        }
                      }}
                    />
                    {formErrors.tripDateTime && <span className="error-message">{formErrors.tripDateTime}</span>}
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>📊</span> Trip Status <span className="required-asterisk">*</span>
                    </label>
                    <select
                      className={`form-input-field ${formErrors.tripStatus ? 'error' : ''}`}
                      value={formData.tripStatus}
                      onChange={(e) => {
                        setFormData({...formData, tripStatus: e.target.value});
                        if (formErrors.tripStatus) {
                          setFormErrors({...formErrors, tripStatus: ''});
                        }
                      }}
                    >
                      <option value="Scheduled">Scheduled</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Completed">Completed</option>
                      <option value="Cancelled">Cancelled</option>
                      <option value="Delayed">Delayed</option>
                    </select>
                    {formErrors.tripStatus && <span className="error-message">{formErrors.tripStatus}</span>}
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>🏢</span> Client / Transporter Name <span className="required-asterisk">*</span>
                    </label>
                    <select
                      className={`form-input-field ${formErrors.clientAgency ? 'error' : ''}`}
                      value={formData.clientAgency}
                      onChange={(e) => {
                        setFormData({...formData, clientAgency: e.target.value});
                        if (formErrors.clientAgency) {
                          setFormErrors({...formErrors, clientAgency: ''});
                        }
                      }}
                    >
                      <option value="">Select client or transporter</option>
                      {allAgenciesForFilter.map((agency) => {
                        const agencyName = agency.name || agency.agency_name || '';
                        const agencyId = agency.id || agency.agency_id;
                        return (
                          <option key={agencyId || agencyName} value={agencyName}>
                            {agencyName}
                          </option>
                        );
                      })}
                    </select>
                    {formErrors.clientAgency && <span className="error-message">{formErrors.clientAgency}</span>}
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>👤</span> Customer
                    </label>
                    <div style={{ position: 'relative' }} ref={customerInputRef}>
                      <input
                        type="text"
                        className="form-input-field"
                        placeholder="Search or select customer"
                        value={customerSearch || (formData.customer ? allCustomersForFilter.find(c => c.id === formData.customer || c.id?.toString() === formData.customer?.toString())?.name || '' : '')}
                        onChange={(e) => {
                          setCustomerSearch(e.target.value);
                          setCustomerDropdownOpen(true);
                          if (!e.target.value) {
                            setFormData({...formData, customer: ''});
                          }
                        }}
                        onFocus={() => {
                          setCustomerDropdownOpen(true);
                          if (!customerSearch && formData.customer) {
                            const selectedCustomer = allCustomersForFilter.find(c => c.id === formData.customer || c.id?.toString() === formData.customer?.toString());
                            if (selectedCustomer) {
                              setCustomerSearch(selectedCustomer.name || '');
                            }
                          }
                        }}
                        style={{ cursor: 'text' }}
                      />
                      {customerDropdownOpen && allCustomersForFilter && allCustomersForFilter.length > 0 && (
                        <div
                          ref={customerDropdownRef}
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            zIndex: 1000,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            marginTop: '4px'
                          }}
                        >
                          {allCustomersForFilter
                            .filter(customer => {
                              const customerName = customer.name || customer.customer_name || '';
                              if (!customerName || !customer.id) return false;
                              if (!customerSearch) return true;
                              return customerName.toLowerCase().includes(customerSearch.toLowerCase());
                            })
                            .map((customer) => {
                              const customerName = customer.name || customer.customer_name || '';
                              const customerId = customer.id;
                              return (
                                <div
                                  key={customerId || customerName}
                                  onClick={() => {
                                    setFormData({...formData, customer: customerId});
                                    setCustomerSearch(customerName);
                                    setCustomerDropdownOpen(false);
                                  }}
                                  style={{
                                    padding: '10px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #eee',
                                    backgroundColor: formData.customer === customerId || formData.customer?.toString() === customerId?.toString() ? '#f0f7ff' : 'white'
                                  }}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = formData.customer === customerId || formData.customer?.toString() === customerId?.toString() ? '#f0f7ff' : 'white'}
                                >
                                  {customerName}
                                </div>
                              );
                            })}
                          {allCustomersForFilter.filter(customer => {
                            const customerName = customer.name || customer.customer_name || '';
                            if (!customerName || !customer.id) return false;
                            if (!customerSearch) return true;
                            return customerName.toLowerCase().includes(customerSearch.toLowerCase());
                          }).length === 0 && (
                            <div style={{ padding: '10px', color: '#999', textAlign: 'center' }}>
                              No customers found
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-field full-width">
                    <label className="form-field-label">
                      <span>📝</span> Notes / Special Instructions
                    </label>
                    <textarea
                      className="form-input-field"
                      placeholder="Enter any special instructions or notes for this trip"
                      rows="3"
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      style={{ resize: 'vertical', minHeight: '80px' }}
                    />
                  </div>

                  {/* Vehicle & Driver Assignment Section */}
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333', marginBottom: '8px', marginTop: '24px' }}>Vehicle & Driver Assignment</h3>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>🚚</span> Vehicle <span className="required-asterisk">*</span>
                    </label>
                    <select
                      className={`form-input-field ${formErrors.vehicle ? 'error' : ''}`}
                      value={formData.vehicle}
                      onChange={(e) => handleVehicleChange(e.target.value)}
                    >
                      <option value="">Select vehicle</option>
                      {allVehiclesForFilter.map((vehicle) => {
                        const vehicleNumber = vehicle.number || vehicle.vehicle_number || '';
                        const vehicleId = vehicle.id || vehicle.vehicle_id;
                        return (
                          <option key={vehicleId || vehicleNumber} value={vehicleNumber}>
                            {vehicleNumber}
                        </option>
                        );
                      })}
                    </select>
                    {formErrors.vehicle && <span className="error-message">{formErrors.vehicle}</span>}
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>👤</span> Assigned Driver <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input-field ${formErrors.assignedDriver ? 'error' : ''}`}
                      placeholder="Select vehicle to auto-populate driver"
                      value={formData.assignedDriver}
                      readOnly
                      style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                    />
                    {formErrors.assignedDriver && <span className="error-message">{formErrors.assignedDriver}</span>}
                  </div>

                  {/* Route Details Section */}
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333', marginBottom: '8px', marginTop: '24px' }}>Route Details</h3>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>🛣️</span> Route Name
                    </label>
                    <input
                      type="text"
                      className="form-input-field"
                      placeholder="Enter route name"
                      value={formData.routeName}
                      onChange={(e) => setFormData({ ...formData, routeName: e.target.value })}
                    />
                  </div>

                  <div className="form-field" style={{ position: 'relative' }}>
                    <label className="form-field-label">
                      <span>📍</span> Start Location
                    </label>
                    <input
                      ref={startLocationAutocomplete.inputRef}
                      type="text"
                      className="form-input-field"
                      placeholder="Search location (e.g., Mumbai, Maharashtra)"
                      value={formData.startLocation || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only update if the value is actually different to prevent truncation
                        if (value !== formData.startLocation) {
                          handleStartLocationInput(value);
                        }
                      }}
                      onBlur={(e) => {
                        // Ensure the value is preserved on blur
                        if (e.target.value && e.target.value !== formData.startLocation) {
                          setFormData(prev => ({ ...prev, startLocation: e.target.value }));
                          setStartLocationSearch(e.target.value);
                        }
                      }}
                      onFocus={() => {
                        if (startLocationSearch.trim().length > 0) {
                          const searchLower = startLocationSearch.toLowerCase();
                          const hasMatches = indianCities.some(city => city.toLowerCase().includes(searchLower));
                          setStartLocationSuggestionsOpen(hasMatches);
                        }
                      }}
                    />
                    {/* Only show city dropdown if Google Maps is not loaded (fallback) */}
                    {!isGoogleMapsLoaded && startLocationSuggestionsOpen && filteredStartCities.length > 0 && (
                      <div
                        ref={startLocationSuggestionsRef}
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: 'white',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          zIndex: 1000,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          marginTop: '4px'
                        }}
                      >
                        {filteredStartCities.map((city, index) => (
                          <div
                            key={index}
                            onClick={() => handleStartLocationCitySelect(city)}
                            style={{
                              padding: '10px',
                              cursor: 'pointer',
                              borderBottom: index < filteredStartCities.length - 1 ? '1px solid #eee' : 'none'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                          >
                            {city}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="form-field" style={{ position: 'relative' }}>
                    <label className="form-field-label">
                      <span>📍</span> Destination
                    </label>
                    <input
                      ref={destinationAutocomplete.inputRef}
                      type="text"
                      className="form-input-field"
                      placeholder="Search location (e.g., Delhi, Delhi)"
                      value={formData.destination || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only update if the value is actually different to prevent truncation
                        if (value !== formData.destination) {
                          setDestinationSearch(value);
                          setFormData(prev => ({ ...prev, destination: value }));
                        }
                      }}
                      onBlur={(e) => {
                        // Ensure the value is preserved on blur
                        if (e.target.value && e.target.value !== formData.destination) {
                          setFormData(prev => ({ ...prev, destination: e.target.value }));
                          setDestinationSearch(e.target.value);
                        }
                      }}
                      onFocus={() => {
                        if (destinationSearch.trim().length > 0) {
                          const searchLower = destinationSearch.toLowerCase();
                          const hasMatches = indianCities.some(city => city.toLowerCase().includes(searchLower));
                          setDestinationSuggestionsOpen(hasMatches);
                        }
                      }}
                    />
                    {/* Only show city dropdown if Google Maps is not loaded (fallback) */}
                    {!isGoogleMapsLoaded && destinationSuggestionsOpen && filteredDestinationCities.length > 0 && (
                      <div
                        ref={destinationSuggestionsRef}
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: 'white',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          zIndex: 1000,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          marginTop: '4px'
                        }}
                      >
                        {filteredDestinationCities.map((city, index) => (
                          <div
                            key={index}
                            onClick={() => handleDestinationCitySelect(city)}
                            style={{
                              padding: '10px',
                              cursor: 'pointer',
                              borderBottom: index < filteredDestinationCities.length - 1 ? '1px solid #eee' : 'none'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                          >
                            {city}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>


                  {/* Intermediate Stops Section */}
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333', marginBottom: '8px', marginTop: '24px' }}>Intermediate Stops</h3>

                  {formData.stops.map((stop, index) => (
                    <div key={index} className="stop-item">
                      <div className="stop-drag-handle">⋮⋮</div>
                      <div className="stop-fields">
                        <div className="form-field">
                          <label className="form-field-label">Location</label>
                          <input
                            type="text"
                            className="form-input-field"
                            placeholder="Enter location"
                            value={stop.location}
                            onChange={(e) => handleStopChange(index, 'location', e.target.value)}
                          />
                        </div>
                        <div className="stop-grid">
                          <div className="form-field">
                            <label className="form-field-label">Estimated Date</label>
                            <input
                              type="datetime-local"
                              className="form-input-field"
                              value={stop.estimatedDate || ''}
                              onChange={(e) => handleStopChange(index, 'estimatedDate', e.target.value)}
                            />
                          </div>
                          <div className="form-field">
                            <label className="form-field-label">Purpose</label>
                            <select
                              className="form-input-field"
                              value={stop.purpose || ''}
                              onChange={(e) => handleStopChange(index, 'purpose', e.target.value)}
                            >
                              <option value="">Select purpose</option>
                              <option value="Checkpoint">Checkpoint</option>
                              <option value="Break">Break</option>
                              <option value="Loading">Loading</option>
                              <option value="Unloading">Unloading</option>
                              <option value="Inspection">Inspection</option>
                            </select>
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="stop-delete-btn"
                        onClick={() => handleRemoveStop(index)}
                        title="Delete stop"
                      >
                        🗑️
                      </button>
                    </div>
                  ))}

                  <button
                    type="button"
                    className="add-stop-btn"
                    onClick={handleAddStop}
                  >
                    + Add Stop
                  </button>

                  {/* Load Details Section */}
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333', marginBottom: '8px', marginTop: '24px' }}>Load Details</h3>

                      <div className="form-field">
                        <label className="form-field-label">
                          <span>📊</span> Total Capacity (tons) <span className="required-asterisk">*</span>
                        </label>
                        <input
                          type="number"
                          className={`form-input-field ${formErrors.totalCapacity ? 'error' : ''}`}
                          placeholder="Enter total capacity"
                          value={formData.totalCapacity}
                          onChange={(e) => {
                            setFormData({...formData, totalCapacity: e.target.value});
                            if (formErrors.totalCapacity) {
                              setFormErrors({...formErrors, totalCapacity: ''});
                            }
                          }}
                          min="0"
                          step="0.01"
                        />
                        {formErrors.totalCapacity && <span className="error-message">{formErrors.totalCapacity}</span>}
                      </div>

                      <div className="form-field">
                        <label className="form-field-label">
                          <span>📦</span> Used Capacity (tons) <span className="required-asterisk">*</span>
                        </label>
                        <input
                          type="number"
                          className={`form-input-field ${formErrors.usedCapacity ? 'error' : ''}`}
                          placeholder="Enter used capacity"
                          value={formData.usedCapacity}
                          onChange={(e) => {
                            setFormData({...formData, usedCapacity: e.target.value});
                            if (formErrors.usedCapacity) {
                              setFormErrors({...formErrors, usedCapacity: ''});
                            }
                          }}
                          min="0"
                          step="0.01"
                        />
                        {formErrors.usedCapacity && <span className="error-message">{formErrors.usedCapacity}</span>}
                      </div>

                  <div className="form-field full-width">
                    <label className="form-field-label">
                      <span>📝</span> Load Description
                    </label>
                    <textarea
                      className="form-input-field"
                      placeholder="Enter load description"
                      rows="3"
                      value={formData.loadDescription}
                      onChange={(e) => setFormData({...formData, loadDescription: e.target.value})}
                      style={{ resize: 'vertical', minHeight: '80px' }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer-bar">
              <button className="modal-btn-cancel" onClick={handleCloseModal}>
                Cancel
              </button>
              <button className="modal-btn-save" onClick={async () => {
                // Basic validation
                const errors = {};
                if (!formData.tripDateTime) errors.tripDateTime = 'Trip date and time is required';
                if (!formData.clientAgency) errors.clientAgency = 'Transporter is required';
                if (!formData.vehicle) errors.vehicle = 'Vehicle is required';
                if (!formData.assignedDriver) errors.assignedDriver = 'Driver is required';
                if (!formData.startLocation) errors.startLocation = 'Start location is required';
                if (!formData.destination) errors.destination = 'Destination is required';
                
                if (Object.keys(errors).length > 0) {
                  setFormErrors(errors);
                  return;
                }
                
                try {
                  // Find agency ID from agency name
                  let agencyId = null;
                  if (allAgenciesForFilter && allAgenciesForFilter.length > 0) {
                    const agency = allAgenciesForFilter.find(a => 
                      (a.name || a.agency_name || '').toString() === formData.clientAgency.toString() ||
                      (a.id && a.id.toString() === formData.clientAgency.toString())
                    );
                    if (agency) {
                      agencyId = agency.id;
                    }
                  }
                  
                  if (!agencyId) {
                    alert('Transporter not found. Please select a valid transporter.');
                    return;
                  }

                  // Find vehicle ID from vehicle number
                  let vehicleId = null;
                  if (allVehiclesForFilter && allVehiclesForFilter.length > 0) {
                    const vehicle = allVehiclesForFilter.find(v => 
                      (v.number || v.vehicle_number || '').toString() === formData.vehicle.toString() ||
                      (v.id && v.id.toString() === formData.vehicle.toString())
                    );
                    if (vehicle) {
                      vehicleId = vehicle.id;
                    }
                  }
                  
                  if (!vehicleId) {
                    alert('Vehicle not found. Please select a valid vehicle.');
                    return;
                  }

                  // Find driver ID from driver name
                  let driverId = null;
                  if (allDriversForFilter && allDriversForFilter.length > 0) {
                    const driver = allDriversForFilter.find(d => 
                      (d.name || d.full_name || '').toString() === formData.assignedDriver.toString() ||
                      (d.id && d.id.toString() === formData.assignedDriver.toString())
                    );
                    if (driver) {
                      driverId = driver.id;
                    }
                  }
                  
                  if (!driverId && formData.assignedDriver) {
                    try {
                      const driverResponse = await driversService.getDrivers({ limit: 1000 });
                      let driversList = [];
                      if (Array.isArray(driverResponse.data)) {
                        driversList = driverResponse.data;
                      } else if (driverResponse.data?.items) {
                        driversList = driverResponse.data.items;
                      } else if (Array.isArray(driverResponse)) {
                        driversList = driverResponse;
                      } else if (driverResponse.items) {
                        driversList = driverResponse.items;
                      }
                      
                      const driver = driversList.find(d => 
                        (d.full_name || d.name || d.driver_name || '') === formData.assignedDriver ||
                        (d.id && d.id.toString() === formData.assignedDriver) ||
                        (d.driver_id && d.driver_id.toString() === formData.assignedDriver)
                      );
                      
                      if (driver) {
                        driverId = driver.id || driver.driver_id;
                      }
                    } catch (err) {
                      console.error('Error fetching drivers:', err);
                    }
                  }
                  
                  if (!driverId) {
                    alert('Driver not found. Please select a valid driver.');
                    return;
                  }

                  // Convert datetime to ISO format
                  let tripDateTime = formData.tripDateTime;
                  if (tripDateTime && !tripDateTime.includes('T')) {
                    tripDateTime = tripDateTime + 'T00:00:00';
                  }
                  if (tripDateTime) {
                    const date = new Date(tripDateTime);
                    if (!isNaN(date.getTime())) {
                      tripDateTime = date.toISOString();
                    }
                  }

                  // Get customer_id from selected customer
                  let customerId = null;
                  if (formData.customer) {
                    const customer = allCustomersForFilter.find(c => 
                      (c.id && c.id.toString() === formData.customer.toString()) ||
                      (c.name && c.name === formData.customer)
                    );
                    if (customer) {
                      customerId = customer.id;
                    }
                  }

                  // Prepare trip data for API
                  const tripData = {
                    trip_date_time: tripDateTime,
                    trip_status: formData.tripStatus || 'Scheduled',
                    agency_id: agencyId,
                    client_agency: formData.clientAgency || '',
                    customer_id: customerId || null,
                    vehicle_id: vehicleId,
                    assigned_driver_id: driverId,
                    route_name: formData.routeName || null, // Route name
                    start_location: formData.startLocation || '',
                    destination: formData.destination || '',
                    load_type: formData.loadType || null,
                    total_capacity: formData.totalCapacity ? parseFloat(formData.totalCapacity) : null, // Total capacity in tons
                    used_capacity: formData.usedCapacity ? parseFloat(formData.usedCapacity) : null, // Used capacity in tons
                    remaining_capacity: formData.remainingCapacity ? parseFloat(formData.remainingCapacity) : null, // Remaining capacity in tons
                    load_description: formData.loadDescription || null,
                    load_volume: formData.loadVolume ? parseFloat(formData.loadVolume) : null,
                    volume_unit: formData.volumeUnit || 'm³',
                    notes: formData.notes || ''
                  };
                  
                  console.log('Updating trip with data:', tripData);
                  
                  await tripsService.updateTrip(editingTrip.id || editingTrip.tripId, tripData);
                  
                  // Handle trip stops - fetch existing stops first
                  const existingStopsResponse = await tripsService.getStopsByTripId(editingTrip.id || editingTrip.tripId);
                  const existingStops = Array.isArray(existingStopsResponse.data) 
                    ? existingStopsResponse.data 
                    : (Array.isArray(existingStopsResponse) ? existingStopsResponse : []);
                  
                  // Determine which stops to create, update, or delete
                  const stopsToCreate = formData.stops.filter(stop => !stop.id && stop.location && stop.location.trim());
                  const stopsToUpdate = formData.stops.filter(stop => stop.id && stop.location && stop.location.trim());
                  const stopsToDelete = existingStops.filter(existingStop => 
                    !formData.stops.some(stop => stop.id === existingStop.id)
                  );
                  
                  // Create new stops
                  if (stopsToCreate.length > 0) {
                    try {
                      const createPromises = stopsToCreate.map((stop, index) => {
                        let estimatedDate = null;
                        if (stop.estimatedDate) {
                          const date = new Date(stop.estimatedDate);
                          if (!isNaN(date.getTime())) {
                            estimatedDate = date.toISOString();
                          }
                        }
                        
                        const stopData = {
                          trip_id: editingTrip.id || editingTrip.tripId,
                          stop_sequence: existingStops.length + index + 1,
                          stop_location: stop.location || '',
                          estimated_date: estimatedDate,
                          purpose: stop.purpose || null
                        };
                        
                        return tripsService.createTripStop(stopData);
                      });
                      
                      await Promise.all(createPromises);
                    } catch (stopError) {
                      console.error('Error creating stops:', stopError);
                    }
                  }
                  
                  // Update existing stops
                  if (stopsToUpdate.length > 0) {
                    try {
                      const updatePromises = stopsToUpdate.map(stop => {
                        let estimatedDate = null;
                        if (stop.estimatedDate) {
                          const date = new Date(stop.estimatedDate);
                          if (!isNaN(date.getTime())) {
                            estimatedDate = date.toISOString();
                          }
                        }
                        
                        const stopData = {
                          stop_location: stop.location || '',
                          estimated_date: estimatedDate,
                          purpose: stop.purpose || null
                        };
                        
                        return tripsService.updateTripStop(stop.id, stopData);
                      });
                      
                      await Promise.all(updatePromises);
                    } catch (stopError) {
                      console.error('Error updating stops:', stopError);
                    }
                  }
                  
                  // Delete removed stops
                  if (stopsToDelete.length > 0) {
                    try {
                      const deletePromises = stopsToDelete.map(stop => 
                        tripsService.deleteTripStop(stop.id)
                      );
                      await Promise.all(deletePromises);
                    } catch (stopError) {
                      console.error('Error deleting stops:', stopError);
                    }
                  }
                  
                  // Refresh trips list
                  await fetchTrips();
                  
                handleCloseModal();
                } catch (err) {
                  setError(err.message);
                  console.error('Error updating trip:', err);
                  alert('Failed to update trip: ' + err.message);
                }
              }}>
                Update Trip
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Trip Details Modal */}
      {showViewModal && viewingTrip && (
        <div className="add-vehicle-modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="view-trip-modal-content" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="view-trip-header">
              <div>
                <h2 className="view-trip-title">View Trip Details</h2>
                <div className="view-trip-header-info">
                  <span className="view-trip-id">{viewingTrip.id}</span>
                  <span className={`status-badge ${getStatusClass(viewingTrip.status)}`}>
                    {viewingTrip.status}
                  </span>
                </div>
              </div>
              <div className="view-trip-header-right">
                <div className="view-trip-remaining">{viewingTrip.eta}</div>
                <button className="modal-close-btn" onClick={() => setShowViewModal(false)}>
                  ✕
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="form-tabs">
              <button className="form-tab active">Overview</button>
            </div>

            {/* Body */}
            <div className="view-trip-body">
              {/* Trip Progress */}
              <div className="view-trip-progress-section">
                <div className="view-trip-progress-header">
                  <h3 className="view-trip-section-title">Trip Progress</h3>
                  <span className="view-trip-progress-percent">{viewingTrip.progress !== undefined ? `${viewingTrip.progress}%` : '0%'}</span>
                </div>
                <div className="view-trip-progress-bar">
                  <div className="view-trip-progress-fill" style={{ width: `${viewingTrip.progress !== undefined ? viewingTrip.progress : 0}%` }}></div>
                </div>
              </div>

              {/* Information Cards */}
              <div className="view-trip-cards-grid">
                {/* Trip Details Card */}
                <div className="view-trip-card">
                  <div className="view-trip-card-icon">ℹ️</div>
                  <div className="view-trip-card-content">
                    <h4 className="view-trip-card-title">Trip Details</h4>
                    <div className="view-trip-card-item">
                      <span className="view-trip-card-label">Trip ID:</span>
                      <span className="view-trip-card-value">{viewingTrip.id}</span>
                    </div>
                    <div className="view-trip-card-item">
                      <span className="view-trip-card-label">Date & Time:</span>
                      <span className="view-trip-card-value">{viewingTrip.tripDateTime}</span>
                    </div>
                    <div className="view-trip-card-item">
                      <span className="view-trip-card-label">Status:</span>
                      <span className={`status-badge-small ${getStatusClass(viewingTrip.status)}`}>
                        {viewingTrip.status}
                      </span>
                    </div>
                    <div className="view-trip-card-item">
                      <span className="view-trip-card-label">ETA:</span>
                      <span className="view-trip-card-value">{viewingTrip.eta}</span>
                    </div>
                  </div>
                </div>

                {/* Vehicle Information Card */}
                <div className="view-trip-card">
                  <div className="view-trip-card-icon">🚚</div>
                  <div className="view-trip-card-content">
                    <h4 className="view-trip-card-title">Vehicle Information</h4>
                    <div className="view-trip-card-item">
                      <span className="view-trip-card-label">Vehicle Number:</span>
                      <span className="view-trip-card-value">{viewingTrip.vehicleNumber}</span>
                    </div>
                    <div className="view-trip-card-item">
                      <span className="view-trip-card-label">Type:</span>
                      <span className="view-trip-card-value">{viewingTrip.vehicleType || 'N/A'}</span>
                    </div>
                    <div className="view-trip-card-item">
                      <span className="view-trip-card-label">Model:</span>
                      <span className="view-trip-card-value">{viewingTrip.vehicleModel || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                {/* Driver Information Card */}
                <div className="view-trip-card">
                  <div className="view-trip-card-icon">👤</div>
                  <div className="view-trip-card-content">
                    <h4 className="view-trip-card-title">Driver Information</h4>
                    <div className="view-trip-card-item">
                      <span className="view-trip-card-label">Driver Name:</span>
                      <span className="view-trip-card-value">{viewingTrip.driverName}</span>
                    </div>
                    <div className="view-trip-card-item">
                      <span className="view-trip-card-label">Contact:</span>
                      <span className="view-trip-card-value">{viewingTrip.driverContact || 'N/A'}</span>
                    </div>
                    <div className="view-trip-card-item">
                      <span className="view-trip-card-label">License:</span>
                      <span className="view-trip-card-value">{viewingTrip.driverLicense || 'N/A'}</span>
                    </div>
                    <div className="view-trip-card-item">
                      <span className="view-trip-card-label">Total Trips:</span>
                      <span className="view-trip-card-value">{viewingTrip.driverTotalTrips || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Route Information */}
              <div className="view-trip-route-section">
                <div className="view-trip-route-header">
                  <span className="view-trip-route-icon">📍</span>
                  <h3 className="view-trip-section-title">Route Information</h3>
                </div>
                <div className="view-trip-route-content">
                  <div className="view-trip-route-origin">
                    <div className="view-trip-route-dot origin-dot"></div>
                    <div>
                      <div className="view-trip-route-location">{viewingTrip.startLocation || 'N/A'}</div>
                      <div className="view-trip-route-address">{viewingTrip.startLocation || 'Start location not specified'}</div>
                    </div>
                  </div>
                  <div className="view-trip-route-distance">
                    <span className="view-trip-route-distance-value">
                      {viewingTrip.estimatedDistance ? `${viewingTrip.estimatedDistance} km` : 'N/A'}
                    </span>
                  </div>
                  <div className="view-trip-route-destination">
                    <div className="view-trip-route-dot destination-dot"></div>
                    <div>
                      <div className="view-trip-route-location">{viewingTrip.destination || 'N/A'}</div>
                      <div className="view-trip-route-address">{viewingTrip.destination || 'Destination not specified'}</div>
                    </div>
                  </div>
                </div>
                {/* Google Maps Route Map */}
                <RouteMap 
                  startLocation={viewingTrip.startLocation} 
                  destination={viewingTrip.destination}
                />
              </div>

              {/* Timeline Section */}
              <div className="view-trip-timeline-grid">
                {/* Stops Column */}
                <div className="view-trip-timeline-column">
                  <div className="view-trip-timeline-header">
                    <span className="view-trip-timeline-icon">⊞</span>
                    <h3 className="view-trip-section-title">Stops</h3>
                  </div>
                  <div className="view-trip-timeline-list">
                    {viewingTrip.stopsData && viewingTrip.stopsData.length > 0 ? (
                      viewingTrip.stopsData.map((stop, index) => (
                        <div key={stop.id || index} className="view-trip-timeline-item">
                          <div className="view-trip-timeline-dot"></div>
                          <div className="view-trip-timeline-content">
                            <div className="view-trip-timeline-description">
                              {stop.stop_location || stop.location || `Stop ${index + 1}`}
                            </div>
                            {stop.purpose && (
                              <div className="view-trip-timeline-purpose">{stop.purpose}</div>
                            )}
                            {stop.estimated_date && (
                              <div className="view-trip-timeline-date">
                                {new Date(stop.estimated_date).toLocaleString('en-GB', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: true
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="view-trip-timeline-item">
                        <div className="view-trip-timeline-content">
                          <div className="view-trip-timeline-description">No stops scheduled</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Timeline Column */}
                <div className="view-trip-timeline-column">
                  <div className="view-trip-timeline-header">
                    <span className="view-trip-timeline-icon">🕐</span>
                    <h3 className="view-trip-section-title">Timeline</h3>
                  </div>
                  <div className="view-trip-timeline-list">
                    {/* Trip Created */}
                    {viewingTrip.created_at_formatted && (
                      <div className="view-trip-timeline-item">
                        <div className="view-trip-timeline-dot"></div>
                        <div className="view-trip-timeline-content">
                          <div className="view-trip-timeline-date">{viewingTrip.created_at_formatted}</div>
                          <div className="view-trip-timeline-description">Trip Created</div>
                        </div>
                      </div>
                    )}
                    
                    {/* Trip Scheduled */}
                    {viewingTrip.tripDateTime && (
                      <div className="view-trip-timeline-item">
                        <div className="view-trip-timeline-dot"></div>
                        <div className="view-trip-timeline-content">
                          <div className="view-trip-timeline-date">{viewingTrip.tripDateTime}</div>
                          <div className="view-trip-timeline-description">
                            Trip Scheduled
                            {viewingTrip.startLocation && ` from ${viewingTrip.startLocation}`}
                            {viewingTrip.destination && ` to ${viewingTrip.destination}`}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Status: In Progress */}
                    {viewingTrip.status === 'In Progress' && (
                      <div className="view-trip-timeline-item">
                        <div className="view-trip-timeline-dot"></div>
                        <div className="view-trip-timeline-content">
                          <div className="view-trip-timeline-date">
                            {viewingTrip.updated_at_formatted || viewingTrip.tripDateTime || 'N/A'}
                          </div>
                          <div className="view-trip-timeline-description">
                            Trip Started - In Progress
                            {viewingTrip.startLocation && ` from ${viewingTrip.startLocation}`}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Status: Completed */}
                    {viewingTrip.status === 'Completed' && (
                      <>
                        <div className="view-trip-timeline-item">
                          <div className="view-trip-timeline-dot"></div>
                          <div className="view-trip-timeline-content">
                            <div className="view-trip-timeline-date">
                              {viewingTrip.tripDateTime || viewingTrip.created_at_formatted || 'N/A'}
                            </div>
                            <div className="view-trip-timeline-description">
                              Trip Started
                              {viewingTrip.startLocation && ` from ${viewingTrip.startLocation}`}
                            </div>
                          </div>
                        </div>
                        <div className="view-trip-timeline-item">
                          <div className="view-trip-timeline-dot"></div>
                          <div className="view-trip-timeline-content">
                            <div className="view-trip-timeline-date">
                              {viewingTrip.updated_at_formatted || viewingTrip.actual_duration || 'N/A'}
                            </div>
                            <div className="view-trip-timeline-description">
                              Trip Completed
                              {viewingTrip.destination && ` at ${viewingTrip.destination}`}
                              {viewingTrip.actual_duration && ` (Duration: ${viewingTrip.actual_duration})`}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                    
                    {/* Status: Cancelled */}
                    {viewingTrip.status === 'Cancelled' && (
                      <div className="view-trip-timeline-item">
                        <div className="view-trip-timeline-dot"></div>
                        <div className="view-trip-timeline-content">
                          <div className="view-trip-timeline-date">
                            {viewingTrip.updated_at_formatted || viewingTrip.tripDateTime || 'N/A'}
                          </div>
                          <div className="view-trip-timeline-description">Trip Cancelled</div>
                        </div>
                      </div>
                    )}
                    
                    {/* Status: Delayed */}
                    {viewingTrip.status === 'Delayed' && (
                      <div className="view-trip-timeline-item">
                        <div className="view-trip-timeline-dot"></div>
                        <div className="view-trip-timeline-content">
                          <div className="view-trip-timeline-date">
                            {viewingTrip.updated_at_formatted || viewingTrip.tripDateTime || 'N/A'}
                          </div>
                          <div className="view-trip-timeline-description">Trip Delayed</div>
                        </div>
                      </div>
                    )}
                    
                    {/* Show stops in timeline if available */}
                    {viewingTrip.stopsData && viewingTrip.stopsData.length > 0 && viewingTrip.stopsData
                      .filter(stop => stop.estimated_date)
                      .sort((a, b) => new Date(a.estimated_date) - new Date(b.estimated_date))
                      .map((stop, index) => (
                        <div key={`stop-timeline-${stop.id || index}`} className="view-trip-timeline-item">
                          <div className="view-trip-timeline-dot"></div>
                          <div className="view-trip-timeline-content">
                            <div className="view-trip-timeline-date">
                              {new Date(stop.estimated_date).toLocaleString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true
                              })}
                            </div>
                            <div className="view-trip-timeline-description">
                              {stop.purpose ? `${stop.purpose} at ` : 'Stop at '}
                              {stop.stop_location || stop.location || `Stop ${index + 1}`}
                            </div>
                          </div>
                        </div>
                      ))}
                    
                    {/* If no timeline events, show a message */}
                    {!viewingTrip.tripDateTime && !viewingTrip.created_at_formatted && viewingTrip.status === 'Scheduled' && (
                      <div className="view-trip-timeline-item">
                        <div className="view-trip-timeline-content">
                          <div className="view-trip-timeline-description">No timeline events available</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="view-trip-footer">
              <button className="view-trip-download-btn" onClick={handleDownloadTripSheet}>
                <span>⬇️</span> Download Trip Sheet
              </button>
              <button className="view-trip-close-btn" onClick={() => setShowViewModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stops Modal */}
      {showStopsModal && selectedTripForStops && (
        <div 
          className="modal-overlay" 
          onClick={() => {
            console.log('Admin Trip Management: Closing stops modal');
            setShowStopsModal(false);
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <div 
            className="modal-content" 
            onClick={(e) => e.stopPropagation()} 
            style={{ 
              maxWidth: '600px', 
              width: '90%',
              backgroundColor: 'white',
              borderRadius: '8px',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              zIndex: 10000
            }}
          >
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', borderBottom: '1px solid #e5e7eb' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#111827' }}>
                {selectedTripForStops.scheduleId || selectedTripForStops.schedule_id 
                  ? `Schedule Stops - ${selectedTripForStops.scheduleId || selectedTripForStops.schedule_id}${selectedTripForStops.scheduleName ? ` (${selectedTripForStops.scheduleName})` : ''}`
                  : `Trip Stops - ${selectedTripForStops.id || selectedTripForStops.tripId || 'N/A'}`}
              </h2>
              <button 
                onClick={() => setShowStopsModal(false)}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  fontSize: '24px', 
                  cursor: 'pointer', 
                  color: '#6b7280',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px', maxHeight: '500px', overflowY: 'auto' }}>
              {getStopsData(selectedTripForStops).length === 0 ? (
                <p style={{ textAlign: 'center', color: '#6b7280', padding: '20px' }}>No stops for this trip</p>
              ) : (
                <div className="stops-list-container">
                  {getStopsData(selectedTripForStops).map((stop, index) => {
                    const totalStops = selectedTripForStops.stopsData?.length || selectedTripForStops.stops || 0;
                    
                    return (
                    <div 
                      key={index} 
                      style={{
                        padding: '20px',
                        marginBottom: '16px',
                        background: '#ffffff',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        borderLeft: '4px solid #2563EB',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                        <div style={{ flex: 1 }}>
                          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>
                            {stop.location}
                        </h3>
                          <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: 500 }}>
                            Stop {stop.stopSequence || (index + 1)} of {totalStops}
                        </span>
                      </div>
                        {stop.hasActualArrival && (
                          <span style={{ 
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '4px 12px',
                            background: '#d1fae5',
                            color: '#065f46',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 600
                          }}>
                              <span style={{ 
                                display: 'inline-block',
                              width: '6px',
                              height: '6px',
                                borderRadius: '50%',
                                background: '#10b981'
                              }}></span>
                            Arrived
                              </span>
                            )}
                          </div>
                      
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '12px' }}>
                        {stop.purpose && (
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                            <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: 500, minWidth: '80px' }}>Purpose:</span>
                            <span style={{ fontSize: '14px', color: '#111827' }}>{stop.purpose}</span>
                          </div>
                        )}
                        
                        {stop.estimatedDate && (
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                            <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: 500, minWidth: '80px' }}>Estimated:</span>
                            <span style={{ fontSize: '14px', color: '#111827' }}>{stop.estimatedDate}</span>
                              </div>
                            )}
                        
                        {stop.actualArrivalTime && (
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                            <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: 500, minWidth: '80px' }}>Arrived:</span>
                            <span style={{ fontSize: '14px', color: '#065f46', fontWeight: 500 }}>{stop.actualArrivalTime}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                  })}
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ padding: '16px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
              <button 
                onClick={() => setShowStopsModal(false)}
                style={{
                  padding: '10px 20px',
                  background: '#2563EB',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Trip Confirmation Modal */}
      {showCancelConfirm && tripToCancel && (
        <div className="modal-overlay" onClick={handleCancelCancel}>
          <div className="cancel-trip-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="cancel-trip-header">
              <div className="cancel-warning-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
              </div>
              <h3 className="cancel-trip-title">Confirm Action</h3>
            </div>
            <div className="cancel-trip-body">
              <p className="cancel-trip-message">
                Are you sure you want to mark this trip as Cancelled?
              </p>
              <div className="cancel-trip-details">
                <div className="cancel-detail-row">
                  <span className="cancel-detail-label">Trip ID:</span>
                  <span className="cancel-detail-value">{tripToCancel.id}</span>
                </div>
                <div className="cancel-detail-row">
                  <span className="cancel-detail-label">Current Status:</span>
                  <span className={`status-badge-small ${getStatusClass(tripToCancel.status)}`}>
                    {tripToCancel.status}
                  </span>
                </div>
              </div>
            </div>
            <div className="cancel-trip-footer">
              <button className="cancel-trip-cancel-btn" onClick={handleCancelCancel}>
                Cancel
              </button>
              <button className="cancel-trip-confirm-btn" onClick={handleCancelConfirm}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Trip Schedule Modal */}
      {(showAddScheduleModal || showScheduleEditModal) && (
        <div className="add-vehicle-modal-overlay" onClick={handleCloseScheduleModal}>
          <div className="add-vehicle-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1000px' }}>
            <div className="modal-header-bar">
              <h2 className="modal-title">{editingSchedule ? 'Edit Trip Schedule' : 'Add Trip Schedule'}</h2>
              <button className="modal-close-btn" onClick={handleCloseScheduleModal}>
                ✕
              </button>
            </div>

            <div className="modal-body-content">
              <div className="form-tabs">
                <button className="form-tab active">Schedule Details</button>
              </div>

              <div className="form-content-trip">
                <div className="form-fields-column">
                  {/* Schedule Information Section */}
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333', marginBottom: '8px', marginTop: '0' }}>Schedule Information</h3>
                  
                  <div className="form-field">
                    <label className="form-field-label">
                      <span>🆔</span> Schedule ID (auto-generated)
                    </label>
                    <input
                      type="text"
                      className="form-input-field"
                      value={scheduleFormData.scheduleId}
                      readOnly
                      style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                    />
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>📋</span> Schedule Name <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input-field ${scheduleFormErrors.scheduleName ? 'error' : ''}`}
                      placeholder="Enter schedule name (e.g., Daily Mumbai-Delhi Route)"
                      value={scheduleFormData.scheduleName}
                      onChange={(e) => handleScheduleFormChange('scheduleName', e.target.value)}
                    />
                    {scheduleFormErrors.scheduleName && <span className="error-message">{scheduleFormErrors.scheduleName}</span>}
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>📅</span> Schedule Type <span className="required-asterisk">*</span>
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="scheduleType"
                          value="daily"
                          checked={scheduleFormData.scheduleType === 'daily'}
                          onChange={(e) => handleScheduleFormChange('scheduleType', e.target.value)}
                        />
                        <span>🔄 Daily - Repeat every day</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="scheduleType"
                          value="weekly"
                          checked={scheduleFormData.scheduleType === 'weekly'}
                          onChange={(e) => handleScheduleFormChange('scheduleType', e.target.value)}
                        />
                        <span>📅 Weekly - Repeat every week on selected day(s)</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="scheduleType"
                          value="monthly"
                          checked={scheduleFormData.scheduleType === 'monthly'}
                          onChange={(e) => handleScheduleFormChange('scheduleType', e.target.value)}
                        />
                        <span>📆 Monthly - Repeat monthly on selected day</span>
                      </label>
                    </div>
                  </div>

                  {/* Weekly Schedule Options */}
                  {scheduleFormData.scheduleType === 'weekly' && (
                    <div className="form-field">
                      <label className="form-field-label">
                        <span>📅</span> Select Day(s) of Week <span className="required-asterisk">*</span>
                      </label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                        {daysOfWeek.map((day) => (
                          <label
                            key={day}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '8px 12px',
                              border: `2px solid ${scheduleFormData.selectedDays.includes(day) ? '#2563EB' : '#e0e0e0'}`,
                              borderRadius: '6px',
                              cursor: 'pointer',
                              backgroundColor: scheduleFormData.selectedDays.includes(day) ? '#e3f2fd' : 'white',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={scheduleFormData.selectedDays.includes(day)}
                              onChange={() => handleSelectedDayToggle(day)}
                              style={{ marginRight: '6px' }}
                            />
                            {day}
                          </label>
                        ))}
                      </div>
                      {scheduleFormErrors.selectedDays && <span className="error-message">{scheduleFormErrors.selectedDays}</span>}
                    </div>
                  )}

                  {/* Monthly Schedule Options */}
                  {scheduleFormData.scheduleType === 'monthly' && (
                    <div className="form-field" style={{ position: 'relative' }}>
                      <label className="form-field-label">
                        <span>📆</span> Monthly Date(s) <span className="required-asterisk">*</span>
                      </label>
                      <div ref={calendarInputRef} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                          type="text"
                          readOnly
                          className={`form-input-field ${scheduleFormErrors.monthlyDay ? 'error' : ''}`}
                          value={scheduleFormData.monthlyDay.length > 0 
                            ? `Selected: ${scheduleFormData.monthlyDay.sort((a, b) => a - b).join(', ')}`
                            : 'Click to select dates'
                          }
                          onClick={() => setShowCalendarPopup(!showCalendarPopup)}
                          placeholder="Click to select dates"
                          style={{ cursor: 'pointer', flex: 1 }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowCalendarPopup(!showCalendarPopup)}
                          style={{
                            padding: '10px 16px',
                            background: '#2563EB',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: 500
                          }}
                        >
                          📅
                        </button>
                      </div>
                      
                      {/* Compact Calendar Popup */}
                      {showCalendarPopup && (
                        <div
                          ref={calendarPopupRef}
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            zIndex: 1000,
                            marginTop: '8px',
                            backgroundColor: 'white',
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            padding: '12px',
                            width: '280px'
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* Calendar Header */}
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            marginBottom: '10px'
                          }}>
                            <button
                              type="button"
                              onClick={handlePrevMonth}
                              style={{
                                background: 'none',
                                border: '1px solid #e0e0e0',
                                borderRadius: '4px',
                                padding: '4px 8px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                color: '#333'
                              }}
                            >
                              ←
                            </button>
                            <div style={{ 
                              fontSize: '0.95rem', 
                              fontWeight: 600, 
                              color: '#333' 
                            }}>
                              {monthNames[calendarMonth]} {calendarYear}
                            </div>
                            <button
                              type="button"
                              onClick={handleNextMonth}
                              style={{
                                background: 'none',
                                border: '1px solid #e0e0e0',
                                borderRadius: '4px',
                                padding: '4px 8px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                color: '#333'
                              }}
                            >
                              →
                            </button>
                          </div>

                          {/* Day Headers */}
                          <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(7, 1fr)', 
                            gap: '2px',
                            marginBottom: '6px'
                          }}>
                            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, idx) => (
                              <div key={idx} style={{ 
                                textAlign: 'center', 
                                fontWeight: 600, 
                                color: '#666',
                                fontSize: '0.75rem',
                                padding: '4px 2px'
                              }}>
                                {day}
                              </div>
                            ))}
                          </div>

                          {/* Calendar Days */}
                          <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(7, 1fr)', 
                            gap: '2px'
                          }}>
                            {getCalendarDays().map((date, index) => {
                              if (date === null) {
                                return (
                                  <div
                                    key={`empty-${index}`}
                                    style={{
                                      aspectRatio: '1',
                                      padding: '2px'
                                    }}
                                  />
                                );
                              }
                              const isSelected = scheduleFormData.monthlyDay.includes(date);
                              return (
                                <button
                                  key={date}
                                  type="button"
                                  onClick={() => handleMonthlyDateToggle(date)}
                                  style={{
                                    width: '100%',
                                    aspectRatio: '1',
                                    border: `1px solid ${isSelected ? '#2563EB' : '#e0e0e0'}`,
                                    borderRadius: '4px',
                                    backgroundColor: isSelected ? '#e3f2fd' : 'white',
                                    color: isSelected ? '#2563EB' : '#333',
                                    fontWeight: isSelected ? 600 : 400,
                                    cursor: 'pointer',
                                    fontSize: '0.8rem',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '0'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isSelected) {
                                      e.target.style.backgroundColor = '#f5f5f5';
                                      e.target.style.borderColor = '#2563EB';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isSelected) {
                                      e.target.style.backgroundColor = 'white';
                                      e.target.style.borderColor = '#e0e0e0';
                                    }
                                  }}
                                >
                                  {date}
                                </button>
                              );
                            })}
                          </div>
                          {scheduleFormData.monthlyDay.length > 0 && (
                            <div style={{ 
                              marginTop: '10px', 
                              padding: '6px', 
                              backgroundColor: '#e3f2fd', 
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              color: '#2563EB',
                              textAlign: 'center'
                            }}>
                              Selected: {scheduleFormData.monthlyDay.sort((a, b) => a - b).join(', ')}
                            </div>
                          )}
                        </div>
                      )}
                      <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '8px' }}>
                        Select one or more dates to repeat on those days of every month
                      </div>
                      {scheduleFormErrors.monthlyDay && <span className="error-message">{scheduleFormErrors.monthlyDay}</span>}
                    </div>
                  )}

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>⏰</span> Trip Time <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="time"
                      className={`form-input-field ${scheduleFormErrors.tripDateTime ? 'error' : ''}`}
                      value={scheduleFormData.tripDateTime}
                      onChange={(e) => handleScheduleFormChange('tripDateTime', e.target.value)}
                    />
                    {scheduleFormErrors.tripDateTime && <span className="error-message">{scheduleFormErrors.tripDateTime}</span>}
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>📅</span> End Date (Optional)
                    </label>
                    <input
                      type="date"
                      className="form-input-field"
                      value={scheduleFormData.endDate}
                      onChange={(e) => handleScheduleFormChange('endDate', e.target.value)}
                    />
                    <small style={{ color: '#666', fontSize: '0.85rem', marginTop: '4px' }}>Leave empty for indefinite schedule</small>
                  </div>

                  {/* Trip Details Section - Same as Add Trip */}
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333', marginBottom: '8px', marginTop: '24px' }}>Trip Details</h3>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>📊</span> Trip Status <span className="required-asterisk">*</span>
                    </label>
                    <select
                      className={`form-input-field ${scheduleFormErrors.tripStatus ? 'error' : ''}`}
                      value={scheduleFormData.tripStatus}
                      onChange={(e) => handleScheduleFormChange('tripStatus', e.target.value)}
                    >
                      <option value="Scheduled">Scheduled</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Completed">Completed</option>
                      <option value="Cancelled">Cancelled</option>
                      <option value="Delayed">Delayed</option>
                    </select>
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>🏢</span> Client / Transporter Name <span className="required-asterisk">*</span>
                    </label>
                    <select
                      className={`form-input-field ${scheduleFormErrors.clientAgency ? 'error' : ''}`}
                      value={scheduleFormData.clientAgency}
                      onChange={(e) => handleScheduleFormChange('clientAgency', e.target.value)}
                    >
                      <option value="">Select client or transporter</option>
                      {allAgenciesForFilter.map((agency) => {
                        const agencyName = agency.name || agency.agency_name || '';
                        const agencyId = agency.id || agency.agency_id;
                        return (
                          <option key={agencyId || agencyName} value={agencyName}>
                            {agencyName}
                          </option>
                        );
                      })}
                    </select>
                    {scheduleFormErrors.clientAgency && <span className="error-message">{scheduleFormErrors.clientAgency}</span>}
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>📝</span> Notes / Special Instructions
                    </label>
                    <textarea
                      className="form-input-field"
                      placeholder="Enter any special instructions or notes"
                      rows="3"
                      value={scheduleFormData.notes}
                      onChange={(e) => handleScheduleFormChange('notes', e.target.value)}
                      style={{ resize: 'vertical', minHeight: '80px' }}
                    />
                  </div>

                  {/* Vehicle & Driver Assignment Section */}
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333', marginBottom: '8px', marginTop: '24px' }}>Vehicle & Driver Assignment</h3>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>🚚</span> Vehicle <span className="required-asterisk">*</span>
                    </label>
                    <select
                      className={`form-input-field ${scheduleFormErrors.vehicle ? 'error' : ''}`}
                      value={scheduleFormData.vehicle}
                      onChange={(e) => {
                        handleScheduleVehicleChange(e.target.value);
                      }}
                    >
                      <option value="">Select vehicle</option>
                      {allVehiclesForFilter.map((vehicle) => {
                        const vehicleNumber = vehicle.number || vehicle.vehicle_number || '';
                        return (
                          <option key={vehicle.id || vehicleNumber} value={vehicleNumber}>
                            {vehicleNumber}
                        </option>
                        );
                      })}
                    </select>
                    {scheduleFormErrors.vehicle && <span className="error-message">{scheduleFormErrors.vehicle}</span>}
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>👤</span> Assigned Driver <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input-field ${scheduleFormErrors.assignedDriver ? 'error' : ''}`}
                      placeholder="Select vehicle to auto-populate driver"
                      value={scheduleFormData.assignedDriver}
                      readOnly
                      style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                    />
                    {scheduleFormErrors.assignedDriver && <span className="error-message">{scheduleFormErrors.assignedDriver}</span>}
                  </div>

                  {/* Route Details Section */}
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333', marginBottom: '8px', marginTop: '24px' }}>Route Details</h3>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>🛣️</span> Route Name
                    </label>
                    <input
                      type="text"
                      className="form-input-field"
                      placeholder="Enter route name"
                      value={scheduleFormData.routeName}
                      onChange={(e) => handleScheduleFormChange('routeName', e.target.value)}
                    />
                  </div>

                  <div className="form-field" ref={scheduleStartLocationInputRef} style={{ position: 'relative' }}>
                    <label className="form-field-label">
                      <span>📍</span> Start Location
                    </label>
                    <input
                      ref={scheduleStartLocationAutocomplete.inputRef}
                      type="text"
                      className="form-input-field"
                      placeholder="Search location (e.g., Mumbai, Maharashtra)"
                      value={scheduleFormData.startLocation || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only update if the value is actually different to prevent truncation
                        if (value !== scheduleFormData.startLocation) {
                          setScheduleStartLocationSearch(value);
                          handleScheduleFormChange('startLocation', value);
                        }
                      }}
                      onBlur={(e) => {
                        // Ensure the value is preserved on blur
                        if (e.target.value && e.target.value !== scheduleFormData.startLocation) {
                          handleScheduleFormChange('startLocation', e.target.value);
                          setScheduleStartLocationSearch(e.target.value);
                        }
                      }}
                      onFocus={() => {
                        if (!isScheduleGoogleMapsLoaded && scheduleStartLocationSearch.trim().length > 0) {
                          const searchLower = scheduleStartLocationSearch.toLowerCase();
                          const hasMatches = indianCities.some(city => city.toLowerCase().includes(searchLower));
                          setScheduleStartLocationSuggestionsOpen(hasMatches);
                        }
                      }}
                    />
                    {!isScheduleGoogleMapsLoaded && scheduleStartLocationSuggestionsOpen && filteredScheduleStartCities.length > 0 && (
                      <div
                        ref={scheduleStartLocationSuggestionsRef}
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: 'white',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          zIndex: 1000,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          marginTop: '4px'
                        }}
                      >
                        {filteredScheduleStartCities.map((city) => (
                          <div
                            key={city}
                            style={{ padding: '10px 15px', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                            onClick={() => handleScheduleStartLocationCitySelect(city)}
                          >
                            {city}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="form-field" ref={scheduleDestinationInputRef} style={{ position: 'relative' }}>
                    <label className="form-field-label">
                      <span>🎯</span> Destination <span className="required-asterisk">*</span>
                    </label>
                    <input
                      ref={scheduleDestinationAutocomplete.inputRef}
                      type="text"
                      className={`form-input-field ${scheduleFormErrors.destination ? 'error' : ''}`}
                      placeholder="Search location (e.g., Delhi, Delhi)"
                      value={scheduleFormData.destination || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only update if the value is actually different to prevent truncation
                        if (value !== scheduleFormData.destination) {
                          setScheduleDestinationSearch(value);
                          handleScheduleFormChange('destination', value);
                        }
                      }}
                      onBlur={(e) => {
                        // Ensure the value is preserved on blur
                        if (e.target.value && e.target.value !== scheduleFormData.destination) {
                          handleScheduleFormChange('destination', e.target.value);
                          setScheduleDestinationSearch(e.target.value);
                        }
                      }}
                      onFocus={() => {
                        if (!isScheduleGoogleMapsLoaded && scheduleDestinationSearch.trim().length > 0) {
                          const searchLower = scheduleDestinationSearch.toLowerCase();
                          const hasMatches = indianCities.some(city => city.toLowerCase().includes(searchLower));
                          setScheduleDestinationSuggestionsOpen(hasMatches);
                        }
                      }}
                    />
                    {!isScheduleGoogleMapsLoaded && scheduleDestinationSuggestionsOpen && filteredScheduleDestinationCities.length > 0 && (
                      <div
                        ref={scheduleDestinationSuggestionsRef}
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: 'white',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          zIndex: 1000,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          marginTop: '4px'
                        }}
                      >
                        {filteredScheduleDestinationCities.map((city) => (
                          <div
                            key={city}
                            style={{ padding: '10px 15px', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                            onClick={() => handleScheduleDestinationCitySelect(city)}
                          >
                            {city}
                          </div>
                        ))}
                      </div>
                    )}
                    {scheduleFormErrors.destination && <span className="error-message">{scheduleFormErrors.destination}</span>}
                  </div>


                  {/* Intermediate Stops Section */}
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333', marginBottom: '8px', marginTop: '24px' }}>Intermediate Stops</h3>

                  {scheduleFormData.stops.map((stop, index) => (
                    <div key={index} className="stop-item">
                      <div className="stop-drag-handle">⋮⋮</div>
                      <div className="stop-fields">
                        <div className="form-field" style={{ position: 'relative' }}>
                          <label className="form-field-label">Location</label>
                          <input
                            ref={el => {
                              if (el) {
                                scheduleStopLocationInputRefs.current[index] = { current: el };
                                // Initialize autocomplete if Google Maps is loaded
                                if (isScheduleGoogleMapsLoaded) {
                                  setTimeout(() => initializeScheduleStopAutocomplete(index, el), 100);
                                }
                              }
                            }}
                            type="text"
                            className="form-input-field"
                            placeholder="Search location (e.g., Mumbai, Maharashtra)"
                            value={stop.location || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Only update if the value is actually different to prevent truncation
                              if (value !== stop.location) {
                                handleScheduleStopLocationInput(index, value);
                              }
                            }}
                            onBlur={(e) => {
                              // Ensure the value is preserved on blur
                              if (e.target.value && e.target.value !== stop.location) {
                                handleScheduleStopChange(index, 'location', e.target.value);
                                setScheduleStopLocationSearches(prev => ({ ...prev, [index]: e.target.value }));
                              }
                            }}
                            onFocus={() => {
                              if (!isScheduleGoogleMapsLoaded && scheduleStopLocationSearches[index] && scheduleStopLocationSearches[index].trim().length > 0) {
                                const searchLower = scheduleStopLocationSearches[index].toLowerCase();
                                const hasMatches = indianCities.some(city => city.toLowerCase().includes(searchLower));
                                setScheduleStopLocationSuggestionsOpen(prev => ({ ...prev, [index]: hasMatches }));
                              }
                            }}
                          />
                          {!isScheduleGoogleMapsLoaded && scheduleStopLocationSuggestionsOpen[index] && filteredScheduleStopCities(index).length > 0 && (
                            <div
                              ref={el => {
                                if (el) scheduleStopLocationSuggestionsRefs.current[index] = el;
                              }}
                              style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                backgroundColor: 'white',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                maxHeight: '200px',
                                overflowY: 'auto',
                                zIndex: 1000,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                marginTop: '4px'
                              }}
                            >
                              {filteredScheduleStopCities(index).map((city) => (
                                <div
                                  key={city}
                                  style={{ padding: '10px 15px', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                                  onClick={() => handleScheduleStopLocationCitySelect(index, city)}
                                >
                                  {city}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="stop-grid">
                          <div className="form-field">
                            <label className="form-field-label">Estimated Date</label>
                            <input
                              type="datetime-local"
                              className="form-input-field"
                              value={stop.estimatedDate || ''}
                              onChange={(e) => handleScheduleStopChange(index, 'estimatedDate', e.target.value)}
                            />
                          </div>
                          <div className="form-field">
                            <label className="form-field-label">Purpose</label>
                            <select
                              className="form-input-field"
                              value={stop.purpose}
                              onChange={(e) => handleScheduleStopChange(index, 'purpose', e.target.value)}
                            >
                              <option value="">Select purpose</option>
                              <option value="Checkpoint">Checkpoint</option>
                              <option value="Break">Break</option>
                              <option value="Loading">Loading</option>
                              <option value="Unloading">Unloading</option>
                              <option value="Inspection">Inspection</option>
                            </select>
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="stop-delete-btn"
                        onClick={() => handleScheduleRemoveStop(index)}
                        title="Delete stop"
                      >
                        🗑️
                      </button>
                    </div>
                  ))}

                  <button
                    type="button"
                    className="add-stop-btn"
                    onClick={handleScheduleAddStop}
                  >
                    + Add Stop
                  </button>

                  {/* Load Details Section */}
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333', marginBottom: '8px', marginTop: '24px' }}>Load Details</h3>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>📊</span> Total Capacity (tons) <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="number"
                      className={`form-input-field ${scheduleFormErrors.totalCapacity ? 'error' : ''}`}
                      placeholder="Enter total capacity"
                      value={scheduleFormData.totalCapacity}
                      onChange={(e) => {
                        handleScheduleFormChange('totalCapacity', e.target.value);
                        if (scheduleFormErrors.totalCapacity) {
                          setScheduleFormErrors({...scheduleFormErrors, totalCapacity: ''});
                        }
                      }}
                      min="0"
                      step="0.01"
                    />
                    {scheduleFormErrors.totalCapacity && <span className="error-message">{scheduleFormErrors.totalCapacity}</span>}
                </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>📦</span> Used Capacity (tons) <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="number"
                      className={`form-input-field ${scheduleFormErrors.usedCapacity ? 'error' : ''}`}
                      placeholder="Enter used capacity"
                      value={scheduleFormData.usedCapacity}
                      onChange={(e) => {
                        handleScheduleFormChange('usedCapacity', e.target.value);
                        if (scheduleFormErrors.usedCapacity) {
                          setScheduleFormErrors({...scheduleFormErrors, usedCapacity: ''});
                        }
                      }}
                      min="0"
                      step="0.01"
                    />
                    {scheduleFormErrors.usedCapacity && <span className="error-message">{scheduleFormErrors.usedCapacity}</span>}
                  </div>

                  <div className="form-field full-width">
                    <label className="form-field-label">
                      <span>📝</span> Load Description
                    </label>
                    <textarea
                      className="form-input-field"
                      placeholder="Enter load description"
                      rows="3"
                      value={scheduleFormData.loadDescription}
                      onChange={(e) => handleScheduleFormChange('loadDescription', e.target.value)}
                      style={{ resize: 'vertical', minHeight: '80px' }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer-bar">
              <button className="modal-btn-cancel" onClick={handleCloseScheduleModal}>
                Cancel
              </button>
              <button className="modal-btn-save" onClick={handleSaveSchedule}>
                {editingSchedule ? 'Update Schedule' : 'Create Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Trip Schedule Modal */}
      {showScheduleViewModal && viewingSchedule && (
        <div className="add-vehicle-modal-overlay" onClick={() => setShowScheduleViewModal(false)}>
          <div className="view-trip-modal-content" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="view-trip-header">
              <div>
                <h2 className="view-trip-title">View Schedule Details</h2>
                <div className="view-trip-header-info">
                  <span className="view-trip-id">{viewingSchedule.scheduleId}</span>
                  <span className={`status-badge ${getStatusClass(viewingSchedule.tripStatus)}`}>
                    {viewingSchedule.tripStatus}
                  </span>
                </div>
              </div>
              <div className="view-trip-header-right">
                <button className="modal-close-btn" onClick={() => setShowScheduleViewModal(false)}>
                  ✕
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="form-tabs">
              <button className="form-tab active">Overview</button>
            </div>

            {/* Body */}
            <div className="view-trip-body">
              {/* Information Cards */}
              <div className="view-trip-cards-grid">
                {/* Schedule Details Card */}
                <div className="view-trip-card">
                  <div className="view-trip-card-icon">ℹ️</div>
                  <div className="view-trip-card-content">
                    <h4 className="view-trip-card-title">Schedule Details</h4>
                    <div className="view-trip-card-item">
                      <span className="view-trip-card-label">Schedule ID:</span>
                      <span className="view-trip-card-value">{viewingSchedule.scheduleId}</span>
                    </div>
                    <div className="view-trip-card-item">
                      <span className="view-trip-card-label">Schedule Name:</span>
                      <span className="view-trip-card-value">{viewingSchedule.scheduleName}</span>
                    </div>
                    <div className="view-trip-card-item">
                      <span className="view-trip-card-label">Schedule Type:</span>
                      <span className="view-trip-card-value">
                        {viewingSchedule.scheduleType === 'daily' ? 'Daily' : 
                         viewingSchedule.scheduleType === 'weekly' ? 'Weekly' : 
                         'Monthly'}
                      </span>
                    </div>
                    <div className="view-trip-card-item">
                      <span className="view-trip-card-label">Trip Time:</span>
                      <span className="view-trip-card-value">{viewingSchedule.tripDateTime || 'Not set'}</span>
                    </div>
                    <div className="view-trip-card-item">
                      <span className="view-trip-card-label">Status:</span>
                      <span className={`status-badge-small ${getStatusClass(viewingSchedule.tripStatus)}`}>
                        {viewingSchedule.tripStatus}
                      </span>
                    </div>
                    {viewingSchedule.endDate && (
                      <div className="view-trip-card-item">
                        <span className="view-trip-card-label">End Date:</span>
                        <span className="view-trip-card-value">{viewingSchedule.endDate}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Route Information Card */}
                <div className="view-trip-card">
                  <div className="view-trip-card-icon">🛣️</div>
                  <div className="view-trip-card-content">
                    <h4 className="view-trip-card-title">Route Information</h4>
                    <div className="view-trip-card-item">
                      <span className="view-trip-card-label">Route Name:</span>
                      <span className="view-trip-card-value">{viewingSchedule.routeName || 'N/A'}</span>
                    </div>
                    <div className="view-trip-card-item">
                      <span className="view-trip-card-label">Start Location:</span>
                      <span className="view-trip-card-value">{viewingSchedule.startLocation}</span>
                    </div>
                    <div className="view-trip-card-item">
                      <span className="view-trip-card-label">Destination:</span>
                      <span className="view-trip-card-value">{viewingSchedule.destination}</span>
                    </div>
                    <div className="view-trip-card-item">
                      <span className="view-trip-card-label">Distance:</span>
                      <span className="view-trip-card-value">{viewingSchedule.estimatedDistance} km</span>
                    </div>
                    <div className="view-trip-card-item">
                      <span className="view-trip-card-label">Duration:</span>
                      <span className="view-trip-card-value">{viewingSchedule.estimatedDuration} hours</span>
                    </div>
                    <div className="view-trip-card-item">
                      <span className="view-trip-card-label">Stops:</span>
                      <span className="view-trip-card-value">{viewingSchedule.stops ? viewingSchedule.stops.length : 0}</span>
                    </div>
                  </div>
                </div>

                {/* Vehicle & Driver Card */}
                <div className="view-trip-card">
                  <div className="view-trip-card-icon">🚚</div>
                  <div className="view-trip-card-content">
                    <h4 className="view-trip-card-title">Vehicle & Driver</h4>
                    <div className="view-trip-card-item">
                      <span className="view-trip-card-label">Vehicle:</span>
                      <span className="view-trip-card-value">{viewingSchedule.vehicle || 'Not assigned'}</span>
                    </div>
                    <div className="view-trip-card-item">
                      <span className="view-trip-card-label">Driver:</span>
                      <span className="view-trip-card-value">{viewingSchedule.assignedDriver || 'Not assigned'}</span>
                    </div>
                    <div className="view-trip-card-item">
                      <span className="view-trip-card-label">Client/Transporter:</span>
                      <span className="view-trip-card-value">{viewingSchedule.clientAgency || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                {/* Schedule Pattern Card */}
                <div className="view-trip-card">
                  <div className="view-trip-card-icon">📅</div>
                  <div className="view-trip-card-content">
                    <h4 className="view-trip-card-title">Schedule Pattern</h4>
                    {viewingSchedule.scheduleType === 'weekly' && viewingSchedule.selectedDays && viewingSchedule.selectedDays.length > 0 && (
                      <div className="view-trip-card-item">
                        <span className="view-trip-card-label">Days:</span>
                        <span className="view-trip-card-value">{viewingSchedule.selectedDays.join(', ')}</span>
                      </div>
                    )}
                    {viewingSchedule.scheduleType === 'monthly' && viewingSchedule.monthlyDay && viewingSchedule.monthlyDay.length > 0 && (
                      <div className="view-trip-card-item">
                        <span className="view-trip-card-label">Dates:</span>
                        <span className="view-trip-card-value">
                          {viewingSchedule.monthlyDay.sort((a, b) => a - b).map(d => `${d}${d === 1 || d === 21 || d === 31 ? 'st' : d === 2 || d === 22 ? 'nd' : d === 3 || d === 23 ? 'rd' : 'th'}`).join(', ')} of every month
                        </span>
                      </div>
                    )}
                    {viewingSchedule.scheduleType === 'daily' && (
                      <div className="view-trip-card-item">
                        <span className="view-trip-card-label">Frequency:</span>
                        <span className="view-trip-card-value">Every day</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Notes Section */}
              {viewingSchedule.notes && (
                <div className="view-trip-section" style={{ marginTop: '24px' }}>
                  <h3 className="view-trip-section-title">Notes</h3>
                  <p style={{ color: '#6b7280', lineHeight: '1.6' }}>{viewingSchedule.notes}</p>
                </div>
              )}

              {/* Stops Section */}
              {viewingSchedule.stops && viewingSchedule.stops.length > 0 && (
                <div className="view-trip-section" style={{ marginTop: '24px' }}>
                  <h3 className="view-trip-section-title">Intermediate Stops</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {viewingSchedule.stops.map((stop, index) => (
                      <div key={index} style={{
                        padding: '16px',
                        background: '#f9fafb',
                        borderRadius: '8px',
                        borderLeft: '4px solid #2563EB'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#111827' }}>
                            {stop.location}
                          </h4>
                          <span style={{ fontSize: '14px', color: '#6b7280' }}>
                            Stop {index + 1} of {viewingSchedule.stops.length}
                          </span>
                        </div>
                        {stop.eta && (
                          <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                            <strong>ETA:</strong> {stop.eta}
                          </div>
                        )}
                        {stop.purpose && (
                          <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                            <strong>Purpose:</strong> {stop.purpose}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="view-trip-footer" style={{ padding: '16px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button 
                onClick={() => setShowScheduleViewModal(false)}
                style={{
                  padding: '10px 20px',
                  background: 'white',
                  color: '#333',
                  border: '1px solid #d0d0d0',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 500
                }}
              >
                Close
              </button>
              <button 
                onClick={() => {
                  setShowScheduleViewModal(false);
                  handleScheduleEditClick(viewingSchedule);
                }}
                style={{
                  padding: '10px 20px',
                  background: '#10B981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 500
                }}
              >
                Edit Schedule
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Trip Success Modal */}
      {showCancelSuccess && cancelledTripInfo && (
        <div className="modal-overlay" onClick={handleCancelSuccessClose}>
          <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="40" cy="40" r="40" fill="#10B981"/>
                <path d="M25 40L35 50L55 30" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="success-title">Success!</h2>
            <p className="success-message">Trip has been successfully Cancelled</p>
            <div className="success-id-badge">{cancelledTripInfo.id}</div>
            <button className="success-ok-btn" onClick={handleCancelSuccessClose}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* Add Trip Schedule Confirmation Modal */}
      {showScheduleConfirmModal && (
        <div className="modal-overlay" onClick={() => setShowScheduleConfirmModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Confirm Create Schedule</h2>
              <button className="modal-close" onClick={() => setShowScheduleConfirmModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to create this trip schedule?</p>
              <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
                <p><strong>Schedule Name:</strong> {scheduleFormData.scheduleName}</p>
                <p><strong>Schedule Type:</strong> {
                  scheduleFormData.scheduleType === 'daily' ? 'Daily' : 
                  scheduleFormData.scheduleType === 'weekly' ? 'Weekly' : 
                  'Monthly'
                }</p>
                <p><strong>Route:</strong> {scheduleFormData.startLocation} → {scheduleFormData.destination}</p>
                <p><strong>Vehicle:</strong> {scheduleFormData.vehicle || 'Not assigned'}</p>
                <p><strong>Driver:</strong> {scheduleFormData.assignedDriver || 'Not assigned'}</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowScheduleConfirmModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleConfirmScheduleSave}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Trip Schedule Success Modal */}
      {showScheduleSuccessModal && savedScheduleInfo && (
        <div className="modal-overlay" onClick={() => {
          setShowScheduleSuccessModal(false);
          setEditingSchedule(null);
        }}>
          <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="40" cy="40" r="40" fill="#10B981"/>
                <path d="M25 40L35 50L55 30" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="success-title">Success!</h2>
            <p className="success-message">
              Trip schedule has been successfully {savedScheduleInfo.wasEdit ? 'updated' : 'created'}
            </p>
            <div className="success-id-badge">{savedScheduleInfo.scheduleId}</div>
            <button className="success-ok-btn" onClick={() => {
              setShowScheduleSuccessModal(false);
              setEditingSchedule(null);
            }}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TripManagement;


